import * as THREE from "three";
import { frameHasHeadroom, frameIsOverBudget } from "./frameBudget.js";
import { EffectComposer } from "three/examples/jsm/postprocessing/EffectComposer.js";
import { RenderPass } from "three/examples/jsm/postprocessing/RenderPass.js";
import { UnrealBloomPass } from "three/examples/jsm/postprocessing/UnrealBloomPass.js";
import { FilmPass } from "three/examples/jsm/postprocessing/FilmPass.js";
import { OutputPass } from "three/examples/jsm/postprocessing/OutputPass.js";
import { RGBELoader } from "three/examples/jsm/loaders/RGBELoader.js";
import type { N8AOPass } from "n8ao";
import { GFX, gfxQuality, qualityCaps, setGfxTarget, type QualityCaps } from "./gfxConfig.js";
import { PRACTICE_MOVER_TAG, type MapBox, type MapDef } from "@neon-purge/shared";
import {
  DRESS_MAX_BOXES, GROUND_MAX_BOXES, dressGround, dressPriority, dressStructure, dressable,
  exposedGroundEdges, facadeLike, groundDressable, naturalGround,
  STREET_MAX_MARKS, paintedStreets, streetFixtures, streetMarkings,
  ROOF_MAX_PIECES, elevatedSlab, roofDressable, roofFelt, roofFurniture, roofPieces,
  SIGN_MAX_PIECES, facadeSigns, shellOutward, signable,
  SHOP_MAX_PIECES, shopfront,
  BLOT_MAX_PIECES, DEBRIS_MAX_BOXES, SKIRT_MAX_PIECES,
  baseDebris, contactCaster, contactSkirt, floorUnder, groundBlots,
  MINOR_MAX_BOXES, dressMinor, minorKind, type MinorKind,
  type ContactPatch,
  type DressBox, type DressTarget, type RoofPiece, type ShopPiece, type SignPiece,
  type StreetFixture, type StreetMark,
  houseWindowMap, houseWindows, windowable, type WindowPiece,
} from "./mapDressing.js";

/** Awning stripe colourways. A shop awning is the one thing on a street that is
 *  allowed to be SATURATED — every other surface in this game's map palette is
 *  a grey, a stone or a rust, which is exactly why the eye reads a canopy as
 *  fabric the moment it sees one. Paired with off-white on alternate stripes. */
const AWNING_COLS = ["#b5303a", "#2b6a4f", "#2a4f86", "#8a4a1e", "#5a3570"];

/** The colour architectural trim is pulled toward — a warm cast-stone grey.
 *  Module scope: it is a constant, and allocating a Color per detail box would
 *  be a map-build allocation for nothing. */
const TRIM_STONE = new THREE.Color("#cfc7b6");

/** Half-texel inset on every `grime` atlas cell. Without it a mip of the sheet
 *  bleeds the neighbouring cell in along the seam, and cell 0's crease is the
 *  darkest thing on the sheet — it would print a grey line down the edge of
 *  every dirt patch in the game. */
const GRIME_INSET = 1.5 / 512;

/**
 * One floor-contact quad, UV-mapped into its `grime` atlas cell.
 *
 * PlaneGeometry lies in XY; `rotateX(-90°)` lays it flat with its u axis on +X
 * and its v axis on -Z — which is why a skirt at `rot = 0` puts its dark v = 1
 * edge against a wall lying to the quad's -z. `mapDressing.contactSkirt` picks
 * `rot` on exactly that convention.
 */
function contactQuad(p: ContactPatch): THREE.BufferGeometry {
  const q = new THREE.PlaneGeometry(p.w, p.d).toNonIndexed().rotateX(-Math.PI / 2);
  const uv = q.attributes.uv;
  const u0 = (p.cell % 2) * 0.5, v0 = p.cell < 2 ? 0.5 : 0;
  for (let i = 0; i < uv.count; i++) {
    const u = p.flip ? 1 - uv.getX(i) : uv.getX(i), v = uv.getY(i);
    uv.setXY(i,
      u0 + GRIME_INSET + u * (0.5 - GRIME_INSET * 2),
      v0 + GRIME_INSET + v * (0.5 - GRIME_INSET * 2));
  }
  uv.needsUpdate = true;
  if (p.rot) q.rotateY(p.rot);
  q.translate(p.x, p.y, p.z);
  return q;
}
import { RoundedBoxGeometry } from "three/examples/jsm/geometries/RoundedBoxGeometry.js";
import { mergeGeometries } from "three/examples/jsm/utils/BufferGeometryUtils.js";
import { FACADE_LAYOUT, groundGrimeTexture, mapTextures, roadPaintTexture, surfaceMaps, toonGradient, uvAnchor, worldProjectUV, type SurfaceFamily } from "./mapTextures.js";
import { rippleNormalTexture } from "./fxTextures.js";
import { avatarPhotoLoadPromise, avatarTextures, startAvatarPhotoLoad, upgradeAvatarTextures, warmAvatarTextures } from "./avatarTextures.js";
import { prewarmVehicleTextures, warmVehicleTextures } from "./vehicleTextures.js";
import { prewarmMonsterTextures, warmMonsterTextures } from "./monsterTextures.js";
import { prewarmPropTextures, warmPropTextures } from "./propTextures.js";
import { cloudTexture, moonTexture, prewarmSkyTextures, starTexture } from "./skyTextures.js";
import {
  prewarmCombatTextures, firePuffTexture, smokePuffTexture, emberTexture,
  muzzleFlashTexture, shockRingTexture, debrisTexture,
} from "./combatTextures.js";
import {
  prewarmDecalTextures, bulletCraterTexture, scorchTexture, bloodTexture,
  wetSplatTexture, bootPrintTexture, skidTexture,
} from "./decalTextures.js";
import { buildDecor, EQ_BANDS, type DecorGroundSpot } from "./decor.js";
import { tickWind } from "./wind.js";
import { applyMapWeather, weatherKindFor, WEATHER_PRESETS } from "./mapWeather.js";
import { groundClass, pbrReady, pbrTextures, startPbrLoad, upgradeMapMaterials } from "./pbrTextures.js";
import { awaitLoadGates, revealWarmRoots, warmRootCount } from "./loadGate.js";
import { mapPropsReady, setPropLowDetail, startMapPropLoad, upgradeMapProps } from "./mapProps.js";
import { buildShipDressing, isShipBox, shipReplaces } from "./shipDressing.js";
import { MODEL_LOAD_WAIT_MS, type WarmStage } from "./gltfAssets.js";
import { buildMapExtras } from "./mapExtras.js";
import { planPropSwaps, propSwapsReady, upgradePropSwaps } from "./mapPropSwaps.js";
import { realModelsEnabled } from "./gltfAssets.js";
import { audio } from "./audio.js";
import { perfBeacon } from "../net/clientlog.js";

const POST_KEY = "neonpurge.post";

/** Sky dome shader uniforms — see Renderer.buildSkyDome. */
type SkyUniforms = {
  uZenith: { value: THREE.Color };
  uMid: { value: THREE.Color };
  uHorizon: { value: THREE.Color };
  uFog: { value: THREE.Color };
  uSunDir: { value: THREE.Vector3 };
  uMoonDir: { value: THREE.Vector3 };
  uNight: { value: number };
  uSynth: { value: number };
  uCloudAmt: { value: number };
  uCloud: { value: THREE.Texture };
  uStars: { value: THREE.Texture };
  uMoon: { value: THREE.Texture };
  uCamXZ: { value: THREE.Vector2 };
  uTime: { value: number };
};

/** Animated water surface uniforms — see loadMap(). */
type WaterUniforms = {
  uTime: { value: number };
  uRipple: { value: THREE.Texture };
  uSkyLo: { value: THREE.Color };
  uSkyHi: { value: THREE.Color };
  uSunDir: { value: THREE.Vector3 };
  uNight: { value: number };
};

/** Submerged murk. Linear, not exponential — see setUnderwater. near/far are a
 *  least-worst-case FIT to the FogExp2(density 0.05) curve this replaces, not
 *  round numbers: three's linear fog is `smoothstep(near, far, depth)`
 *  (fog_fragment.glsl, three.cjs:37412), whose S-curve matches exp2 well over
 *  0-50 m at these edges — worst-case 3.9 percentage points of obscuration,
 *  measured by agent/bench-fog.mjs. `near` is DELIBERATELY negative: it shifts
 *  the S-curve to put a slight haze at zero distance, which is both the better
 *  fit and correct for being underwater. Re-run the harness if you retune. */
const UNDERWATER_FOG = 0x155c6b;
const UNDERWATER_FOG_NEAR = -2.5;
const UNDERWATER_FOG_FAR = 37.5;

type MapMaterialFamily =
  | "ground"
  | "paved"
  /** a made slab four metres up: a ROOF, not a floor (see `elevatedSlab`) */
  | "roof"
  | "building"
  /** medieval_castle stone: coursed ashlar photo, heavy grime (see box loop) */
  | "ashlar"
  | "big"
  | "small"
  | "mc_stone"
  | "mc_brick"
  | "mc_wood"
  | "mc_foliage"
  | "mc_earth"
  | "mc_glass"
  | "mc_metal";

/** weathered limestone the ashlar tint is pulled toward (load-time only). */
const ASHLAR_LIMESTONE = new THREE.Color("#b8b0a0");

const mcRgb = { r: 0, g: 0, b: 0 };
function hexRgb(hex: string | undefined, out: { r: number; g: number; b: number }): boolean {
  if (!hex || !/^#[0-9a-fA-F]{6}$/.test(hex)) return false;
  out.r = parseInt(hex.slice(1, 3), 16);
  out.g = parseInt(hex.slice(3, 5), 16);
  out.b = parseInt(hex.slice(5, 7), 16);
  return true;
}

function minecraftFamily(color: string | undefined): MapMaterialFamily {
  if (!hexRgb(color, mcRgb)) return "mc_stone";
  const { r, g, b } = mcRgb;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const sat = max - min;
  if (b > 150 && g > 130 && r < 190) return "mc_glass";
  if (g > r * 1.08 && g > b * 1.08) return "mc_foliage";
  if (r > 95 && g > 55 && b < 75 && r > b * 1.55) return "mc_brick";
  if (r > 95 && g > 70 && b < 95 && r - b > 22) return "mc_wood";
  if (r > 85 && g > 65 && b < 70) return "mc_earth";
  if (sat < 28 && max > 95) return "mc_stone";
  if (sat < 50 && b > r && b > g) return "mc_metal";
  return "mc_stone";
}

export function postEnabled(): boolean {
  return localStorage.getItem(POST_KEY) !== "off";
}

export function setPostEnabled(on: boolean): void {
  localStorage.setItem(POST_KEY, on ? "on" : "off");
}

/**
 * Three.js scene wrapper. Supports per-map themes:
 *  - "neon": dark arena, soft glow edges, floor grid (toned down, not overpowering)
 *  - "day":  bright krunker-style look — light sky, sun, clean flat colors
 */
// One WebGLRenderer per canvas for the whole tab lifetime — created lazily,
// never destroyed. GameRenderer wrappers come and go each round; the GPU
// context underneath is shared so we never exhaust the browser's limit.
let _shared: THREE.WebGLRenderer | null = null;
let _sharedCanvas: HTMLCanvasElement | null = null;

const LOWSPEC_KEY = "neonpurge.lowspec";

/** BOOT-TIME hardware probe: weak machines (office laptops, iGPUs, few
 *  cores) START conservative instead of stuttering for a minute while the
 *  reactive ladder figures it out. The adaptive system then climbs back up
 *  if there is real headroom — same look, right cost, no first-minute lag. */
/** Current dynamic cost-ladder level (0 = full … 3 = minimum). Other systems
 *  (FX lights, particles, cosmetic tick rates) read this to shed load too. */
let currentGfxLevel = 0;
export function gfxLevel(): number { return currentGfxLevel; }

/** What the perf HUD reads from the renderer at flush time (see perfSnapshot). */
export interface RendererPerfSnap {
  calls: number; tris: number; programs: number; geometries: number; textures: number;
  frameEma: number; busyEma: number; pixelScale: number; gfxLvl: number;
}
const GFXPIN_KEY = "neonpurge.gfxpin";
/** SCREENSHOT-ONLY diagnostic (same family as match.ts's SHOT_* demo hooks):
 *  pin the cost ladder + resolution at full quality so the agent's software-GL
 *  capture rig (headless Chrome SwiftShader, ~10 fps) photographs the SHIPPED
 *  look — shadows / post / normal maps ON — instead of the L4 degrade the frame
 *  timer forces on a machine that renders every frame in software. Never set in
 *  a real client; the adaptive ladder is what keeps weak hardware playable. */
function gfxPinned(): boolean {
  try { return localStorage.getItem(GFXPIN_KEY) === "1"; } catch { return false; }
}
function initialGfxLvl(): number {
  if (gfxPinned()) { currentGfxLevel = 0; return 0; }
  // sticky low-spec machines have PROVEN they can't afford the post chain —
  // start at L2 (no bloom) so the first minute is smooth, not a re-discovery
  // TV app starts at the FLOOR (L3): a Chromecast can't afford any of the cost
  // ladder, so skip the lag-then-discover and begin at minimum quality.
  // An iGPU (weakGpu) ALSO starts at L2, not L1: bloom at 1080p is the single
  // heaviest GPU cost, and an Intel UHD sat at ~21 fps for the ~10s the ladder
  // needs to discover that (owner's desktop, 2026-08-08). Weak-CPU-but-real-GPU
  // machines (≤4 cores with a GeForce) keep the L1 start.
  const l = isTvApp() ? 3 : lowSpec() ? 2 : weakGpu() ? 2 : weakHardware() ? 1 : 0;
  currentGfxLevel = l;
  return l;
}

let weakHw: boolean | null = null;
let weakGpuFlag = false;
/** True when the probe saw an integrated/software GPU (regardless of CPU). */
export function weakGpu(): boolean { void weakHardware(); return weakGpuFlag; }
let gpuName = "?";
/** Short GPU label (cached from the weakHardware probe) — tags perf beacons
 *  so laptop vs desktop are distinguishable in the logs. */
export function gpuTag(): string { void weakHardware(); return gpuName; }
export function weakHardware(): boolean {
  if (weakHw !== null) return weakHw;
  let gpu = "";
  try {
    const cv = document.createElement("canvas");
    const gl = cv.getContext("webgl2") ?? cv.getContext("webgl");
    const dbg = gl?.getExtension("WEBGL_debug_renderer_info");
    if (gl && dbg) gpu = String(gl.getParameter(dbg.UNMASKED_RENDERER_WEBGL) ?? "");
    // release the probe context — browsers cap ~16 live contexts and the
    // shared-renderer design exists to protect that budget
    gl?.getExtension("WEBGL_lose_context")?.loseContext();
  } catch { /* probe is best-effort */ }
  // pull the recognizable chip name out of the ANGLE string for the tag
  gpuName = ((gpu.match(/(UHD Graphics \d+|Iris \w+|ADL \w+|Arc \w+|RTX \d+|GTX \d+|Radeon[\w ]*|Apple \w+|Mali[\w-]*|Adreno \d+)/i)?.[0]) || gpu.slice(0, 24) || "?").trim();
  const cores = navigator.hardwareConcurrency ?? 8;
  const mem = (navigator as unknown as { deviceMemory?: number }).deviceMemory ?? 8;
  const igpu = /intel|uhd|hd graphics|iris|llvmpipe|swiftshader|mali|adreno|powervr|apple gpu/i.test(gpu) &&
    !/nvidia|geforce|radeon|rx |arc /i.test(gpu);
  weakGpuFlag = igpu;
  weakHw = cores <= 4 || mem <= 4 || igpu;
  if (weakHw) console.log(`[render] weak hardware detected (cores=${cores}, mem=${mem}GB, gpu="${gpu.slice(0, 60)}") — starting conservative`);
  return weakHw;
}
const HIGHGFX_KEY = "neonpurge.highgfx";
const TVAPP_KEY = "neonpurge.tvapp";

/** True when running inside the Neon Purge TV app (a low-power Chromecast/Google
 *  TV). The wrapper tags itself two ways — a "NeonPurgeTV" UA token and a `_tv`
 *  query param — either of which flips this on; it is then sticky (localStorage)
 *  so it survives in-app navigation. This signal is ONLY ever set by the TV APK,
 *  so nothing here can affect desktop or tablet players. */
let _tvApp: boolean | null = null;
export function isTvApp(): boolean {
  if (_tvApp !== null) return _tvApp;
  try {
    const byUa = /NeonPurgeTV/i.test(navigator.userAgent || "");
    const byParam = new URLSearchParams(location.search).has("_tv");
    const sticky = localStorage.getItem(TVAPP_KEY) === "1";
    _tvApp = byUa || byParam || sticky;
    if (_tvApp && !sticky) { try { localStorage.setItem(TVAPP_KEY, "1"); } catch { /* private mode */ } }
  } catch { _tvApp = false; }
  return _tvApp;
}

/** Persistent auto-detected low-spec mode: once a machine proves it can't hold
 *  frame rate even at floor quality, every later load starts CHEAP — no MSAA
 *  (context-creation-time!), no shadows, no dynamic point lights, no env maps.
 *  Purely visual downgrades; gameplay identical.
 *  The TV app is forced low-spec from the first frame (no 8-second lag to
 *  auto-discover it), and never lets the user turn it back up. */
export function lowSpec(): boolean {
  if (isTvApp()) return true;
  try {
    if (localStorage.getItem(HIGHGFX_KEY) === "1") return false; // user forced full quality
    return localStorage.getItem(LOWSPEC_KEY) === "1";
  } catch { return false; }
}
/** Settings override: force full effects (and clear any auto low-spec flag) or
 *  return to automatic. */
export function setForceHighGfx(on: boolean): void {
  try {
    localStorage.setItem(HIGHGFX_KEY, on ? "1" : "0");
    if (on) localStorage.setItem(LOWSPEC_KEY, "0"); // wipe the sticky auto-flag
  } catch { /* private mode */ }
}
export function forceHighGfx(): boolean {
  try { return localStorage.getItem(HIGHGFX_KEY) === "1"; } catch { return false; }
}
export function setLowSpec(on: boolean): void {
  try { localStorage.setItem(LOWSPEC_KEY, on ? "1" : "0"); } catch { /* private mode */ }
}

/** One-shot toast when auto low-spec engages — a persistent visual downgrade
 *  must never be silent (a flagged machine looked "broken" with no clue why).
 *  Plain DOM on purpose: the renderer has no HUD reference, and this must work
 *  from any screen (menu background included). */
function announcePerfMode(): void {
  try {
    const d = document.createElement("div");
    d.textContent = "PERFORMANCE MODE — graphics reduced to hold framerate. Settings → \"Full effects\" to override.";
    d.style.cssText =
      "position:fixed;left:50%;bottom:16%;transform:translateX(-50%);z-index:99999;" +
      "padding:8px 14px;background:rgba(8,12,16,0.85);border:1px solid rgba(0,240,255,0.35);" +
      "color:#9adfff;font:600 12px/1.4 monospace;letter-spacing:0.08em;border-radius:4px;" +
      "pointer-events:none;max-width:80vw;text-align:center;transition:opacity 1.2s;opacity:1";
    document.body.appendChild(d);
    window.setTimeout(() => { d.style.opacity = "0"; }, 7000);
    window.setTimeout(() => d.remove(), 8500);
  } catch { /* non-browser env */ }
}

/** Pre-upload an async-loaded texture to the GPU the moment it finishes
 *  decoding — otherwise the first frame that SAMPLES it pays the glTexImage2D
 *  upload (1–2 ms on weak iGPUs), and custom skins arrive mid-match (remote
 *  join, killcam, vehicle claim). Safe no-op before the renderer exists. */
export function warmTexture(tex: THREE.Texture): void {
  try { _shared?.initTexture(tex); } catch { /* context lost — first draw uploads instead */ }
}

export function sharedRenderer(canvas: HTMLCanvasElement): THREE.WebGLRenderer {
  if (_shared && _sharedCanvas === canvas) return _shared;
  _shared?.dispose();
  // MSAA is decided at context creation — on proven-weak machines skip it
  // (it's one of the biggest costs on integrated GPUs)
  _shared = new THREE.WebGLRenderer({ canvas, antialias: !lowSpec(), powerPreference: "high-performance" });
  // PARALLEL SHADER COMPILE: on slow shader compilers (Intel UHD 630 / D3D11
  // ANGLE) linking a program forces a synchronous driver stall — every new
  // material mid-match froze the main thread for hundreds of ms ("desktop
  // still laggy"). This extension lets three.js poll COMPLETION_STATUS_KHR
  // instead of blocking on LINK_STATUS, so compiles happen off the hot path.
  try { _shared.getContext().getExtension("KHR_parallel_shader_compile"); } catch { /* absent on some drivers */ }
  _sharedCanvas = canvas;
  return _shared;
}

/** N8AO is ~230 KB and only the High preset uses it, so it is loaded on
 *  demand (kicked off by the first High renderer, awaited under the match load
 *  screen in precompile) instead of riding in the main bundle. */
type N8AOModule = typeof import("n8ao");
let n8aoMod: N8AOModule | null = null;
let n8aoLoad: Promise<N8AOModule | null> | null = null;
function loadN8AO(): Promise<N8AOModule | null> {
  n8aoLoad ??= import("n8ao").then((m) => (n8aoMod = m), () => null);
  return n8aoLoad;
}

/** Half-width (m) of the sun-shadow box on maps too big to fit in one: the
 *  box then follows the camera. 50 m covers combat range; past it the linear
 *  fog (150 m+) and distance already flatten contact shadows. */
const SHADOW_HALF = 50;

/** PHOTOGRAPHED SKY LIGHT (High preset, day maps at full daylight). A CC0
 *  Poly Haven HDRI (kloofendal_48d_partly_cloudy_puresky, 1k) prefiltered
 *  ONCE per tab into a PMREM target on the shared context — it is never
 *  repainted, so the day/night cycle's procedural probe takes over the moment
 *  dusk starts (envStep > 0) and hands back at full day.
 *  The raw HDRI is NORMALISED before prefiltering so it cannot change the
 *  game's exposure: every texel is clamped (the sun is ~10⁴, which would
 *  flood the diffuse term) and the whole image is scaled so its mean
 *  luminance matches the painted probe it replaces (0.69). Only the
 *  DIRECTIONAL character changes — real sky gradient, cloud cover, a sun
 *  highlight in glossy reflections — not the overall brightness. */
const HDRI_URL = "/textures/hdri/sky_1k.hdr";
const HDRI_CLAMP = 12;
// measured: the painted day probe's linear pixel-mean luminance (zenith
// #4f93da → mid #a5cbe8 → horizon #eaf3fa gradient) is 0.69. The HDRI's own
// clamped mean is 0.34, so it is scaled ×~2 to match.
const HDRI_MEAN = 0.69;
let hdriEnv: { rt: THREE.WebGLRenderTarget; sunAz: number } | null = null;
let hdriLoad: Promise<void> | null = null;

/** Normalise an equirect float HDR in place; returns the sun's azimuth
 *  (radians, three's equirect convention atan2(z, x)). */
function normaliseHdri(tex: THREE.DataTexture): number {
  const img = tex.image as { data: Float32Array; width: number; height: number };
  const d = img.data, w = img.width, h = img.height;
  const stride = d.length / (w * h); // RGBA (4) from RGBELoader
  let best = -1, bestI = 0, sum = 0;
  for (let i = 0, p = 0; i < w * h; i++, p += stride) {
    const lum = 0.2126 * d[p] + 0.7152 * d[p + 1] + 0.0722 * d[p + 2];
    if (lum > best) { best = lum; bestI = i; }
    for (let c = 0; c < 3; c++) if (d[p + c] > HDRI_CLAMP) d[p + c] = HDRI_CLAMP;
    sum += 0.2126 * d[p] + 0.7152 * d[p + 1] + 0.0722 * d[p + 2];
  }
  const k = sum > 0 ? HDRI_MEAN / (sum / (w * h)) : 1;
  for (let i = 0, p = 0; i < w * h; i++, p += stride) { d[p] *= k; d[p + 1] *= k; d[p + 2] *= k; }
  tex.needsUpdate = true;
  const u = ((bestI % w) + 0.5) / w;
  return (u - 0.5) * Math.PI * 2;
}

/** The live GameRenderer (the debug panel tunes this one). */
let activeRenderer: GameRenderer | null = null;
export function currentGameRenderer(): GameRenderer | null { return activeRenderer; }

export class GameRenderer {
  readonly renderer: THREE.WebGLRenderer;
  readonly scene: THREE.Scene;
  readonly camera: THREE.PerspectiveCamera;
  private mapGroup: THREE.Group | null = null;
  private lightGroup: THREE.Group | null = null;
  /** Animated-water shader uniforms (null when the map has no water). */
  private waterUniforms: WaterUniforms | null = null;
  /** The animated water-surface mesh, lifted by the rising tide (see setTide). */
  private waterMesh: THREE.Mesh | null = null;
  /** RISING TIDE: raise the rendered sea surface to match world.tideY. */
  setTide(tideY: number): void {
    if (this.waterMesh) this.waterMesh.position.y = tideY;
  }
  private lavaUniforms: { uTime: { value: number } } | null = null;
  // MapDef.weather rain: a THREE.Points cloud (child of mapGroup, disposed with
  // it) recentred on the camera each frame. Cosmetic — no sim/collision.
  // The FALL is a GPU vertex-shader displacement now (see the rain block in
  // buildMap): the base droplet positions upload to the GPU ONCE and the wrap
  // `y = mod(baseY - fall*t, span)` runs per-vertex on the card. render() only
  // writes one `uRainTime` float per frame — no CPU per-droplet loop and no
  // per-frame re-upload of the (up to 4000×3-float) position buffer. `rainSpan`
  // is the wrap height; `rainUniforms` is the injected shader uniform block,
  // captured in onBeforeCompile so the frame loop can advance uRainTime.
  private rainPoints: THREE.Points | null = null;
  private rainSpeed = 12;
  private rainSpanY = 44;
  private rainUniforms: { uRainTime: { value: number } } | null = null;
  // mega_chibre flavour (from the photobooth/pong project): pixelised floating
  // sprites that bob + face the camera, and a projector-style wallpaper plane.
  private chibreSprites: THREE.Mesh[] = [];
  private composer: EffectComposer | null = null;
  private bloomPass: UnrealBloomPass | null = null;
  /** The plain scene pass and the N8AO pass that REPLACES it when AO is on
   *  (N8AO renders the beauty + depth itself, so running both would draw the
   *  scene twice). Exactly one is enabled; see syncAoPass(). */
  private renderPass: RenderPass | null = null;
  private aoPass: N8AOPass | null = null;
  /** Quality preset resolved against the hardware probe (gfxConfig.ts). Fixed
   *  for the renderer's life — Settings changes apply from the next match. */
  private readonly caps: QualityCaps = qualityCaps(gfxQuality(), weakHardware(), weakGpu());

  constructor(canvas: HTMLCanvasElement) {
    activeRenderer = this;
    setGfxTarget(this);
    // REUSE one WebGLRenderer/context for the tab: a browser caps live WebGL
    // contexts (~16), and a new renderer was built every round/menu — after
    // a few cycles the context was lost and the map "semi-froze" (no remote
    // players, nothing rendering). One shared context never runs out.
    this.renderer = sharedRenderer(canvas);
    // 1.5 ratio cap: on 2x displays that's 44% fewer pixels for a difference
    // you can't see at game FOV — the single biggest across-the-board saving
    // TV app renders at 0.5x internal res (the display upscales) — halves the
    // pixel work on a weak Chromecast; soft but smooth at couch distance.
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, isTvApp() ? 0.5 : lowSpec() ? 1.0 : this.caps.pixelCap));
    this.renderer.setSize(window.innerWidth, window.innerHeight);
    // richer look: soft shadows + filmic tone mapping + correct color space
    this.renderer.shadowMap.enabled = !lowSpec(); // proven-weak machines skip shadows entirely
    // Softer static shadows sell the less-blocky material pass. Low-spec still
    // disables the shadow pass entirely above.
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    // shadows re-render on OUR schedule (see render()) — the world casters
    // are static, so a full shadow pass every frame is mostly wasted work
    this.renderer.shadowMap.autoUpdate = false;
    this.renderer.shadowMap.needsUpdate = true;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = GFX.exposure;
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    // DRAW-CALL READOUT for the offline capture harness (agent/shot.mjs).
    // BACKLOG has filed "expose renderer.info" as the top next-pass item twice
    // now, because every draw-call claim in it — including this iteration's —
    // has been arithmetic from the source rather than a measurement. `info` is
    // a live object three updates in place, so this is one property write at
    // construction and zero per-frame cost; nothing in the game reads it.
    (window as unknown as { npInfo?: THREE.WebGLRenderer["info"] }).npInfo = this.renderer.info;

    // PHOTOSCANNED MAP SURFACES (pbrTextures.ts): start fetching + decoding the
    // Poly Haven set NOW — a GameRenderer only exists for a match / spectate,
    // never a menu — so it is usually done by the time precompile() runs under
    // the load screen. lowSpec / TV stay on the procedural canvases (memory +
    // fill budget); integrated GPUs DO get the 1k photos (cheap to sample) but
    // with anisotropy capped at 4.
    this.ensureHdri();
    // avatar photo detail (avatarTextures.ts): same gate
    void startAvatarPhotoLoad(!lowSpec() && !isTvApp());
    // PHOTOSCANNED PROPS (mapProps.ts): same gate as the photo surfaces below
    // scanned props are MODELS: always on except the TV app (gltfAssets realModelsEnabled)
    setPropLowDetail(lowSpec());
    startMapPropLoad(!isTvApp(),
      weakGpu() ? Math.min(4, this.renderer.capabilities.getMaxAnisotropy()) : this.renderer.capabilities.getMaxAnisotropy());
    // High preset: start fetching the AO module now (see loadN8AO)
    if (this.caps.ao && !lowSpec()) void loadN8AO();
    void startPbrLoad({
      enabled: !lowSpec() && !isTvApp(),
      maxAnisotropy: weakGpu() ? Math.min(4, this.renderer.capabilities.getMaxAnisotropy())
        : this.renderer.capabilities.getMaxAnisotropy(),
    });

    this.scene = new THREE.Scene();
    // Dev-only draw-call attribution handle (see agent/shot.mjs SHOT_ATTR): the
    // screenshot rig walks the scene graph to blame draw calls by top-level
    // child. One property write at construction, zero per-frame cost, nothing
    // in the game reads it — same contract as npInfo above.
    (window as unknown as { __np_scene?: THREE.Scene }).__np_scene = this.scene;
    // ONE fog object for the tab's lifetime, mutated in place — never replaced.
    // three compares scene fog by IDENTITY (`materialProperties.fog !== fog`,
    // three.cjs:53619) and re-derives the shader program for EVERY material in
    // the scene whenever it changes. Colour/near/far are plain uniforms
    // (refreshFogUniforms, :50814) refreshed on each material's first bind of
    // the frame — `_currentMaterialId` resets to -1 at the end of every render
    // (:53008) — so mutating this object is free and propagates immediately.
    // See applyFog(). Everything that wants to change fog goes through it.
    this.scene.fog = this.fog;
    this.camera = new THREE.PerspectiveCamera(
      90,
      window.innerWidth / window.innerHeight,
      0.05,
      600, // far — covers the 2x-scaled arena maps (small maps unaffected; fog hides distance)
    );
    this.camera.rotation.order = "YXZ";
    // camera is part of the scene graph so the viewmodel (its child) renders
    this.scene.add(this.camera);

    window.addEventListener("resize", this.onResize);

    // GPU CONTEXT RESTORE. three's own listener (registered FIRST, in the
    // WebGLRenderer ctor) preventDefaults the loss — that is what makes the
    // context restorable — and re-inits GL on restore (three.module.js:15349).
    // But every shader PROGRAM is dropped on loss and three recompiles them
    // LAZILY on next use, so the first frame back would cold-compile every
    // currently-visible entity AND the post chain at once: the exact match-start
    // freeze `precompile()` exists to avoid, now landing MID-FIGHT on the weak
    // GPUs that lose context in the first place (driver reset / memory pressure /
    // tab wake). Flag it here and warm everything in ONE controlled pass on the
    // next render() (GL is already re-inited by then — our listener runs after
    // three's). Idempotent: the flag coalesces repeated events, matching the
    // constructor's existing add-listener-per-instance pattern (see onResize).
    canvas.addEventListener("webglcontextrestored", this.onContextRestored, false);
  }

  /** Set by the canvas `webglcontextrestored` handler; drained once by render()
   *  to re-warm all shader programs the lost context dropped. */
  private restorePending = false;
  private onContextRestored = (): void => { this.restorePending = true; };

  private onResize = (): void => {
    this.camera.aspect = window.innerWidth / window.innerHeight;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(window.innerWidth, window.innerHeight);
    this.composer?.setSize(window.innerWidth, window.innerHeight);
  };

  /** Dispose the post chain FULLY. EffectComposer.dispose() frees only its two
   *  RTs + copy pass — UnrealBloomPass's mip-chain RTs (and the film pass) must
   *  be disposed explicitly or every rebuild (practice-gap crossing, heavy
   *  bursts, round end) leaks the whole bloom chain. */
  private teardownPost(): void {
    // a composer PARKED by the dimension-blend swap must die here too — this
    // is the real disposal path (map change, heavy burst, renderer teardown)
    if (this.parkedPost) {
      for (const p of this.parkedPost.composer.passes) (p as { dispose?: () => void }).dispose?.();
      this.parkedPost.composer.dispose();
      this.parkedPost = null;
    }
    if (!this.composer) { this.bloomPass = null; this.renderPass = null; this.aoPass = null; return; }
    for (const p of this.composer.passes) (p as { dispose?: () => void }).dispose?.();
    this.composer.dispose();
    this.composer = null;
    this.bloomPass = null;
    this.renderPass = null;
    this.aoPass = null;
  }

  /** Build the bloom + scanline chain. Neon maps only; day maps skip it. */
  private setupPost(theme: string): void {
    this.teardownPost();
    // `!postEnabled() || lowSpec()` still skips post entirely — the "works
    // everywhere" floor. DAY maps used to also skip it unconditionally; they now
    // get a RESTRAINED highlight bloom (2026-10-21 `graphics`) so sun glints on
    // metal/glass and bright edges bleed light like a real camera, instead of the
    // flat no-post look. The cost ladder (postKilled at L2+) and the split/spectate
    // direct paths bypass it exactly as they do for neon, so weak machines and
    // couch seats are unaffected. The render path is theme-agnostic; only the
    // bloom TUNE differs below.
    if (!postEnabled() || lowSpec()) return;
    const day = theme === "day";

    // ANTIALIASING ON THE POST PATH. The canvas `antialias` flag only covers
    // the default framebuffer; with the composer live the scene renders into
    // the composer's own targets, which had NO multisampling — every neon map
    // (and every day map since day bloom) was drawing aliased edges. MSAA on
    // the scene target restores it; the count comes from the quality preset
    // (0 on integrated GPUs, where it is one of the biggest fill costs).
    const msaa = this.caps.msaa;
    const pr = this.renderer.getPixelRatio();
    const target = new THREE.WebGLRenderTarget(
      Math.max(1, Math.floor(window.innerWidth * pr)),
      Math.max(1, Math.floor(window.innerHeight * pr)),
      { type: THREE.HalfFloatType, samples: msaa },
    );
    const composer = new EffectComposer(this.renderer, target);
    composer.setPixelRatio(pr);
    composer.setSize(window.innerWidth, window.innerHeight);
    const renderPass = new RenderPass(this.scene, this.camera);
    composer.addPass(renderPass);
    this.renderPass = renderPass;
    // SCREEN-SPACE AO (High preset). N8AO reconstructs normals from depth, so
    // unlike SSAO/GTAO it needs no extra normal pass — it renders the beauty
    // frame + depth ONCE itself and replaces the RenderPass (syncAoPass keeps
    // exactly one of the two enabled). Half-res + "Performance" sampling keeps
    // it to a fraction of a ms on the GPUs that qualify for High.
    if (this.caps.ao && n8aoMod) this.addAoPass(composer, n8aoMod);

    // subtle bloom — glow, not blur-out. HALF-res blur chain: bloom is a blur
    // anyway, and full-res mips were the bulk of the cost. DAY uses a HIGH
    // threshold (0.9) + low strength so only genuine highlights bloom — a bright
    // daylit scene has huge areas above neon's 0.55 threshold, which would
    // white-out; neon keeps its punchy glow (threshold 0.55, strength 0.55).
    // (values live in gfxConfig.ts GFX — day 0.22/0.5/0.9, neon 0.55/0.7/0.55)
    const bloom = new UnrealBloomPass(
      new THREE.Vector2(window.innerWidth / 2, window.innerHeight / 2),
      day ? GFX.bloomStrengthDay : GFX.bloomStrengthNeon,
      day ? GFX.bloomRadiusDay : GFX.bloomRadiusNeon,
      day ? GFX.bloomThresholdDay : GFX.bloomThresholdNeon, // day blooms only glints
    );
    composer.addPass(bloom);
    // Film grain reads as moody neon texture but as dirt on a clean daylight
    // scene, so day skips it; neon keeps the subtle grain it always had.
    if (!day) composer.addPass(new FilmPass(0.28, false));
    composer.addPass(new OutputPass());
    this.composer = composer;
    this.bloomPass = bloom;
    this.syncAoPass();
  }

  private addAoPass(composer: EffectComposer, mod: N8AOModule): void {
    const pr = this.renderer.getPixelRatio();
    const ao = new mod.N8AOPass(this.scene, this.camera, window.innerWidth * pr, window.innerHeight * pr);
    // never auto-switch to transparency-aware mode: it adds two more full
    // scene renders, and the neon glow/decal layers are mostly transparent
    ao.autoDetectTransparency = false;
    ao.configuration.transparencyAware = false;
    ao.configuration.gammaCorrection = false; // OutputPass does tone map + sRGB
    ao.configuration.halfRes = true;
    ao.setQualityMode("Performance");
    ao.beautyRenderTarget.samples = this.caps.msaa; // keep MSAA when AO owns the beauty pass
    // straight after the RenderPass it stands in for (index 0)
    composer.insertPass(ao, 1);
    this.aoPass = ao;
    this.tuneAoPass();
    this.syncAoPass();
  }

  /** Re-read every GFX tuning value (gfxConfig.ts) into the live scene. Called
   *  by the debug panel on each edit. Cheap; never rebuilds the post chain or
   *  re-derives shader programs (all of these are uniforms / plain fields). */
  applyGfxConfig(): void {
    this.renderer.toneMappingExposure = GFX.exposure;
    const b = this.bloomPass;
    if (b) {
      const day = this.loadedTheme === "day";
      b.strength = day ? GFX.bloomStrengthDay : GFX.bloomStrengthNeon;
      b.radius = day ? GFX.bloomRadiusDay : GFX.bloomRadiusNeon;
      b.threshold = day ? GFX.bloomThresholdDay : GFX.bloomThresholdNeon;
    }
    this.applyFog();
    this.tuneAoPass();
    this.syncAoPass();
    for (const l of this.shadowLights()) {
      l.shadow.bias = GFX.shadowBias;
      l.shadow.normalBias = GFX.shadowNormalBias;
    }
    this.renderer.shadowMap.needsUpdate = true;
    this.refreshEnv();
  }

  /** What the debug panel shows as read-only status. */
  gfxStatus(): string {
    const i = this.renderer.info.render;
    return `${gfxQuality()} · L${this.gfxLvl} · ${this.pixelScale.toFixed(2)}x · ${i.calls} calls · ` +
      `shadow ${this.caps.shadowSize || "off"}${this.shadowFit.follow ? " follow" : ""} · msaa ${this.caps.msaa} · ` +
      `ao ${this.aoPass?.enabled ? "on" : "off"} · hdri ${this.scene.environment === hdriEnv?.rt.texture ? "on" : "off"}`;
  }

  /** AO runs only at ladder L0 — it is the first thing to go when frames are
   *  late (before shadows, at L1). Exactly one of {RenderPass, N8AO} draws. */
  private syncAoPass(): void {
    const on = !!this.aoPass && this.gfxLvl === 0 && GFX.aoIntensity > 0;
    if (this.aoPass) this.aoPass.enabled = on;
    if (this.renderPass) this.renderPass.enabled = !on;
  }

  private tuneAoPass(): void {
    const ao = this.aoPass;
    if (!ao) return;
    ao.configuration.intensity = GFX.aoIntensity;
    ao.configuration.aoRadius = GFX.aoRadius;
    ao.configuration.distanceFalloff = GFX.aoDistanceFalloff;
  }

  private dayBuildingMat: THREE.MeshStandardMaterial | null = null;
  private skyDome: THREE.Mesh | null = null;
  /** Sky dome shader uniforms — the whole day/night cycle is now uniform writes
   *  on this one object (see buildSkyDome). Null until the first setSky. */
  private skyU: SkyUniforms | null = null;
  /** Live sun direction + sky colours, mirrored into the WATER shader so the
   *  sea reflects the sky it is actually under (see setSky / loadMap). */
  private readonly sunDir = new THREE.Vector3(0.42, 0.84, 0.31).normalize();
  /** Live sun direction (normalized; follows the theme, night cycle and
   *  practice-field dimension blend). Read-only by convention — the
   *  contact-shadow field uses it to displace blobs the way a real sun would. */
  sunDirection(): THREE.Vector3 { return this.sunDir; }
  /** Persistent sky canvas+texture: repainted in place per blend step — a
   *  fresh canvas + CanvasTexture per step meant a full 512x256 GPU upload
   *  allocation churn during every day/night tick and gap crossing. */
  private skyCanvas: HTMLCanvasElement | null = null;
  private skyTex: THREE.CanvasTexture | null = null;
  private pmrem: THREE.PMREMGenerator | null = null;
  private envRT: THREE.WebGLRenderTarget | null = null;
  private envStep = -1;
  /** `night` of the last env build — the neon theme passes 1, so 0 here
   *  means a day map at full daylight (the only place the HDRI applies). */
  private envNight = 1;
  /** Set once precompile() finishes: the match is live, no env swaps. */
  private envLocked = false;

  private useHdri(): boolean {
    return this.caps.hdri && GFX.hdri && !lowSpec() && hdriEnv !== null;
  }

  /** Swap the photographed sky in as the environment, rotated so its sun
   *  sits on the same azimuth as the scene's sun (sunDir). */
  private applyHdriEnv(): void {
    if (!hdriEnv) return;
    this.scene.environment = hdriEnv.rt.texture;
    // three rotates the LOOKUP direction by -rotation; sampling world azimuth
    // a then reads texture azimuth a + y, so y = hdriSun - sceneSun lines up
    const sceneAz = Math.atan2(this.sunDir.z, this.sunDir.x);
    this.scene.environmentRotation.set(0, hdriEnv.sunAz - sceneAz, 0);
  }

  /** Kick off the one-per-tab HDRI load (High preset). When it lands, the
   *  active renderer swaps it in if it is sitting at full daylight. */
  private ensureHdri(): void {
    if (!this.caps.hdri || lowSpec() || hdriLoad) return;
    hdriLoad = new RGBELoader().setDataType(THREE.FloatType).loadAsync(HDRI_URL).then((tex) => {
      const sunAz = normaliseHdri(tex);
      const r = activeRenderer;
      if (!r) { tex.dispose(); hdriLoad = null; return; }
      r.pmrem ??= new THREE.PMREMGenerator(r.renderer);
      hdriEnv = { rt: r.pmrem.fromEquirectangular(tex), sunAz };
      tex.dispose(); // the prefiltered target is all we keep
      // never swap the lighting under a live match — a late arrival waits
      // for the next map load (precompile gives it a bounded wait)
      if (!r.envLocked) r.refreshEnv();
    }, (e) => { console.warn("[hdri] load failed — painted sky probe stays", e); });
  }

  /** Re-pick the environment for the current sky step (HDRI toggled, HDRI
   *  just loaded, or the debug panel changed the intensity). */
  refreshEnv(): void {
    if (lowSpec() || this.envStep < 0) return;
    if (this.envStep === 0 && this.envNight === 0 && this.useHdri()) this.applyHdriEnv();
    else if (this.scene.environment !== this.envRT?.texture) {
      // was on the HDRI, go back to the painted probe: force a rebuild on the
      // next setSky — or right now from the dome's live colours
      this.envStep = -1;
      const u = this.skyU;
      if (u) this.setSky("#" + u.uZenith.value.getHexString(), "#" + u.uHorizon.value.getHexString(), "#" + u.uMid.value.getHexString(), u.uNight.value);
    }
    this.scene.environmentIntensity = GFX.envIntensity;
  }

  /** The sky dome's shader. Replaces (2026-08-01, mission `graphics`) a painted
   *  512x256 canvas stretched over a 560 m sphere — see skyTextures.ts for why
   *  that had to go. Everything here is analytic or a cached texture lookup:
   *
   *   - the vertical gradient is evaluated per PIXEL, so there is no banding and
   *     no resolution to run out of;
   *   - the sun is a real disc with limb, halo and forward scatter, placed on
   *     the SAME vector as the scene's directional light, so sky and shadows
   *     finally agree;
   *   - TWO cloud decks at different altitudes are sampled through a true
   *     ground-plane projection (`camXZ + dir.xz * H / dir.y`), which means they
   *     have correct perspective — converging to the horizon — and real
   *     PARALLAX as the player moves, despite the dome being camera-centred and
   *     there being no extra geometry at all. They drift, and they are shaded by
   *     the cloud-top normal baked into the texture;
   *   - night is the star field + a moon with maria, and Skyhaven's synthwave
   *     sky and striped retro sun are a `uSynth` branch.
   *
   *  Cost: one full-screen-ish pass over ~30 ALU + 4 texture fetches, no CPU
   *  work per frame beyond three uniform writes in `render()`. */
  private buildSkyDome(): void {
    const u: SkyUniforms = {
      uZenith: { value: new THREE.Color("#b9dcf2") },
      uMid: { value: new THREE.Color("#f3efe6") },
      uHorizon: { value: new THREE.Color("#6f9fce") },
      uFog: { value: new THREE.Color("#c7d8e4") },
      uSunDir: { value: this.sunDir.clone() },
      uMoonDir: { value: new THREE.Vector3(-0.45, 0.55, -0.7).normalize() },
      uNight: { value: 0 },
      uSynth: { value: 0 },
      uCloudAmt: { value: 1 },
      uCloud: { value: cloudTexture() },
      uStars: { value: starTexture() },
      uMoon: { value: moonTexture() },
      uCamXZ: { value: new THREE.Vector2() },
      uTime: { value: 0 },
    };
    this.skyU = u;
    const mat = new THREE.ShaderMaterial({
      uniforms: u as unknown as Record<string, THREE.IUniform>,
      side: THREE.BackSide,
      fog: false,
      depthWrite: false,
      vertexShader: `
        varying vec3 vDir;
        void main() {
          // the dome is a unit-ish sphere centred on the camera, so the local
          // vertex position IS the view direction — no matrix work needed
          vDir = normalize(position);
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }`,
      fragmentShader: `
        uniform vec3 uZenith, uMid, uHorizon, uFog, uSunDir, uMoonDir;
        uniform float uNight, uSynth, uCloudAmt, uTime;
        uniform sampler2D uCloud, uStars, uMoon;
        uniform vec2 uCamXZ;
        varying vec3 vDir;

        // One cloud deck: the ground-plane projection of the view ray onto a
        // horizontal plane H metres above the camera. Dividing by dir.y is
        // what gives the deck perspective AND parallax for free.
        vec4 deck(float H, float scale, vec2 drift, float cover, float uoff) {
          vec3 D = normalize(vDir);
          vec2 uv = (uCamXZ + D.xz * (H / max(D.y, 0.018))) * scale + drift + uoff;
          vec4 t = texture2D(uCloud, uv);
          return vec4(t.rgb, smoothstep(cover, cover + 0.34, t.a));
        }

        void main() {
          // RENORMALIZE: vDir is interpolated across the dome's 48x24 quads, so
          // the raw varying is only unit-length AT the vertices. Left as-is,
          // every angular test (the sun disc, the moon) contours along the
          // tessellation and comes out POLYGONAL — a dashed heptagon ring
          // around the moon, measured in the first capture of this pass.
          vec3 D = normalize(vDir);
          float h = D.y;
          float day = 1.0 - uNight;
          vec3 sunCol = vec3(1.0, 0.94, 0.80);

          // ---- base gradient. uMid is the HAZE band and is confined to a low
          // wedge near the horizon (that is where atmospheric scattering piles
          // up); above it the sky is uHorizon -> uZenith. The old painted dome
          // put the near-white mid stop across a third of the sky, which is why
          // it read as a milky lid and why nothing drawn on it had contrast.
          vec3 sky = mix(uHorizon, uZenith, smoothstep(0.0, 0.62, h));
          sky = mix(sky, uMid, smoothstep(0.20, -0.02, h) * 0.8);
          sky = mix(uFog, sky, smoothstep(-0.11, 0.05, h));

          // ---- SYNTHWAVE override (Skyhaven nights)
          if (uSynth * uNight > 0.004) {
            vec3 sw = vec3(0.086, 0.039, 0.204);
            sw = mix(sw, vec3(0.24, 0.083, 0.376), smoothstep(0.52, 0.18, h));
            sw = mix(sw, vec3(0.63, 0.12, 0.43), smoothstep(0.28, 0.09, h));
            sw = mix(sw, vec3(1.0, 0.35, 0.16), smoothstep(0.11, 0.005, h));
            sw = mix(sw, vec3(0.11, 0.04, 0.19), smoothstep(0.0, -0.10, h));
            sky = mix(sky, sw, uSynth * uNight);
          }

          // ---- SUN: disc + limb + inner halo + broad forward scatter
          float sd = dot(D, uSunDir);
          float disc = smoothstep(0.99930, 0.99970, sd);
          sky += sunCol * day * (1.0 - uSynth * uNight) * (
                   disc * 2.4
                 + pow(max(sd, 0.0), 260.0) * 0.55
                 + pow(max(sd, 0.0), 22.0) * 0.20
                 + pow(max(sd, 0.0), 3.0) * 0.07);

          // ---- NIGHT: stars, milky way, moon
          if (uNight > 0.008) {
            vec2 suv = vec2(atan(D.z, D.x) * 0.15915494 + 0.5,
                            clamp(asin(clamp(h, -1.0, 1.0)) * 0.31830989 + 0.5, 0.001, 0.999));
            sky += texture2D(uStars, suv).rgb * uNight * smoothstep(-0.01, 0.20, h)
                 * (1.0 - uSynth * 0.30);

            float md = dot(D, uMoonDir);
            sky += vec3(0.62, 0.58, 0.78) * pow(max(md, 0.0), 900.0) * 0.5 * uNight;
            if (md > 0.985) {
              vec3 mr = normalize(cross(uMoonDir, vec3(0.0, 1.0, 0.0)));
              vec3 mu = cross(mr, uMoonDir);
              vec2 mp = vec2(dot(D, mr), dot(D, mu)) / 0.11 * 0.5 + 0.5;
              vec4 mt = texture2D(uMoon, clamp(mp, 0.0, 1.0));
              vec3 mcol = mt.rgb;
              float ma = mt.a;
              if (uSynth > 0.5) {
                // the iconic retro sun: hot gradient + widening horizontal gaps
                float ty = clamp(mp.y, 0.0, 1.0);
                mcol = mix(vec3(0.76, 0.12, 0.47),
                           mix(vec3(1.0, 0.39, 0.65), vec3(1.0, 0.89, 0.42), smoothstep(0.5, 1.0, ty)),
                           smoothstep(0.0, 0.5, ty));
                float q = max(0.0, 0.5 - ty);
                ma *= 1.0 - step(fract(q * 11.0), 0.16 + q * 0.95);
              } else {
                mcol *= vec3(0.95, 0.94, 1.0);
              }
              sky = mix(sky, mcol, clamp(ma, 0.0, 1.0) * uNight);
            }
          }

          // ---- CLOUD DECKS: high thin sheet first, low cumulus over it
          float hf = smoothstep(0.012, 0.15, h); // the deck goes edge-on at the horizon
          // a REAL cumulus is near-white on top and genuinely dark underneath —
          // that range is the whole reason a cloud reads as a solid body rather
          // than as fog. Keep the two ends far apart.
          vec3 shadeCol = mix(vec3(0.26, 0.31, 0.42), uHorizon, 0.40);
          vec3 litCol = mix(vec3(1.06, 1.04, 1.00), sunCol, 0.25);
          float rim = pow(max(sd, 0.0), 10.0) * day;

          vec4 hi = deck(760.0, 0.00048, vec2(uTime * 0.0011, -uTime * 0.0007), 0.42, 0.37);
          vec3 hin = normalize(vec3(hi.r, hi.b, hi.g) * 2.0 - 1.0);
          vec3 hic = mix(shadeCol, litCol, clamp(dot(hin, uSunDir) * 0.75 + 0.52, 0.0, 1.0));
          hic = mix(hic, uZenith * 0.5 + vec3(0.02), uNight);
          sky = mix(sky, hic + sunCol * rim * 0.35, hi.a * hf * uCloudAmt * 0.62);

          vec4 lo = deck(300.0, 0.00100, vec2(uTime * 0.0034, uTime * 0.0021), 0.30, 0.0);
          vec3 lon = normalize(vec3(lo.r, lo.b, lo.g) * 2.0 - 1.0);
          vec3 loc = mix(shadeCol, litCol, clamp(dot(lon, uSunDir) * 0.85 + 0.42, 0.0, 1.0));
          loc = mix(loc, uZenith * 0.55 + vec3(0.03), uNight);
          sky = mix(sky, loc + sunCol * rim * 0.55, lo.a * hf * uCloudAmt);

          // ordered-ish dither: a 1/255 jiggle kills the banding a smooth
          // gradient across 1000 px of sky would otherwise show
          sky += (fract(sin(dot(gl_FragCoord.xy, vec2(12.9898, 78.233))) * 43758.545) - 0.5) * 0.0039;

          gl_FragColor = vec4(sky, 1.0);
          #include <tonemapping_fragment>
          #include <colorspace_fragment>
        }`,
    });
    this.skyDome = new THREE.Mesh(new THREE.SphereGeometry(560, 48, 24), mat);
    this.skyDome.renderOrder = -1; // draw first, behind everything
    this.skyDome.frustumCulled = false;
    this.scene.add(this.skyDome);
  }

  /** Sky colours + sun + night blend. `night` in [0,1] cross-fades day and
   *  night (used by the day/night cycle and the practice-field dimension
   *  blend). Writes the dome's uniforms — no canvas repaint unless the
   *  environment map's quantized step actually moved. */
  private setSky(zenith: string, horizon: string, mid: string, night = 0): void {
    if (!this.skyDome) this.buildSkyDome();
    const u = this.skyU!;
    u.uZenith.value.set(zenith);
    u.uMid.value.set(mid);
    u.uHorizon.value.set(horizon);
    u.uFog.value.copy(this.fogBase.color);
    u.uNight.value = night;
    u.uSunDir.value.copy(this.sunDir);
    // The MOON tracks the cycle now (it was pinned at its init vector while the
    // sun set around it): roughly opposite the sun's azimuth, climbing as the
    // night deepens — so dusk has a low rising moon and deep night a high one.
    u.uMoonDir.value.set(-this.sunDir.x - 0.15, 0.3 + night * 0.5, -this.sunDir.z - 0.35).normalize();
    u.uSynth.value = (this.loadedMapId === "neo_island" || this.loadedMapId === "rooftop_spiderweb") ? 1 : 0;
    // the neon/synthwave dimension is a clear night; daylight maps are cloudy
    u.uCloudAmt.value = 1 - 0.62 * night;
    this.syncWaterSky();

    // Environment map source. PMREM wants an equirect texture, so this stays a
    // painted canvas — but it is BLURRED to irradiance, so it only has to carry
    // colour, and it is repainted only when the quantized step actually moves
    // (it used to repaint on every blend tick and throw the result away).
    const envStep = Math.round(night * 3); // fewer PMREM regens/cycle (each is a big hitch)
    if (envStep !== this.envStep && !lowSpec()) {
      this.envStep = envStep;
      this.envNight = night;
      if (envStep === 0 && this.useHdri()) this.applyHdriEnv();
      else {
        this.paintEnvCanvas(zenith, horizon, mid, night);
        this.pmrem ??= new THREE.PMREMGenerator(this.renderer);
        const rt = this.pmrem.fromEquirectangular(this.skyTex!);
        this.scene.environment = rt.texture;
        this.scene.environmentRotation.set(0, 0, 0);
        this.envRT?.dispose();
        this.envRT = rt;
      }
      this.scene.environmentIntensity = GFX.envIntensity; // ambience, not a mirror world (0.55)
    }
    // fallback behind the dome — reuse the Color, no per-repaint alloc
    if (this.scene.background instanceof THREE.Color) this.scene.background.set(horizon);
    else this.scene.background = new THREE.Color(horizon);
  }

  /** Push the live sky colours + sun vector into the water shader. Called from
   *  setSky (theme / blend step) and from loadMap once the water exists — the
   *  map's water is built AFTER applyAtmosphere, so setSky alone would leave a
   *  freshly loaded sea on its constructor defaults until the next blend tick. */
  private syncWaterSky(): void {
    const w = this.waterUniforms, u = this.skyU;
    if (!w || !u) return;
    w.uSkyLo.value.copy(u.uHorizon.value);
    w.uSkyHi.value.copy(u.uZenith.value);
    w.uSunDir.value.copy(this.sunDir);
    w.uNight.value = u.uNight.value;
  }

  /** Repaint the low-res equirect canvas that feeds the PMREM environment map.
   *  This is NOT the visible sky any more (buildSkyDome owns that) — it only
   *  has to carry the right colours and rough light placement into the blurred
   *  irradiance probe, which is why it stays at 512x256. */
  private paintEnvCanvas(zenith: string, horizon: string, mid: string, night: number): void {
    const W = 512, H = 256; // half-res: PMREM blurs it to irradiance anyway
    if (!this.skyCanvas) {
      this.skyCanvas = document.createElement("canvas");
      this.skyCanvas.width = W;
      this.skyCanvas.height = H;
    }
    const cvs = this.skyCanvas;
    const g = cvs.getContext("2d")!;
    // dome UVs: v=0 zenith, v≈0.5 horizon, v=1 below the world (never seen)
    const grad = g.createLinearGradient(0, 0, 0, H);
    grad.addColorStop(0, zenith);
    grad.addColorStop(0.34, mid);
    grad.addColorStop(0.52, horizon);
    grad.addColorStop(1, horizon);
    g.fillStyle = grad;
    g.fillRect(0, 0, W, H);

    if (night < 1) {
      // sun + halo
      g.globalAlpha = 1 - night;
      const sx = 700, sy = 130;
      const halo = g.createRadialGradient(sx, sy, 8, sx, sy, 95);
      halo.addColorStop(0, "rgba(255,246,214,0.95)");
      halo.addColorStop(0.3, "rgba(255,240,190,0.5)");
      halo.addColorStop(1, "rgba(255,240,190,0)");
      g.fillStyle = halo;
      g.fillRect(sx - 95, sy - 95, 190, 190);
      g.fillStyle = "#fffcf0";
      g.beginPath(); g.arc(sx, sy, 24, 0, Math.PI * 2); g.fill();
      // soft cumulus blobs (fixed layout — deterministic, repaint-stable)
      const clouds: [number, number, number][] = [[150, 170, 62], [330, 115, 48], [520, 195, 70], [830, 215, 52], [70, 245, 44], [940, 120, 40]];
      g.fillStyle = "rgba(255,255,255,0.85)";
      for (const [cx, cy, r] of clouds) {
        for (let k = 0; k < 5; k++) {
          const rr = r * (0.36 - Math.abs(k - 2) * 0.055);
          g.beginPath();
          g.ellipse(cx + (k - 2) * r * 0.42, cy + Math.sin(k * 2.1) * r * 0.12, rr * 1.6, rr, 0, 0, Math.PI * 2);
          g.fill();
        }
      }
      g.globalAlpha = 1;
    }
    if (night > 0 && (this.loadedMapId === "neo_island" || this.loadedMapId === "rooftop_spiderweb")) {
      // SKYHAVEN gets a full SYNTHWAVE night: a purple→magenta→hot-orange sky
      // and a big striped retro sun-moon on the horizon.
      g.globalAlpha = night;
      const sg = g.createLinearGradient(0, 0, 0, H);
      sg.addColorStop(0, "#160a34");    // deep indigo zenith
      sg.addColorStop(0.32, "#3d1560");  // purple
      sg.addColorStop(0.46, "#a11f6e");  // magenta
      sg.addColorStop(0.53, "#ff5a2a");  // hot horizon band
      sg.addColorStop(0.6, "#7a1550");
      sg.addColorStop(1, "#1c0a30");
      g.fillStyle = sg;
      g.fillRect(0, 0, W, H);
      let seed = 4242;
      const rnd = (): number => (seed = (seed * 1103515245 + 12345) & 0x7fffffff) / 0x7fffffff;
      for (let k = 0; k < 140; k++) {
        const x = rnd() * W, y = rnd() * H * 0.4, r = 0.3 + rnd() * 1.1;
        g.globalAlpha = night * (0.2 + rnd() * 0.7);
        g.fillStyle = rnd() > 0.8 ? "#ffd0f0" : "#ffffff";
        g.beginPath(); g.arc(x, y, r, 0, Math.PI * 2); g.fill();
      }
      // the retro sun-moon: glow + gradient disc + horizontal gap lines
      g.globalAlpha = night;
      const mx = 256, my = 150, R = 74;
      const glow = g.createRadialGradient(mx, my, 12, mx, my, R * 1.9);
      glow.addColorStop(0, "rgba(255,120,200,0.55)");
      glow.addColorStop(1, "rgba(255,120,200,0)");
      g.fillStyle = glow;
      g.fillRect(mx - R * 1.9, my - R * 1.9, R * 3.8, R * 3.8);
      g.save();
      g.beginPath(); g.arc(mx, my, R, 0, Math.PI * 2); g.clip();
      const disc = g.createLinearGradient(mx, my - R, mx, my + R);
      disc.addColorStop(0, "#ffe36b"); disc.addColorStop(0.5, "#ff64a6"); disc.addColorStop(1, "#c31f77");
      g.fillStyle = disc;
      g.fillRect(mx - R, my - R, 2 * R, 2 * R);
      g.fillStyle = "#160a34"; // the iconic widening gaps across the lower half
      for (let i = 0; i < 7; i++) g.fillRect(mx - R, my + 6 + i * (R / 6.5), 2 * R, 2 + i * 0.9);
      g.restore();
      g.globalAlpha = 1;
    } else if (night > 0) {
      // starfield (deterministic LCG so repaints don't twinkle) + neon moon
      let seed = 1337;
      const rnd = (): number => (seed = (seed * 1103515245 + 12345) & 0x7fffffff) / 0x7fffffff;
      for (let k = 0; k < 240; k++) {
        const x = rnd() * W, y = rnd() * H * 0.48, r = 0.3 + rnd() * 1.2;
        g.globalAlpha = night * (0.25 + rnd() * 0.75);
        g.fillStyle = rnd() > 0.85 ? "#ffd0f0" : "#ffffff";
        g.beginPath(); g.arc(x, y, r, 0, Math.PI * 2); g.fill();
      }
      g.globalAlpha = night;
      const mx = 250, my = 110;
      const mglow = g.createRadialGradient(mx, my, 10, mx, my, 115);
      mglow.addColorStop(0, "rgba(255,170,240,0.9)");
      mglow.addColorStop(0.35, "rgba(200,120,255,0.35)");
      mglow.addColorStop(1, "rgba(200,120,255,0)");
      g.fillStyle = mglow;
      g.fillRect(mx - 115, my - 115, 230, 230);
      g.fillStyle = "#f2d9ff";
      g.beginPath(); g.arc(mx, my, 32, 0, Math.PI * 2); g.fill();
      g.fillStyle = "rgba(205,155,225,0.55)"; // craters
      for (const [ox, oy, r] of [[-9, -5, 7], [11, 8, 5], [3, -15, 4]] as const) {
        g.beginPath(); g.arc(mx + ox, my + oy, r, 0, Math.PI * 2); g.fill();
      }
      g.globalAlpha = 1;
    }

    if (!this.skyTex) {
      this.skyTex = new THREE.CanvasTexture(cvs);
      this.skyTex.colorSpace = THREE.SRGBColorSpace;
      this.skyTex.magFilter = THREE.LinearFilter;
    } else {
      this.skyTex.needsUpdate = true; // repaint in place — one GPU texture, re-uploaded, never re-allocated
    }
  }

  /** Configure a directional light to cast good soft shadows over the arena. */
  private configureShadow(light: THREE.DirectionalLight): void {
    light.castShadow = true;
    // Shadow map size is HARDWARE-GATED (2026-10-21 `graphics`): a capable GPU
    // gets 1024² (4× the texels → crisp contact shadows over the ±60 m frustum),
    // weak/low-spec machines keep the cheap 512². The shadow pass is the biggest
    // GPU cost on these light scenes, but `autoUpdate=false` means the map only
    // RE-renders on a map load / light-rig change (static casters), not per
    // frame, so the per-frame cost of the bigger map is just the sample — cheap
    // on the hardware that qualifies. Higher res also shrinks the texel, so the
    // existing bias (-0.0004) / normalBias (0.02), tuned at 512, stay safe (less
    // acne, not more). The screenshot rig (`gfxPinned`) qualifies, so the owner's
    // A/B captures the crisp path.
    // 2026-11 (quality presets): the size now comes from the preset —
    // auto keeps the old hardware gate, Medium 1024, High 2048. Low runs with
    // shadows off (ladder floor L2); 512 is just the safe allocation then.
    const sz = lowSpec() ? 512 : this.caps.shadowSize || 512;
    light.shadow.mapSize.set(sz, sz);
    const c = light.shadow.camera;
    c.near = 1;
    c.far = 260;
    // the frustum is fitted per map (fitShadowFrustum) — this is the default
    c.left = -SHADOW_HALF;
    c.right = SHADOW_HALF;
    c.top = SHADOW_HALF;
    c.bottom = -SHADOW_HALF;
    light.shadow.bias = GFX.shadowBias;
    light.shadow.normalBias = GFX.shadowNormalBias;
    this.fitShadowLight(light);
  }

  // ---- SHADOW FRUSTUM. It used to be a fixed ±60 m box around the world
  // origin: on the 2x-scaled maps everything past 60 m had NO shadows at all,
  // and on small maps most of the 1024² texels covered empty space. Now:
  //  - a map that fits inside the box gets a box fitted to its bounds, centred
  //    on it, never moving (tightest texels, zero shimmer);
  //  - a bigger map gets a SHADOW_HALF box that FOLLOWS the camera, snapped to
  //    whole shadow texels in light space so edges don't crawl as you walk.
  // Cost: the shadow map already re-renders up to 60x/s (render()), so moving
  // its camera between those updates is free.
  /** The theme rig's sun (applyAtmosphere replaces it per map/theme). */
  private themeSun: THREE.DirectionalLight | null = null;
  /** Every live rig light that casts the sun shadow (theme, blend, night) —
   *  rebuilt into a reused array, no allocation. */
  private readonly shadowLightBuf: THREE.DirectionalLight[] = [];
  private shadowLights(): THREE.DirectionalLight[] {
    const b = this.shadowLightBuf;
    b.length = 0;
    if (this.themeSun) b.push(this.themeSun);
    if (this.blendRig) b.push(this.blendRig.dir);
    if (this.nightRig) b.push(this.nightRig.sun);
    return b;
  }
  /** Light position relative to its target — the rigs' `position.set(...)`
   *  values. Kept per light so the follow can move both together. */
  private readonly sunOffsets = new WeakMap<THREE.DirectionalLight, THREE.Vector3>();
  /** Map bounds centre (x,z) + half-extent; follow=false when the map fits. */
  private readonly shadowFit = { cx: 0, cz: 0, half: SHADOW_HALF, follow: false };
  private readonly shadowCenter = new THREE.Vector3();
  private readonly shadowTmpR = new THREE.Vector3();
  private readonly shadowTmpU = new THREE.Vector3();
  private readonly shadowTmpD = new THREE.Vector3();

  /** Rigs call this instead of `light.position.set(...)`. */
  private setSunOffset(light: THREE.DirectionalLight, x: number, y: number, z: number): void {
    let o = this.sunOffsets.get(light);
    if (!o) { o = new THREE.Vector3(); this.sunOffsets.set(light, o); }
    o.set(x, y, z);
    this.placeShadowLight(light);
  }

  /** Size the shadow box for a freshly loaded map. */
  private fitShadowFrustum(boxes: readonly MapBox[]): void {
    let x0 = Infinity, x1 = -Infinity, z0 = Infinity, z1 = -Infinity;
    for (const b of boxes) {
      const hx = b.s[0] / 2, hz = b.s[2] / 2;
      if (b.p[0] - hx < x0) x0 = b.p[0] - hx;
      if (b.p[0] + hx > x1) x1 = b.p[0] + hx;
      if (b.p[2] - hz < z0) z0 = b.p[2] - hz;
      if (b.p[2] + hz > z1) z1 = b.p[2] + hz;
    }
    const f = this.shadowFit;
    if (!Number.isFinite(x0)) { f.cx = 0; f.cz = 0; f.half = SHADOW_HALF; f.follow = false; }
    else {
      // +4 m margin: casters at the very edge still throw into the map
      const half = Math.max(x1 - x0, z1 - z0) / 2 + 4;
      f.cx = (x0 + x1) / 2;
      f.cz = (z0 + z1) / 2;
      // a little over the box still fits statically (≤12.5 cm texels at 1024)
      // — a fixed box never shimmers, so only clearly-big maps follow
      f.follow = half > SHADOW_HALF * 1.3;
      // fitted box is a SQUARE (the light looks down at an angle, so the
      // projected footprint is a skewed version of it — the margin covers that)
      f.half = f.follow ? SHADOW_HALF : Math.max(20, half);
    }
    for (const l of this.shadowLights()) this.fitShadowLight(l);
    this.renderer.shadowMap.needsUpdate = true;
  }

  private fitShadowLight(light: THREE.DirectionalLight): void {
    const c = light.shadow.camera;
    const h = this.shadowFit.half;
    if (c.right !== h) {
      c.left = -h; c.right = h; c.top = h; c.bottom = -h;
      c.updateProjectionMatrix();
    }
    this.placeShadowLight(light);
  }

  /** Put the light + its target at the shadow centre (map centre, or the
   *  texel-snapped camera position on a big map). */
  private placeShadowLight(light: THREE.DirectionalLight): void {
    const off = this.sunOffsets.get(light);
    if (!off) return;
    const f = this.shadowFit;
    const ctr = this.shadowCenter;
    if (!f.follow) ctr.set(f.cx, 0, f.cz);
    else {
      ctr.copy(this.camera.position);
      // SNAP in light space: project onto the two axes of the shadow image,
      // round to a whole texel, rebuild. Movement along the light direction
      // doesn't move texels, so it is left alone.
      const d = this.shadowTmpD.copy(off).normalize();
      const r = this.shadowTmpR.set(0, 1, 0).cross(d);
      if (r.lengthSq() < 1e-6) r.set(1, 0, 0); else r.normalize();
      const u = this.shadowTmpU.copy(d).cross(r);
      const texel = (2 * f.half) / light.shadow.mapSize.x;
      const pr = ctr.dot(r), pu = ctr.dot(u);
      ctr.addScaledVector(r, Math.round(pr / texel) * texel - pr);
      ctr.addScaledVector(u, Math.round(pu / texel) * texel - pu);
    }
    light.target.position.copy(ctr);
    // a FIXED 120 m standoff along the sun direction (a directional light only
    // uses the direction): the rigs' raw offsets shrink to ~31 m at dusk, which
    // put the shadow camera's near plane below tall casters and clipped them
    light.position.copy(ctr).addScaledVector(this.shadowTmpD.copy(off).normalize(), 120);
  }

  /** Per-frame: re-centre the shadow box on big maps. Allocation-free. */
  private followShadow(): void {
    if (!this.shadowFit.follow || !this.renderer.shadowMap.enabled) return;
    for (const l of this.shadowLights()) if (l.parent?.parent === this.scene) this.placeShadowLight(l);
  }

  private baseTheme: string = "neon";

  /** Sky + fog + lights + post for a theme. Reusable at runtime (e.g. the
   *  practice-field mirror flipping into a neon "dimension"). */
  private applyAtmosphere(theme: string): void {
    this.blendT = -1; // discrete re-theme invalidates the blend-rig cache
    this.renderer.shadowMap.needsUpdate = true; // new light rig → fresh shadows
    if (this.lightGroup) this.scene.remove(this.lightGroup);
    const lights = new THREE.Group();
    if (theme === "day") {
      this.sunDir.set(38, 76, 28).normalize(); // must match the DirectionalLight below
      this.setSky("#4f93da", "#a5cbe8", "#eaf3fa", 0);
      this.setBaseFog("#c7d8e4", 150, 520);
      lights.add(new THREE.HemisphereLight("#e6f2ff", "#7f7768", 0.95));
      const sun = new THREE.DirectionalLight("#fff1d2", 3.7);
      this.themeSun = sun;
      this.configureShadow(sun);
      this.setSunOffset(sun, 38, 76, 28);
      lights.add(sun, sun.target);
    } else {
      this.sunDir.set(25, 55, 15).normalize();
      this.setSky("#321461", "#160624", "#6d2e8d", 1);
      this.setBaseFog("#2b123f", 130, 430);
      lights.add(new THREE.HemisphereLight("#d7c6ff", "#322452", 3.25));
      lights.add(new THREE.AmbientLight("#8b6cff", 0.55));
      const dir = new THREE.DirectionalLight("#ffd6ea", 4.0);
      this.themeSun = dir;
      this.configureShadow(dir);
      this.setSunOffset(dir, 25, 55, 15);
      lights.add(dir, dir.target);
    }
    this.lightGroup = lights;
    this.scene.add(lights);
    this.setupPost(theme);
  }

  private blendRig?: { group: THREE.Group; hemi: THREE.HemisphereLight; amb: THREE.AmbientLight; dir: THREE.DirectionalLight };
  private blendT = -1;

  /** Practice field: continuous reality↔neon atmosphere blend across the gap
   *  (t=0 day field … t=1 neon copy). Flying across fades gradually; mirror
   *  teleports snap z so the blend snaps with them. Post-processing (not
   *  lerpable) flips at the midpoint. Values mirror applyAtmosphere's two
   *  themes — keep them in sync. */
  setDimensionBlend(t: number): void {
    const q = Math.max(0, Math.min(1, Math.round(t * 64) / 64));
    if (q === this.blendT) return;
    const prev = this.blendT;
    this.blendT = q;
    const mix = (a: string, b: string): string =>
      "#" + new THREE.Color(a).lerp(new THREE.Color(b), q).getHexString();
    if (!this.blendRig) {
      const hemi = new THREE.HemisphereLight();
      const amb = new THREE.AmbientLight("#c0a0ff");
      const dir = new THREE.DirectionalLight();
      this.configureShadow(dir);
      const group = new THREE.Group();
      group.add(hemi, amb, dir, dir.target);
      this.blendRig = { group, hemi, amb, dir };
    }
    const r = this.blendRig;
    if (this.lightGroup !== r.group) {
      if (this.lightGroup) this.scene.remove(this.lightGroup);
      this.lightGroup = r.group;
      this.scene.add(r.group);
    }
    this.sunDir.set(45 - 20 * q, 80 - 25 * q, 30 - 15 * q).normalize(); // == r.dir.position below
    this.setSky(mix("#4f93da", "#4a1a7a"), mix("#a5cbe8", "#2a0a4a"), mix("#eaf3fa", "#7a3aa0"), q);
    this.setBaseFog(mix("#cfe4f5", "#3a1a5a"), 150 + 10 * q, 460 + 20 * q);
    r.hemi.color.set(mix("#eaf3ff", "#ffd0ff"));
    r.hemi.groundColor.set(mix("#8a8570", "#6a4a8a"));
    r.hemi.intensity = 1.5 + (5.5 - 1.5) * q;
    r.amb.intensity = 1.6 * q;
    r.dir.color.set(mix("#fff2d0", "#ffd0f0"));
    r.dir.intensity = 2.3 + (3.2 - 2.3) * q;
    this.setSunOffset(r.dir, 45 - 20 * q, 80 - 25 * q, 30 - 15 * q);
    if (prev < 0 || (prev < 0.5) !== (q < 0.5)) this.swapBlendPost(q < 0.5 ? this.baseTheme : "neon");
  }

  /** Practice-field dimension crossings flip day↔neon CONSTANTLY — rebuilding
   *  the bloom chain each time (RT allocs + shader compiles) was a visible
   *  hitch at every gap crossing. Park the composer on the day side and
   *  re-arm it on the neon side instead of destroy/rebuild. */
  private parkedPost: { composer: EffectComposer; bloom: UnrealBloomPass; renderPass: RenderPass | null; aoPass: N8AOPass | null } | null = null;
  private swapBlendPost(theme: string): void {
    const wantPost = theme !== "day" && postEnabled() && !lowSpec();
    if (!wantPost) {
      if (this.composer) {
        this.parkedPost = { composer: this.composer, bloom: this.bloomPass!, renderPass: this.renderPass, aoPass: this.aoPass };
        this.composer = null;
        this.bloomPass = null;
        this.renderPass = null;
        this.aoPass = null;
      } else {
        this.teardownPost();
      }
      return;
    }
    if (this.parkedPost) {
      this.composer = this.parkedPost.composer;
      this.bloomPass = this.parkedPost.bloom;
      this.renderPass = this.parkedPost.renderPass;
      this.aoPass = this.parkedPost.aoPass;
      this.parkedPost = null;
      this.syncAoPass();
      // re-arm: window size / adaptive pixel ratio may have moved while parked
      this.composer.setPixelRatio(this.renderer.getPixelRatio());
      this.composer.setSize(window.innerWidth, window.innerHeight);
      return;
    }
    this.setupPost(theme);
  }

  private nightRig?: { group: THREE.Group; hemi: THREE.HemisphereLight; sun: THREE.DirectionalLight };
  private nightT = -1;

  /** Day↔night cycle for day-theme maps: t=0 bright day … t=1 DEEP night
   *  (near-lightless on purpose — the accidental "no light" look, kept).
   *  Continuous; call every frame with the cycle phase. */
  setNightBlend(t: number): void {
    const q = Math.max(0, Math.min(1, Math.round(t * 36) / 36));
    if (q === this.nightT) return;
    this.nightT = q;
    const mix = (a: string, b: string): string =>
      "#" + new THREE.Color(a).lerp(new THREE.Color(b), q).getHexString();
    if (!this.nightRig) {
      const hemi = new THREE.HemisphereLight();
      const sun = new THREE.DirectionalLight();
      this.configureShadow(sun);
      const group = new THREE.Group();
      group.add(hemi, sun, sun.target);
      this.nightRig = { group, hemi, sun };
    }
    const r = this.nightRig;
    if (this.lightGroup !== r.group) {
      if (this.lightGroup) this.scene.remove(this.lightGroup);
      this.lightGroup = r.group;
      this.scene.add(r.group);
    }
    // sky fades to a starfield (the dome's uNight brings up stars + the moon).
    // The sun SETS with the light: r.sun.position below is (45-30q, 80-62q,
    // 30-10q), so at dusk the disc sinks toward the horizon and its halo rakes
    // across the sky and the water instead of hanging at noon while the world
    // goes dark.
    this.sunDir.set(45 - 30 * q, 80 - 62 * q, 30 - 10 * q).normalize();
    // Palette (2026-08-01): the day zenith was #bfe3ff — a near-white that made
    // the whole dome a milky lid and left white clouds with nothing to contrast
    // against. A real zenith is deep blue and the PALE colour belongs in the
    // horizon haze band, which is how the shader now reads these three.
    this.setSky(mix("#4f93da", "#05070f"), mix("#a5cbe8", "#101a33"), mix("#eaf3fa", "#1b2540"), q);
    this.setBaseFog(mix("#cfe4f5", "#080c17"), 150 - 25 * q, 460 - 55 * q);
    r.hemi.color.set(mix("#eaf3ff", "#3a4e8c")); // cool-blue moonlight, not near-black
    r.hemi.groundColor.set(mix("#8a8570", "#20243a"));
    r.hemi.intensity = 1.5 - 0.58 * q; // → 0.92: unmistakably night, never blind
    r.sun.color.set(mix("#fff2d0", "#9fb6ee"));
    r.sun.intensity = 2.3 - 1.45 * q; // → 0.85: real moonlight
    if (this.dayBuildingMat) this.dayBuildingMat.emissiveIntensity = 0.12 + q * 1.0; // windows glow at night
    this.applySignGlow();
    // the sun sinks as night falls (long shadows through dusk)
    this.setSunOffset(r.sun, 45 - 30 * q, 80 - 62 * q, 30 - 10 * q);
    this.renderer.shadowMap.needsUpdate = true;
  }

  /** The map's lit-sign material, if this map has any. Held so the day/night
   *  cycle can drive it — a neon sign is barely visible against noon sun and is
   *  the brightest thing on the street at midnight, and that RANGE is most of
   *  what makes it read as a light source rather than as bright paint. */
  private signGlowMat: THREE.MeshStandardMaterial | null = null;
  /** ...and the shop-window glazing, on the same ramp with a narrower range.
   *  A shop interior is lit all day (it is indoors), so its floor is HIGHER
   *  than a neon tube's and its ceiling LOWER — a lit room at midnight should
   *  not outshine the sign hanging over it. */
  private shopGlowMat: THREE.MeshStandardMaterial | null = null;
  private applySignGlow(): void {
    // nightT is -1 until the first applyAtmosphere of a fresh map (line ~1012),
    // and a negative emissiveIntensity turns a neon tube into a black hole.
    const q = Math.max(0, this.nightT);
    if (this.signGlowMat) this.signGlowMat.emissiveIntensity = 0.55 + q * 2.1;
    if (this.shopGlowMat) this.shopGlowMat.emissiveIntensity = 0.7 + q * 1.15;
  }

  private loadedTheme = "day";
  private loadedMapId = "";
  /** Decor meshes that self-rotate each frame (e.g. Skyhaven's spinning vinyl,
   *  cassette reels) — collected from buildDecor via userData.spin. */
  private decorSpinners: THREE.Object3D[] = [];
  /** Boombox EQ bars (userData.eq band index) — driven by the music spectrum. */
  private decorEq: THREE.Object3D[] = [];
  /** InstancedMeshes (userData.beatRing) whose instances squash to the beat. */
  private decorBeat: THREE.InstancedMesh[] = [];
  /** Shared facade-histogram canvases (userData.histViz) redrawn per frame. */
  private decorHistViz: { canvas: HTMLCanvasElement; texture: THREE.CanvasTexture }[] = [];
  /** Ground contact points of the scattered decor props of the CURRENT map
   *  (decor.ts). Static for the life of the map; read once when the contact
   *  shadow field is built. */
  private decorGroundSpots: DecorGroundSpot[] = [];
  decorGroundContacts(): readonly DecorGroundSpot[] { return this.decorGroundSpots; }
  private beatM4 = new THREE.Matrix4();
  private beatQ = new THREE.Quaternion();
  private beatPos = new THREE.Vector3();
  private beatScl = new THREE.Vector3();
  /** Deep-free one subtree's GPU resources (shared-flag aware) — the same
   *  rules as dispose(), for mid-round swaps like the DeLorean time jump. */
  private disposeSubtree(root: THREE.Object3D): void {
    root.traverse((o) => {
      const mesh = o as THREE.Mesh;
      if (mesh.geometry && !mesh.geometry.userData?.shared) mesh.geometry.dispose();
      const mats = Array.isArray(mesh.material) ? mesh.material : mesh.material ? [mesh.material] : [];
      for (const m of mats) {
        const anyM = m as THREE.MeshStandardMaterial;
        if (anyM.map && !anyM.map.userData?.shared) anyM.map.dispose();
        if (anyM.bumpMap && !anyM.bumpMap.userData?.shared) anyM.bumpMap.dispose();
        m.dispose();
      }
    });
  }

  loadMap(map: MapDef): void {
    this.nightT = -1; // fresh map → fresh light rig (applyAtmosphere replaces it)
    this.envLocked = false; // precompile() re-locks once the new map is warm
    this.loadedMapId = map.id;
    // phase timings for the load-screen breakdown (one console line at the end)
    const mt0 = performance.now();
    const mt: string[] = [];
    let mtAt = mt0;
    const mmark = (name: string): void => { const n = performance.now(); mt.push(`${name}=${(n - mtAt).toFixed(0)}`); mtAt = n; };
    // a LIVE map swap (time jump) replaces the whole group — deep-dispose the
    // old one or every jump leaks a full map (merged cells, decor canvases,
    // water shaders) into a context that lives for the tab's lifetime
    if (this.mapGroup) { this.scene.remove(this.mapGroup); this.disposeSubtree(this.mapGroup); }
    if (this.lightGroup) this.scene.remove(this.lightGroup);

    const theme = map.theme ?? "neon";
    this.loadedTheme = theme;
    this.baseTheme = theme;
    this.applyAtmosphere(theme);
    this.fitShadowFrustum(map.boxes);

    const tex = mapTextures();
    mmark("atmos+tex");
    const group = new THREE.Group();
    // WATER boxes (moat / river / sea) are pulled out of the static merge and
    // rendered as one animated surface (rippling shader) instead of flat slabs.
    const WATER_COLORS = new Set(["#12506e", "#2d6774", "#2f5f77", "#3d7d8f"]);
    const LAVA_COLORS = new Set(["#b5330f"]); // the medieval castle moat
    const waterGeos: THREE.BufferGeometry[] = [];
    const lavaGeos: THREE.BufferGeometry[] = [];
    this.waterUniforms = null;
    this.waterMesh = null;
    this.lavaUniforms = null;
    // old rain (if any) rode the previous mapGroup and was just disposed above
    this.rainPoints = null;
    this.rainUniforms = null;
    this.chibreSprites = [];
    // Big maps get many boxes — cap per-box overhead: only large structural
    // boxes touch the shadow pass, and edge outlines are dropped (kept only on
    // glowing accents) once the map is dense. This is the main lag lever.
    const dense = map.boxes.length > 150;
    // TIMBER MAPS — the ship and the frontier town are built from sawn boards, so
    // their generic walls AND their architectural dressing (below) wear the
    // `wood` plank family in place of stucco/cast-stone. Scoped to these two ids;
    // every other map is byte-identical.
    const woodMap = map.id === "pirate_ship" || map.id === "wild_west_train_pursuit";
    // MERGED static geometry: one mesh per material family instead of one per
    // box. A 300-box map used to be ~600 draw calls (mesh + outline each) —
    // it's now under a dozen, which is the single biggest frame-time win on
    // modest GPUs. Box tint × baked AO live in vertex colors so boxes of any
    // colour share a material.
    const meshGroups = new Map<string, THREE.BufferGeometry[]>();
    const edgeGroups = new Map<string, THREE.BufferGeometry[]>();
    const detailGroups = new Map<string, THREE.BufferGeometry[]>();
    const tintColor = new THREE.Color();
    const cellSize = map.boxes.length > 20000 ? 28
      : map.boxes.length > 12000 ? 24
        : map.boxes.length > 8000 ? 22
          : map.boxes.length > 5000 ? 24
            : map.boxes.length > 1500 ? 32
              : 48;
    // Raised 520 -> 1400 (and 180 -> 480) by the 2026-08-01 `graphics` building
    // pass: a dressed structure now costs ~15 more boxes than it did, and at the
    // old ceiling a city map ran out of budget after ~19 buildings and left the
    // rest bare. These are merged static boxes — 36 verts each, folded into the
    // per-cell merge — so the ceiling costs TRIANGLES and map-build time, never
    // draw calls and never per-frame work.
    const detailBudget = lowSpec() || map.boxes.length > 2500 ? 0 : map.boxes.length > 1500 ? 480 : 1400;
    let detailCount = 0;
    const dressQueue: { box: MapBox; building: boolean; stone?: boolean }[] = [];
    const groundQueue: MapBox[] = [];
    /** Flat slabs high enough to be ROOFS — see `elevatedSlab`. */
    const roofSlabQueue: MapBox[] = [];
    /** EVERY flat slab in the map, as a floor candidate for the contact pass. */
    const floorQueue: DressTarget[] = [];
    /** Every thin slab ≥ 3 m (roofs, upper floors): shellOutward's roof test. */
    const capQueue: DressTarget[] = [];
    /** Undressed low walls / ledges / pillars — see `dressMinor`. */
    const minorQueue: { box: MapBox; kind: MinorKind }[] = [];
    /** Thin shell walls that may get house windows (see `houseWindows`). */
    const winQueue: MapBox[] = [];
    const addDetailBox =(dw: number, dh: number, dd: number, x: number, y: number, z: number, color: string): void => {
      if (detailCount >= detailBudget) return;
      const dgeo = new THREE.BoxGeometry(dw, dh, dd).toNonIndexed();
      // Trim is CAST STONE, not the wall repainted. `multiplyScalar(0.58)` on
      // its own made every cornice a darker shade of exactly the wall behind
      // it, so the dressing dissolved into the facade at any distance — you
      // could only see it existed by looking for its shadow. Pulling it most of
      // the way to a warm stone grey is what makes a cornice read AS a cornice,
      // and it is the same relationship real trim has to real brick: lighter,
      // greyer, and the same on every building of the block.
      tintColor.set(color).lerp(TRIM_STONE, 0.62).multiplyScalar(0.94);
      const dpos = dgeo.getAttribute("position");
      const dshade = new Float32Array(dpos.count * 3);
      for (let vi = 0; vi < dpos.count; vi++) {
        const f = 0.82 + 0.24 * (dpos.getY(vi) / dh + 0.5);
        dshade[vi * 3] = Math.min(1, tintColor.r * f);
        dshade[vi * 3 + 1] = Math.min(1, tintColor.g * f);
        dshade[vi * 3 + 2] = Math.min(1, tintColor.b * f);
      }
      dgeo.setAttribute("color", new THREE.BufferAttribute(dshade, 3));
      // WORLD-ANCHORED (2026-10-23): two abutting walls' cornices/plinths
      // overlap where they meet, with coplanar faces. Box-local UVs gave the two
      // copies different texels there — a flicker along every such joint. Same
      // anchor + same tint ⇒ identical fragments, so the overlap is invisible.
      worldProjectUV(dgeo, 2.8, uvAnchor(x, 2.8), uvAnchor(y, 2.8), uvAnchor(z, 2.8));
      dgeo.translate(x, y, z);
      const dkey = `detail|${Math.floor(x / cellSize)}|${Math.floor(z / cellSize)}`;
      (detailGroups.get(dkey) ?? detailGroups.set(dkey, []).get(dkey)!).push(dgeo);
      detailCount++;
    };

    // BAKED CONTACT AMBIENT OCCLUSION (2026-08-26). The floor-contact SKIRT
    // (mapDressing.ts) darkens the horizontal floor where a wall lands on it,
    // but nothing darkened the surfaces themselves — an interior room corner,
    // the underside of an overhang, or the crevice where two boxes abut all
    // stayed as flat-lit as an open exterior wall, which is most of why the
    // buildings still read as stacked cubes. This bakes a Minecraft-style
    // smooth-lighting AO term into the SAME per-box vertex colours the tint and
    // vertical gradient already live in — so it costs ZERO extra draw calls and
    // renders identically on any GPU (it is not a screen-space pass).
    //   Only for CRAFTED maps (<=2500 boxes): dense Minecraft imports already
    // get strong per-voxel face shading (the `minecraft` branch below) and their
    // box count makes the bake expensive. Occluders are every SOLID box (floors
    // included, so a wall's foot finds the slab under it); RECEIVERS are only
    // non-flat structural boxes (a huge floor slab's four far-apart corners would
    // interpolate the whole plaza dark — the skirt owns floor grounding anyway).
    const AO_STRENGTH = 0.6;     // max darkening at a fully-enclosed corner
    const AO_REACH = 0.75;       // metres a neighbour must be within to occlude
    const AO_EPS = 0.06;         // push samples just clear of the receiver's face
    const bakeAO = map.boxes.length <= 2500;
    let aoDarkened = 0; // receiver vertices the contact-AO term darkened (logged)
    let aoGrid: Map<number, number[]> | null = null;
    let aoBx0: Float32Array | null = null, aoBx1: Float32Array | null = null;
    let aoBy0: Float32Array | null = null, aoBy1: Float32Array | null = null;
    let aoBz0: Float32Array | null = null, aoBz1: Float32Array | null = null;
    const aoIndex = new Map<MapBox, number>();
    const AO_CELL = 4; // world-units per grid cell (>= AO_REACH so one lookup suffices)
    if (bakeAO) {
      const occ: MapBox[] = [];
      for (const b of map.boxes) {
        if (b.tag === "nodraw" || b.tag === PRACTICE_MOVER_TAG) continue;
        const lc2 = b.color?.toLowerCase();
        if (lc2 && (WATER_COLORS.has(lc2) || LAVA_COLORS.has(lc2))) continue; // water/lava don't occlude
        aoIndex.set(b, occ.length);
        occ.push(b);
      }
      const n = occ.length;
      aoBx0 = new Float32Array(n); aoBx1 = new Float32Array(n);
      aoBy0 = new Float32Array(n); aoBy1 = new Float32Array(n);
      aoBz0 = new Float32Array(n); aoBz1 = new Float32Array(n);
      aoGrid = new Map();
      let regs = 0;
      for (let i = 0; i < n; i++) {
        const b = occ[i];
        const x0 = b.p[0] - b.s[0] / 2, x1 = b.p[0] + b.s[0] / 2;
        const y0 = b.p[1] - b.s[1] / 2, y1 = b.p[1] + b.s[1] / 2;
        const z0 = b.p[2] - b.s[2] / 2, z1 = b.p[2] + b.s[2] / 2;
        aoBx0[i] = x0; aoBx1[i] = x1; aoBy0[i] = y0; aoBy1[i] = y1; aoBz0[i] = z0; aoBz1[i] = z1;
        const cx0 = Math.floor(x0 / AO_CELL), cx1 = Math.floor(x1 / AO_CELL);
        const cz0 = Math.floor(z0 / AO_CELL), cz1 = Math.floor(z1 / AO_CELL);
        // register into every XZ cell the footprint overlaps, so a sample only
        // ever tests its own cell. A pathological huge-footprint map (regs blown)
        // disables the bake rather than eat the memory.
        if (regs + (cx1 - cx0 + 1) * (cz1 - cz0 + 1) > 400000) { aoGrid = null; break; }
        for (let cx = cx0; cx <= cx1; cx++) {
          for (let cz = cz0; cz <= cz1; cz++) {
            const key = cx * 46341 + cz;
            (aoGrid.get(key) ?? aoGrid.set(key, []).get(key)!).push(i);
            regs++;
          }
        }
      }
    }
    /** Is world point (x,y,z) inside any SOLID box other than `own`? */
    const aoSolid = (x: number, y: number, z: number, own: number): boolean => {
      if (!aoGrid) return false;
      const cell = aoGrid.get(Math.floor(x / AO_CELL) * 46341 + Math.floor(z / AO_CELL));
      if (!cell) return false;
      for (let k = 0; k < cell.length; k++) {
        const i = cell[k];
        if (i === own) continue;
        if (x >= aoBx0![i] && x <= aoBx1![i] && y >= aoBy0![i] && y <= aoBy1![i]
          && z >= aoBz0![i] && z <= aoBz1![i]) return true;
      }
      return false;
    };

    /** Galleon boxes for shipDressing.ts (round spars, canvas, rigging …). */
    const shipBoxes: MapBox[] = [];
    // REAL PROP MODELS (mapPropSwaps.ts): these boxes still collide and still
    // draw, but into per-kind fallback meshes precompile() swaps for models
    const propDivert = planPropSwaps(map, realModelsEnabled());
    for (const box of map.boxes) {
      // collision-only boxes (e.g. the boombox/turntable hitboxes) — their
      // visuals are drawn by decor.ts; don't render a plain box on top.
      if (box.tag === "nodraw" || box.tag === PRACTICE_MOVER_TAG) continue;
      // SHIP parts: spars/sails/barrels are REDRAWN by shipDressing (the box
      // stays a collider); hull + stern draw normally and get dressed on top.
      if (isShipBox(box)) {
        shipBoxes.push(box);
        if (shipReplaces(box)) continue;
      }
      const [w, h, d] = box.s;
      const maxDim = Math.max(w, h, d);
      const minDim = Math.min(w, h, d);
      const minecraft = box.tag === "minecraft" || box.tag === "mcglass";
      // water / lava → animated surface mesh (flat colour baked in; no AO).
      // A deep swim-VOLUME (tag "water") must render ONLY its TOP surface as a
      // horizontal plane — drawing the whole tall box wrapped the camera in
      // teal walls underwater ("weird geometrical layer all over the camera").
      const lc = box.color?.toLowerCase();
      if (lc && (WATER_COLORS.has(lc) || LAVA_COLORS.has(lc))) {
        const isVolume = box.tag === "water" && h > 2.5; // deep swimmable sea
        const wgeo = isVolume
          ? new THREE.PlaneGeometry(w, d).toNonIndexed().rotateX(-Math.PI / 2)
          : new THREE.BoxGeometry(w, h, d).toNonIndexed();
        const wc = new THREE.Color(box.color);
        const wpos = wgeo.getAttribute("position");
        const wcol = new Float32Array(wpos.count * 3);
        for (let vi = 0; vi < wpos.count; vi++) { wcol[vi * 3] = wc.r; wcol[vi * 3 + 1] = wc.g; wcol[vi * 3 + 2] = wc.b; }
        wgeo.setAttribute("color", new THREE.BufferAttribute(wcol, 3));
        wgeo.translate(box.p[0], isVolume ? box.p[1] + h / 2 : box.p[1], box.p[2]); // plane sits at the SURFACE
        (LAVA_COLORS.has(lc) ? lavaGeos : waterGeos).push(wgeo);
        continue;
      }
      // CHAMFERED edges: rounded boxes catch light along every edge and stop
      // reading as minecraft cubes. Purely visual — collision keeps the exact
      // AABB (the chamfer is inside it). Thin trim keeps sharp corners.
      // NB: toNonIndexed() everywhere — RoundedBox and Box disagree on
      // indexing, and mergeGeometries refuses mixed groups (null → crash).
      // 1 chamfer segment: visually identical bevel at half the vertices.
      // Minecraft imports get SHARP boxes, never the chamfer: bevelled edges on
      // every merged slab read as plastic panels ("deconstructed"), while the
      // source material is crisp voxels. Also half the vertices.
      const geo: THREE.BufferGeometry = (!minecraft && minDim >= 0.5
        ? new RoundedBoxGeometry(w, h, d, 1, Math.min(0.12, minDim * 0.2))
        : new THREE.BoxGeometry(w, h, d)).toNonIndexed();
      const structural = maxDim >= 4; // walls/floors/towers, not trim/rails/rivets
      const flatGround = h <= 1.2 && (w >= 8 || d >= 8);
      // the wild west is RUSTIC: no office-window facades (mesas rendered as
      // glass skyscrapers).
      // The galleons are timber too: their bow block and stern transom are tall
      // enough to classify as `building` and wore lit office-window facades.
      const rustic = map.id === "wild_west_train_pursuit" || map.id === "pirate_ship";
      // REAL STONE (owner 2026-11-05, answering "castle walls: keep the lit
      // office windows or make them real stone?" → "real stone"). The castle's
      // curtain walls, towers and gatehouse no longer classify as `building`:
      // no lit window-grid facade, no office mullion fins / belt courses /
      // shop-door surround / generic quoins / roof deck. Exactly the boxes that
      // WERE `building` (curtain walls, the four towers, the gate towers) join
      // the `ashlar` family instead — the coursed-block photo scan at castle
      // scale + grime streaks (familyMat) — so they replace the building cells
      // one for one: same mesh / draw-call count. The few openings are
      // castleLayout's cross-loop arrow slits.
      const castleStone = map.id === "medieval_castle";
      const ashlar = castleStone && !flatGround && h >= 5 && Math.min(w, d) >= 2.5;
      // Skyhaven/Practice Field towers are HOLLOW (thin tall walls) — count a tall wall with
      // a wide footprint as a building too, so its windows light up at night
      // (otherwise the towers were pitch-black once the day/night cycle dimmed).
      const building = !rustic && !castleStone && !flatGround && h >= 5 &&
        (Math.min(w, d) >= 2.5 || ((map.id === "neo_island" || map.id === "rooftop_spiderweb") && Math.max(w, d) >= 6));
      // glass is TAGGED by the importer (it knows the block name) — colour
      // classification alone sent every stained-glass pane to an opaque family
      const mcFamily = box.tag === "mcglass" ? "mc_glass" : minecraft ? minecraftFamily(box.color) : null;
      // MADE ground (streets, plazas, decks) vs NATURAL ground (grass, dirt,
      // sand). Until 2026-08-01 both wore the grass-and-pebbles `terrain`
      // texture, so downtown's streets and every arena deck were painted with
      // turf — the biggest surface on screen wearing the wrong material.
      // ...and MADE ground that is four metres in the air is not ground at all,
      // it is a ROOF (2026-08-01, the roofline pass). Almost no building in
      // this game is a solid extrusion — village, neo_island, neon_district,
      // the rooftop maps and the compound all lay a flat slab on a hollow shell
      // of thin walls — so before this the roofs of this game wore the STREET,
      // kerbs and lane paint included. See `elevatedSlab`.
      const roofTop = flatGround && !minecraft && roofFelt(map.id)
        && elevatedSlab(w, h, d, box.p[1] + h / 2);
      const paved = flatGround && !roofTop && !minecraft && !naturalGround(box.color);
      // Minecraft tiles are INTEGER metres so the texture pattern lands on the
      // 1m block grid: at the old 2.2–3.5m repeats the painted seams were
      // ~0.2m — five times smaller than the real blocks, which made every
      // merged slab read as random panelling instead of stacked blocks.
      // makeStone draws 8 masonry courses per repeat → tile 8 = 1m courses;
      // brick/wood show ~4 courses per metre at tile 2 (Minecraft's own scale).
      // 8m repeats everywhere (glass 1): every family's texture now carries an
      // 8×8 grid of per-1m-cell variation, so neighbouring blocks differ the
      // way real Minecraft blocks do instead of repeating identically
      // SHIP DECKS (2026-11): a galleon's decks are flat slabs, so they were
      // classified as natural GROUND and wore the dirt-and-pebbles scan — a
      // sandy yard with a mast in it. Above the sea they are planking: route
      // them to the timber wall family (`big` → `wood` on this map) at the
      // wall plank scale. The seabed (y ≈ -9) keeps its sand.
      const shipDeck = flatGround && map.id === "pirate_ship" && box.p[1] > -2;
      const tile = minecraft ? (mcFamily === "mc_glass" ? 1 : 8)
        : shipDeck ? 3.2
        // ashlar: the photo repeats 2x per UV unit, so 6.4 m = one 3.2 m scan
        // tile → ~0.3 m courses of 0.6-0.9 m blocks (castle masonry, not brick)
        : ashlar ? 6.4
        : roofTop ? 3.4 : paved ? 5.5 : flatGround ? 6 : building ? 4.5 : 3.2; // world-units per repeat
      // WORLD-ANCHORED UVs (2026-10-23) for every non-facade crafted family:
      // a floor tiled from several slabs, or a wall run built from several
      // boxes, now carries ONE continuous pattern across its joints instead of
      // restarting at each box centre (a hard seam every few metres), and two
      // identical boxes no longer wear the identical patch. The FACADE keeps
      // box-local UVs on purpose — its window grid is laid out relative to the
      // box — and Minecraft imports keep theirs (tuned to the block grid).
      if (!minecraft && !building) {
        worldProjectUV(geo, tile, uvAnchor(box.p[0], tile), uvAnchor(box.p[1], tile), uvAnchor(box.p[2], tile));
      } else {
        worldProjectUV(geo, tile);
      }
      // bake tint × AO gradient (darker box bottoms) into vertex colors — the
      // neon-dimension towers keep their own neon colours (never forced white)
      tintColor.set(box.color ?? "#20263a");
      // ashlar: pull the near-black castle greys toward weathered limestone so
      // the block coursing reads (a #57554f tint crushed the scan to a slab)
      if (ashlar) tintColor.lerp(ASHLAR_LIMESTONE, 0.45);
      const pos = geo.getAttribute("position");
      const normal = geo.getAttribute("normal");
      const shade = new Float32Array(pos.count * 3);
      const ao = !flatGround && h > 0.8;
      // contact-AO receiver: crafted-map structural boxes only (see the grid
      // build above). Floors are excluded so a big slab never interpolates dark.
      const recvAO = bakeAO && aoGrid !== null && !minecraft && !flatGround;
      const ownIdx = recvAO ? (aoIndex.get(box) ?? -1) : -1;
      for (let vi = 0; vi < pos.count; vi++) {
        let f = minecraft
          ? normal.getY(vi) > 0.6 ? 1.18 : normal.getY(vi) < -0.6 ? 0.55 : 0.82
          : ao ? 0.72 + 0.34 * (pos.getY(vi) / h + 0.5) : 1;
        if (recvAO) {
          // Minecraft-style smooth lighting: from this face corner, probe the
          // two edge neighbours + the diagonal just outside the face. The more
          // of them are solid, the deeper into a crevice this vertex sits.
          const nx = normal.getX(vi), ny = normal.getY(vi), nz = normal.getZ(vi);
          const ax = Math.abs(nx), ay = Math.abs(ny), az = Math.abs(nz);
          const px = pos.getX(vi), py = pos.getY(vi), pz = pos.getZ(vi);
          const wx = px + box.p[0], wy = py + box.p[1], wz = pz + box.p[2];
          // u,v = the two in-plane axis dirs pointing toward THIS vertex's edges
          let ux = 0, uy = 0, uz = 0, vx = 0, vy = 0, vz = 0;
          if (ax >= ay && ax >= az) { uy = Math.sign(py) || 1; vz = Math.sign(pz) || 1; }
          else if (ay >= az) { ux = Math.sign(px) || 1; vz = Math.sign(pz) || 1; }
          else { ux = Math.sign(px) || 1; vy = Math.sign(py) || 1; }
          const ox = nx * AO_EPS, oy = ny * AO_EPS, oz = nz * AO_EPS;
          const s1 = aoSolid(wx + ux * AO_REACH + ox, wy + uy * AO_REACH + oy, wz + uz * AO_REACH + oz, ownIdx);
          const s2 = aoSolid(wx + vx * AO_REACH + ox, wy + vy * AO_REACH + oy, wz + vz * AO_REACH + oz, ownIdx);
          const sc = (s1 && s2) ? true
            : aoSolid(wx + (ux + vx) * AO_REACH + ox, wy + (uy + vy) * AO_REACH + oy, wz + (uz + vz) * AO_REACH + oz, ownIdx);
          const occN = (s1 && s2) ? 3 : ((s1 ? 1 : 0) + (s2 ? 1 : 0) + (sc ? 1 : 0));
          if (occN > 0) { f *= 1 - AO_STRENGTH * occN / 3; aoDarkened++; }
        }
        shade[vi * 3] = Math.min(1, tintColor.r * f);
        shade[vi * 3 + 1] = Math.min(1, tintColor.g * f);
        shade[vi * 3 + 2] = Math.min(1, tintColor.b * f);
      }
      geo.setAttribute("color", new THREE.BufferAttribute(shade, 3));
      geo.translate(box.p[0], box.p[1], box.p[2]);
      // family + SPATIAL CELL: merging the whole map into one mesh killed
      // frustum culling (huge maps rendered entirely every frame — Skyhaven).
      // Very dense custom/Minecraft imports use smaller cells so the player
      // does not render the full imported world at once.
      const family: MapMaterialFamily = mcFamily ?? (shipDeck ? "big" : ashlar ? "ashlar" : roofTop ? "roof" : paved ? "paved" : flatGround ? "ground" : building ? "building" : structural ? "big" : "small");
      // NATURAL day ground splits by tint class so each class can wear its own
      // photo scan (pbrTextures grass / sand / dirt); `~` keeps the base family
      // readable to familyMat. Procedural look is identical for all three.
      const famKey = family === "ground" && theme === "day" ? `ground~${groundClass(box.color)}` : family;
      const key = `${famKey}|${Math.floor(box.p[0] / cellSize)}|${Math.floor(box.p[2] / cellSize)}`;
      const swapKind = propDivert.get(box);
      if (swapKind) {
        // own mesh, no dressing/outline: the whole box is about to become a model
        const skey = `${key}|swap:${swapKind}`;
        (meshGroups.get(skey) ?? meshGroups.set(skey, []).get(skey)!).push(geo);
        continue;
      }
      (meshGroups.get(key) ?? meshGroups.set(key, []).get(key)!).push(geo);

      // Architectural dressing is DEFERRED to a second pass (below) so the
      // budget goes to the biggest, most visible structures first. Dressing
      // inline spent it in map-declaration order, which on a dense map meant an
      // arbitrary handful of buildings got cornices and the landmarks did not.
      if (!minecraft && !flatGround && (building || structural) && dressable(w, h, d)) {
        // `facadeLike` (2026-08-01): the MATERIAL gate `building` needs an
        // occupiable thickness because it also lights the windows; the DRESSING
        // gate does not. Thin-slab towers — neon_district's whole skyline —
        // gained literally zero boxes from the building pass because of it.
        dressQueue.push({ box, building: building || (!rustic && !castleStone && facadeLike(w, h, d)), stone: ashlar });
      }
      if (!minecraft && flatGround && groundDressable(w, h, d)) groundQueue.push(box);
      if (!minecraft && !flatGround && !building && !box.glow && !box.tag && windowable(w, h, d)) winQueue.push(box);
      // MINOR MASONRY candidates (coping / footing / capital) — undressed cover,
      // low walls and pillars. Glowing accents and tagged boxes keep their look.
      if (!minecraft && !flatGround && !box.glow && !box.tag) {
        const mk = minorKind(w, h, d);
        if (mk) minorQueue.push({ box, kind: mk });
      }
      // EVERY flat slab, not just the kerb-worthy ones: this is the list the
      // contact pass asks "is this box standing on anything?" against, and a
      // building parked on a 9 x 9 m pad must still get its skirt.
      if (!minecraft && flatGround) floorQueue.push({ p: box.p, s: box.s });
      if (!minecraft && h <= 1.2 && maxDim >= 3) capQueue.push({ p: box.p, s: box.s });
      // an elevated slab keeps its KERB (which reads as a parapet up there) but
      // must never get lane paint, and it is a candidate for roof plant
      if (!minecraft && flatGround && elevatedSlab(w, h, d, box.p[1] + h / 2)) roofSlabQueue.push(box);

      // edge outlines (sharp-box silhouettes; EdgesGeometry finds nothing on
      // chamfers): on dense maps only for glowing accents
      if (!dense || box.glow) {
        const eg = new THREE.EdgesGeometry(new THREE.BoxGeometry(w, h, d));
        eg.translate(box.p[0], box.p[1], box.p[2]);
        const ekey = theme === "day" ? "#2a2a24|0.2" : `${box.glow ?? "#3a4a6a"}|${box.glow ? 0.6 : 0.28}`;
        (edgeGroups.get(ekey) ?? edgeGroups.set(ekey, []).get(ekey)!).push(eg);
      }
    }
    mmark("boxes+ao");
    if (bakeAO) console.log(`[map] contact AO: ${aoGrid ? `${aoDarkened} verts darkened` : "skipped (footprint too large)"}`);

    // ARCHITECTURAL DRESSING — see mapDressing.ts for what each moulding is
    // and for the collision contract they all obey. Deferred to here (rather
    // than done inline in the box loop) so the budget can be spent BIGGEST
    // FIRST; and emitted all-or-nothing per structure, because a building with
    // a cornice and no plinth looks worse than an undressed one.
    dressQueue.sort((a, b) => dressPriority(a.box, b.box));
    const dressOut: DressBox[] = [];
    /** Structures that actually got their mouldings — the roof pass below
     *  follows exactly this list, because the deck tucks inside the PARAPET and
     *  a deck on an undressed box would be a slab floating in mid-air. */
    const roofQueue: MapBox[] = [];
    /** Structures that got the ENTRANCE surround — the signage pass hangs its
     *  awning and shop sign off exactly that door, so it follows this list. */
    const signQueue: MapBox[] = [];
    for (const { box, building, stone } of dressQueue) {
      // castle ashlar takes NO generic mouldings: cast-concrete quoins, plinth
      // and cornice read as modern pilasters on a curtain wall. Its relief is
      // castleLayout's corbels / loops / coping; it stays queued only for the
      // contact skirt + rubble below.
      if (stone) continue;
      if (detailCount + DRESS_MAX_BOXES > detailBudget) break;
      dressOut.length = 0;
      dressStructure(box, building, dressOut, woodMap);
      const col = box.color ?? "#20263a";
      for (const m of dressOut) addDetailBox(m.w, m.h, m.d, m.x, m.y, m.z, col);
      if (building && roofDressable(box.s[0], box.s[1], box.s[2])) roofQueue.push(box);
      // NOTE the gate is `signable`, NOT the `building` dressing flag the two
      // lines above use. A shopfront hangs on the FACE of a wall and does not
      // care how thick that wall is; requiring an occupiable volume would have
      // skipped the compound, the village and Skyhaven entirely — every one of
      // them builds its houses out of 0.3-0.4 m slabs — which is exactly the
      // trap `facadeLike` was written to get the building pass out of.
      if (signable(box.s[0], box.s[1], box.s[2])) signQueue.push(box);
    }

    // GROUND DRESSING — kerbs along the open edges of every plaza/street slab.
    // Runs AFTER the buildings because a landmark tower is worth more of the
    // budget than a kerb is, and only on sides not butted against another slab
    // (see `exposedGroundEdges`: kerbing every side would draw a raised grid
    // across the middle of a tiled floor). Biggest slab first, same rule.
    const groundTargets: DressTarget[] = groundQueue.map((b) => ({ p: b.p, s: b.s }));
    const groundOrder = groundQueue.map((b, i) => i).sort((a, b) =>
      dressPriority(groundTargets[a], groundTargets[b]));
    for (const i of groundOrder) {
      if (detailCount + GROUND_MAX_BOXES > detailBudget) break;
      dressOut.length = 0;
      dressGround(groundTargets[i], exposedGroundEdges(groundTargets[i], groundTargets), dressOut);
      const col = groundQueue[i].color ?? "#20263a";
      for (const m of dressOut) addDetailBox(m.w, m.h, m.d, m.x, m.y, m.z, col);
    }

    // ------------------------------------------------------------------
    // THE FLOOR CONTACT (2026-08-05, `graphics`): a baked ambient contact skirt
    // at the base of everything that stands on a floor, plus hashed dirt blots
    // over the open floor. See mapDressing.ts for why this exists at all — the
    // short version is that the cost ladder turns the shadow map OFF on any
    // machine that dips, and without it nothing in this game touches the
    // ground. ONE merged multiply-blended mesh for the whole map.
    // ------------------------------------------------------------------
    const contactGeos: THREE.BufferGeometry[] = [];
    if (detailBudget > 0) {
      const CONTACT_BUDGET = 900;         // quads for the whole map
      const patches: ContactPatch[] = [];
      // biggest structures first, same argument as the dressing budget
      for (const { box } of dressQueue) {
        if (contactGeos.length + SKIRT_MAX_PIECES > CONTACT_BUDGET) break;
        if (!contactCaster(box.s[0], box.s[1], box.s[2])) continue;
        const floorTop = floorUnder({ p: box.p, s: box.s }, floorQueue);
        if (floorTop === null) continue;  // catwalk / stacked box / floating platform
        patches.length = 0;
        contactSkirt({ p: box.p, s: box.s }, floorTop, patches);
        for (const p of patches) contactGeos.push(contactQuad(p));
        // RUBBLE — the relief half; merged into the trim detail mesh, so free.
        if (detailCount + DEBRIS_MAX_BOXES <= detailBudget) {
          dressOut.length = 0;
          baseDebris({ p: box.p, s: box.s }, floorTop, dressOut);
          for (const m of dressOut) addDetailBox(m.w, m.h, m.d, m.x, m.y, m.z, box.color ?? "#20263a");
        }
      }
      for (const i of groundOrder) {
        if (contactGeos.length + BLOT_MAX_PIECES > CONTACT_BUDGET) break;
        patches.length = 0;
        groundBlots(groundTargets[i], patches);
        for (const p of patches) contactGeos.push(contactQuad(p));
      }
    }

    // ------------------------------------------------------------------
    // STREET SURFACE (2026-08-01, `graphics`): lane paint + ironwork on every
    // MADE ground slab, on every map, generically — see mapDressing.ts for the
    // rules and why they are pure. Two extra merged meshes for the whole map
    // (one alpha-tested paint, one metal), so this costs triangles and build
    // time and exactly +2 draw calls, never per-frame work.
    // ------------------------------------------------------------------
    const markGeos: THREE.BufferGeometry[] = [];
    const ironGeos: THREE.BufferGeometry[] = [];
    if (detailBudget > 0 && paintedStreets(map.id)) {
      const PAINT_TILE = 2.4;     // world metres per texture repeat along a stripe
      const MARK_BUDGET = 260;     // quads for the whole map
      const marks: StreetMark[] = [];
      const fixtures: StreetFixture[] = [];
      const paintCol = new THREE.Color();
      for (const i of groundOrder) {
        if (markGeos.length + STREET_MAX_MARKS > MARK_BUDGET) break;
        if (naturalGround(groundQueue[i].color)) continue; // grass has no lanes
        const gb = groundQueue[i];
        // ...and neither does a ROOF. Before the roofline pass, a rooftops_xl
        // roof slab was indistinguishable from a street to this code and got a
        // dashed centre line and a manhole cover 20 m in the air.
        if (elevatedSlab(gb.s[0], gb.s[1], gb.s[2], gb.p[1] + gb.s[1] / 2)) continue;
        const exposed = exposedGroundEdges(groundTargets[i], groundTargets);
        marks.length = 0; fixtures.length = 0;
        streetMarkings(groundTargets[i], exposed, marks);
        streetFixtures(groundTargets[i], exposed, fixtures);
        for (const m of marks) {
          // PlaneGeometry lies in XY; rotateX(-90°) puts its width on +X and
          // its height on -Z, facing up.
          const q = new THREE.PlaneGeometry(m.w, m.d).toNonIndexed().rotateX(-Math.PI / 2);
          const uv = q.attributes.uv;
          // U must run ALONG the stripe (it is the tiling axis; V is clamped and
          // carries the feathered edge). For a stripe lying on Z that means
          // swapping the two channels, not just rescaling one.
          const len = m.alongX ? m.w : m.d;
          const rep = Math.max(1, Math.round(len / PAINT_TILE));
          for (let vi = 0; vi < uv.count; vi++) {
            const u = uv.getX(vi), v = uv.getY(vi);
            if (m.alongX) uv.setXY(vi, u * rep, v);
            else uv.setXY(vi, v * rep, u);
          }
          uv.needsUpdate = true;
          paintCol.set(m.warm ? "#f7dc86" : "#ffffff");
          const qp = q.attributes.position;
          const qc = new Float32Array(qp.count * 3);
          for (let vi = 0; vi < qp.count; vi++) {
            qc[vi * 3] = paintCol.r; qc[vi * 3 + 1] = paintCol.g; qc[vi * 3 + 2] = paintCol.b;
          }
          q.setAttribute("color", new THREE.BufferAttribute(qc, 3));
          q.translate(m.x, m.y, m.z);
          markGeos.push(q);
        }
        for (const f of fixtures) {
          if (f.kind === "manhole") {
            // cast frame + cover + centre boss: three concentric discs, which is
            // what a manhole is, and enough silhouette to catch the sun's rim.
            const frame = new THREE.CylinderGeometry(0.56, 0.58, 0.05, 16).toNonIndexed().translate(0, 0.02, 0);
            const cover = new THREE.CylinderGeometry(0.44, 0.47, 0.08, 16).toNonIndexed().translate(0, 0.04, 0);
            const boss = new THREE.CylinderGeometry(0.13, 0.15, 0.03, 10).toNonIndexed().translate(0, 0.09, 0);
            for (const p of [frame, cover, boss]) { p.translate(f.x, f.y, f.z); ironGeos.push(p); }
          } else {
            // gutter grate: a solid frame with ribs standing proud of it. The
            // ribs are the whole point — a flat plate with a grating texture
            // reads as a sticker, six 12-triangle bars read as ironwork.
            const L = 1.34, W = 0.72;                     // along the kerb, across it
            const bw = f.alongX ? L : W, bd = f.alongX ? W : L;
            const frame = new THREE.BoxGeometry(bw, 0.07, bd).toNonIndexed().translate(0, 0.035, 0);
            frame.translate(f.x, f.y, f.z); ironGeos.push(frame);
            for (let r = 0; r < 6; r++) {
              const t = (r + 0.5) / 6 - 0.5;
              const rib = new THREE.BoxGeometry(
                f.alongX ? L * 0.11 : W * 0.9, 0.05, f.alongX ? W * 0.9 : L * 0.11,
              ).toNonIndexed();
              rib.translate(
                f.x + (f.alongX ? t * L : 0), f.y + 0.085, f.z + (f.alongX ? 0 : t * L),
              );
              ironGeos.push(rib);
            }
          }
        }
      }
    }

    // ------------------------------------------------------------------
    // STREET LEVEL & SIGNAGE (2026-08-05, `graphics`): an awning over every
    // dressed entrance, a lit fascia sign above it and a blade sign hanging out
    // over the pavement. See mapDressing.ts for the placement rules and the
    // (stricter) collision contract — these are the first dressing pieces that
    // reach a whole metre outside the collider, and they buy that with height.
    //
    // TWO extra merged meshes for the whole map: the lit `signage` faces and the
    // striped `cloth` awnings. The steel — blade bodies, brackets, fascia cases,
    // stay rods — goes into the street-IRONWORK mesh that already existed, so it
    // costs nothing at all. Built once at map load; never a frame of work.
    // ------------------------------------------------------------------
    const signFaceGeos: THREE.BufferGeometry[] = [];
    const awningGeos: THREE.BufferGeometry[] = [];
    // THE SHOPFRONT (2026-08-05, second graphics pass today): the ground storey
    // the awning has been pointing at. One extra merged mesh — the GLAZING,
    // which needs its own emissive atlas; the masonry (riser, cill, piers) goes
    // into the trim detail mesh and the metalwork into the ironwork mesh, both
    // of which already exist.
    const shopGlassGeos: THREE.BufferGeometry[] = [];
    /** walls whose ground storey got a shopfront — house windows start a floor up */
    const shopWalls = new Set<MapBox>();
    if (detailBudget > 0 && signQueue.length && paintedStreets(map.id)) {
      const SIGN_BUDGET = 900;         // pieces for the whole map (~43 shopfronts)
      const pieces: SignPiece[] = [];
      const sgnCol = new THREE.Color();
      /** flat vertex colour + world-projected UVs, then file it */
      const fileSign = (
        g: THREE.BufferGeometry, into: THREE.BufferGeometry[], tile: number, hex: string,
      ): void => {
        worldProjectUV(g, tile);
        sgnCol.set(hex);
        const p = g.getAttribute("position");
        const cbuf = new Float32Array(p.count * 3);
        for (let vi = 0; vi < p.count; vi++) {
          cbuf[vi * 3] = sgnCol.r; cbuf[vi * 3 + 1] = sgnCol.g; cbuf[vi * 3 + 2] = sgnCol.b;
        }
        g.setAttribute("color", new THREE.BufferAttribute(cbuf, 3));
        into.push(g);
      };
      let filed = 0;
      // which way is OUT for each wall. Computed against the WHOLE candidate
      // set, so a hollow shell's four walls all point away from their own room
      // instead of two of them lighting up the inside of it (`shellOutward`).
      const signTargets: DressTarget[] = signQueue.map((b) => ({ p: b.p, s: b.s }));
      const shopPieces: ShopPiece[] = [];
      shopWalls.clear();
      for (let qi = 0; qi < signQueue.length; qi++) {
        if (filed + SIGN_MAX_PIECES + SHOP_MAX_PIECES > SIGN_BUDGET) break;
        const box = signQueue[qi];
        pieces.length = 0;
        // ONE `shellOutward` for both passes: the awning and the glazing under
        // it must agree on which way is out or the shopfront ends up in the
        // room behind the wall (see that function).
        const so = shellOutward(signTargets[qi], signTargets, capQueue);
        facadeSigns(signTargets[qi], true, pieces, so);
        filed += pieces.length;
        shopPieces.length = 0;
        shopfront(signTargets[qi], shopPieces, so);
        filed += shopPieces.length;
        if (shopPieces.length) shopWalls.add(box);
        const shopCol = box.color ?? "#20263a";
        for (const sp of shopPieces) {
          if (sp.kind === "glass") {
            // THE PANE. A plane, like a lit sign face — it is a sheet in front
            // of a room, and the frame around it is what has volume.
            const alongX = sp.w === 0;
            const qw = alongX ? sp.d : sp.w;
            const q = new THREE.PlaneGeometry(qw, sp.h).toNonIndexed();
            const s = alongX ? sp.nx : sp.nz;
            if (alongX) q.rotateY(s * Math.PI / 2);
            else if (s < 0) q.rotateY(Math.PI);
            // ATLAS UVs. Unlike a sign face, u is NOT the whole cell: each pane
            // takes the slice of the room that lies behind it (`u0`..`u1`), so
            // one continuous interior runs across the mullions instead of the
            // same shelf repeating in every pane. A pane on the far side of the
            // door reads the far side of the room.
            const u0 = (sp.cell % 2) * 0.5, v0 = ((sp.cell / 2) | 0) * 0.5, M = 0.006;
            const span = 0.5 - M * 2;
            const uv = q.attributes.uv;
            // mirrored on a -X/-Z facing pane: the quad's own u runs the other
            // way round the world there, and without this the room behind two
            // opposite shopfronts would be each other's mirror image
            const flip = s < 0;
            for (let vi = 0; vi < uv.count; vi++) {
              const t = flip ? 1 - uv.getX(vi) : uv.getX(vi);
              uv.setXY(vi,
                u0 + M + span * (sp.u0 + (sp.u1 - sp.u0) * t),
                v0 + M + span * uv.getY(vi));
            }
            uv.needsUpdate = true;
            q.translate(sp.x, sp.y, sp.z);
            shopGlassGeos.push(q);
            continue;
          }
          if (sp.kind === "riser" || sp.kind === "cill" || sp.kind === "pier") {
            // MASONRY — straight into the trim detail mesh the mouldings use,
            // so the stall riser is the same cast stone as the plinth below it.
            addDetailBox(sp.w, sp.h, sp.d, sp.x, sp.y, sp.z, shopCol);
            continue;
          }
          // FRAMES, DOOR AND HANDLE — dark shopfront metalwork, filed into the
          // street-ironwork pile (no vertex colour: that mesh's other
          // contributors have none and mergeGeometries refuses a mixed set).
          ironGeos.push(new THREE.BoxGeometry(sp.w, sp.h, sp.d).toNonIndexed()
            .translate(sp.x, sp.y, sp.z));
        }
        // one awning colourway per BUILDING (like the roof's tone): a street
        // where every canopy stripes the same two colours reads as wallpaper
        const t = Math.abs(Math.sin(box.p[0] * 41.7 + box.p[2] * 17.3)) % 1;
        const warm = AWNING_COLS[(t * AWNING_COLS.length) | 0];
        for (const pc of pieces) {
          if (pc.kind === "face") {
            // THE LIT FACE. A plane, not a box: it is a sticker on the case in
            // front of it, and the case is what has volume. The zero extent
            // names the axis it faces — see mapDressing's `put`.
            const alongX = pc.w === 0;
            const qw = alongX ? pc.d : pc.w;
            const q = new THREE.PlaneGeometry(qw, pc.h).toNonIndexed();
            const s = alongX ? (pc.nx !== 0 ? pc.nx : pc.stripe) : (pc.nz !== 0 ? pc.nz : pc.stripe);
            if (alongX) q.rotateY(s * Math.PI / 2);
            else if (s < 0) q.rotateY(Math.PI);
            // ATLAS UVs: pin the quad inside one 0.5 x 0.5 cell, with a small
            // margin so the mip chain never bleeds the neighbouring sign in.
            const uv = q.attributes.uv;
            const u0 = (pc.cell % 2) * 0.5, v0 = ((pc.cell / 2) | 0) * 0.5, M = 0.006;
            // ASPECT. The cell is square and the quads are not — a fascia band
            // is 4:1 and a blade is 1:4 — so mapping the whole cell onto either
            // squashes the tube work into a smear (the first capture of this
            // pass photographed exactly that). Sample a centred SUB-RECT of the
            // cell instead, matched to the quad, so glyph strokes keep their
            // proportions. Clamped to half the cell: crop harder than that and
            // the border tube, which is what makes the panel read as a sign at
            // all, disappears off both ends.
            const asp = qw / pc.h;
            const ku = Math.min(1, Math.max(0.5, 1 / asp)), kv = Math.min(1, Math.max(0.5, asp));
            const su = (0.5 - M * 2) * ku, sv = (0.5 - M * 2) * kv;
            const ou = u0 + M + ((0.5 - M * 2) - su) / 2, ov = v0 + M + ((0.5 - M * 2) - sv) / 2;
            for (let vi = 0; vi < uv.count; vi++) {
              uv.setXY(vi, ou + uv.getX(vi) * su, ov + uv.getY(vi) * sv);
            }
            uv.needsUpdate = true;
            q.translate(pc.x, pc.y, pc.z);
            signFaceGeos.push(q);
            continue;
          }
          const g = new THREE.BoxGeometry(pc.w, pc.h, pc.d).toNonIndexed();
          // the sloped pieces: the canopy and its stay rods. `tilt` drops the
          // piece's OUTWARD end, so the sign of the rotation follows the face
          // normal — get this backwards and the awning points at the sky.
          if (pc.tilt) {
            if (pc.nx !== 0) g.rotateZ(-pc.nx * pc.tilt);
            else g.rotateX(pc.nz * pc.tilt);
          }
          g.translate(pc.x, pc.y, pc.z);
          if (pc.kind === "canopy" || pc.kind === "valance") {
            // alternating stripes — the one non-grey thing on any facade in
            // this game, and the reason an awning reads as fabric at 30 m
            fileSign(g, awningGeos, 0.5, pc.stripe % 2 === 0 ? warm : "#f2ece0");
          } else {
            // straight into the street-ironwork pile. NO vertex colour here:
            // that mesh's other contributors (manhole frames, gutter grates)
            // have no `color` attribute and mergeGeometries refuses a mixed
            // set — it would return null and the whole map's ironwork would
            // silently vanish. Its own loop world-projects the UVs.
            ironGeos.push(g);
          }
        }
      }
    }

    // ------------------------------------------------------------------
    // HOUSE WINDOWS (2026-11-03, realism): timber joinery + glass + shutters on
    // the outward face of every thin shell wall on day maps (a wall with a
    // shopfront starts a storey up). See mapDressing `houseWindows`. Two
    // merged meshes for the whole map (joinery on the wood scan, glass), the
    // stone cills go into the trim detail mesh. Built once at map load.
    // ------------------------------------------------------------------
    const joineryGeos: THREE.BufferGeometry[] = [];
    const winGlassGeos: THREE.BufferGeometry[] = [];
    if (detailBudget > 0 && theme === "day" && winQueue.length && houseWindowMap(map.id)) {
      const WIN_BUDGET = 2600;
      const targets: DressTarget[] = winQueue.map((b) => ({ p: b.p, s: b.s }));
      const wp: WindowPiece[] = [];
      const jc = new THREE.Color();
      const SHUTTER_COLS = ["#3f6b4a", "#3d5f7a", "#7a3b2e", "#6b5a3e", "#4c5a63"];
      let filed = 0;
      for (let qi = 0; qi < targets.length && filed < WIN_BUDGET; qi++) {
        wp.length = 0;
        houseWindows(targets[qi], shellOutward(targets[qi], targets, capQueue), true, wp, shopWalls.has(winQueue[qi]) ? 1 : 0);
        filed += wp.length;
        for (const pc of wp) {
          if (pc.kind === "glass") {
            const faceX = pc.w === 0;
            const q = new THREE.PlaneGeometry(faceX ? pc.d : pc.w, pc.h).toNonIndexed();
            if (faceX) q.rotateY(pc.nx * Math.PI / 2);
            else if (pc.nz < 0) q.rotateY(Math.PI);
            q.translate(pc.x, pc.y, pc.z);
            winGlassGeos.push(q);
            continue;
          }
          if (pc.kind === "cill") {
            addDetailBox(pc.w, pc.h, pc.d, pc.x, pc.y, pc.z, winQueue[qi].color ?? "#c9b79a");
            continue;
          }
          const g = new THREE.BoxGeometry(pc.w, pc.h, pc.d).toNonIndexed().translate(pc.x, pc.y, pc.z);
          worldProjectUV(g, 1.2);
          // frames: dark stained timber or off-white paint (per house);
          // shutters: one painted colourway per house
          if (pc.kind === "frame") jc.set(pc.tone < 0.55 ? "#4a3626" : "#e6e0d2");
          else jc.set(SHUTTER_COLS[(pc.tone * 7.31 * SHUTTER_COLS.length | 0) % SHUTTER_COLS.length]);
          const pos = g.getAttribute("position");
          const cb = new Float32Array(pos.count * 3);
          for (let vi = 0; vi < pos.count; vi++) { cb[vi * 3] = jc.r; cb[vi * 3 + 1] = jc.g; cb[vi * 3 + 2] = jc.b; }
          g.setAttribute("color", new THREE.BufferAttribute(cb, 3));
          joineryGeos.push(g);
        }
      }
      if (filed) console.log(`[map] house windows: ${winGlassGeos.length} panes, ${joineryGeos.length} joinery pieces`);
    }

    // ------------------------------------------------------------------
    // THE ROOFLINE (2026-08-01, `graphics`): a felt DECK over every dressed
    // building's roof — until now the top face wore the wall's office-window
    // `facade` tile, a grid of lit windows lying flat facing the sky — plus the
    // PLANT that stands on it (stair hut, water tank, condensers, vent stacks).
    // See mapDressing.ts for the placement rules and the collision contract.
    // Two extra merged meshes for the whole map, built once at map load.
    // ------------------------------------------------------------------
    const deckGeos: THREE.BufferGeometry[] = [];
    const plantGeos: THREE.BufferGeometry[] = [];
    // ROUND TWO (2026-08-05): the OPEN machinery — condenser bodies and their
    // fan cowls — splits off into its own merged mesh so it can wear `louvre`
    // instead of `galv`. One extra draw call for the whole map; the point is
    // that the roof stops being one uniform grey material.
    const grilleGeos: THREE.BufferGeometry[] = [];
    if (detailBudget > 0) {
      // Raised 260 → 1100 by ROUND TWO, which quadrupled the pieces per roof
      // (a perimeter railing is ~28 of them). Every piece is a 12-triangle box
      // or a 10-16 segment cylinder merged ONCE at map load into one of three
      // meshes — the budget buys triangles and load-time, never draw calls.
      const ROOF_BUDGET = 1100; // pieces for the whole map
      const furniture = roofFurniture(map.id);
      const pieces: RoofPiece[] = [];
      const roofCol = new THREE.Color();
      /** Paint one piece's geometry with a flat vertex colour and file it. */
      const filePiece = (
        g: THREE.BufferGeometry, into: THREE.BufferGeometry[], tile: number, hex: string, f: number,
      ): void => {
        worldProjectUV(g, tile);
        roofCol.set(hex).multiplyScalar(f);
        const p = g.getAttribute("position");
        const cbuf = new Float32Array(p.count * 3);
        for (let vi = 0; vi < p.count; vi++) {
          cbuf[vi * 3] = roofCol.r; cbuf[vi * 3 + 1] = roofCol.g; cbuf[vi * 3 + 2] = roofCol.b;
        }
        g.setAttribute("color", new THREE.BufferAttribute(cbuf, 3));
        into.push(g);
      };
      // SOLID buildings get a deck + plant; elevated SLABS (the hollow-shell
      // roofs, which is most of them) already wear the membrane as their own
      // material, so they only take the plant.
      for (const { box, surface } of [
        ...roofQueue.map((box) => ({ box, surface: "building" as const })),
        ...roofSlabQueue.map((box) => ({ box, surface: "slab" as const })),
      ]) {
        if (deckGeos.length + plantGeos.length + grilleGeos.length + ROOF_MAX_PIECES > ROOF_BUDGET) break;
        pieces.length = 0;
        // A slab needs to be a real roof (>= 90 m²) before it gets plant: the
        // elevated-slab test also catches jump platforms and gantries, and a
        // water tank on a 9 x 6 platform is clutter in a place people land.
        roofPieces({ p: box.p, s: box.s }, surface,
          furniture && (surface === "building" || box.s[0] * box.s[2] >= 90), pieces);
        // one tone per BUILDING, not per piece: a block of towers whose roofs
        // are all the identical grey reads as a texture atlas, and a roof that
        // changes shade halfway across reads as a bug.
        const t = Math.abs(Math.sin(box.p[0] * 12.9898 + box.p[2] * 78.233)) % 1;
        for (const pc of pieces) {
          if (pc.kind === "deck") {
            filePiece(new THREE.BoxGeometry(pc.w, pc.h, pc.d).toNonIndexed().translate(pc.x, pc.y, pc.z),
              deckGeos, 3.0, "#4b4a47", 0.86 + t * 0.28);
            continue;
          }
          // PLANT. Chamfered boxes for the housings (a sharp-edged box on a roof
          // is the exact silhouette this whole pass exists to kill); cylinders
          // for the tank and the stacks, each with the cap that makes it read as
          // a vessel rather than a post.
          const shade = 0.9 + t * 0.2;
          if (pc.kind === "tank") {
            const r = pc.w / 2;
            filePiece(new THREE.CylinderGeometry(r, r, pc.h, 14, 1).toNonIndexed().translate(pc.x, pc.y, pc.z),
              plantGeos, 1.1, "#98a0a8", shade);
            filePiece(new THREE.ConeGeometry(r * 1.06, r * 0.55, 14).toNonIndexed()
              .translate(pc.x, pc.y + pc.h / 2 + r * 0.26, pc.z),
            plantGeos, 1.1, "#98a0a8", shade * 1.06);
          } else if (pc.kind === "stack") {
            const r = pc.w / 2;
            filePiece(new THREE.CylinderGeometry(r, r, pc.h, 10, 1).toNonIndexed().translate(pc.x, pc.y, pc.z),
              plantGeos, 0.8, "#8e959c", shade);
            // the COWL: a wider short cap, which is what a vent has and a pipe
            // does not — the difference between machinery and scaffolding
            filePiece(new THREE.CylinderGeometry(r * 1.5, r * 1.2, 0.14, 10, 1).toNonIndexed()
              .translate(pc.x, pc.y + pc.h / 2 + 0.05, pc.z),
            plantGeos, 0.8, "#8e959c", shade * 0.92);
          } else if (pc.kind === "fan") {
            // ROUND TWO. The fan is a recessed FUNNEL with a rim and a hub. The
            // funnel is a closed, front-facing cone rather than an open-ended
            // tube on purpose: a tube would need `side: DoubleSide` to show its
            // far wall, and `doubleSided` is part of three's program cache key
            // (three.cjs:44323) — i.e. a whole extra shader variant, and a cold
            // compile, for one 16-segment ring. The cone's inner slope reads as
            // the same dark hole and costs nothing.
            const r = pc.w / 2;
            filePiece(new THREE.ConeGeometry(r, pc.h * 1.5, 16).toNonIndexed()
              .rotateX(Math.PI).translate(pc.x, pc.y - pc.h * 0.2, pc.z),
            grilleGeos, 0.55, "#31363c", shade * 0.62);
            filePiece(new THREE.TorusGeometry(r, pc.h * 0.34, 6, 18).toNonIndexed()
              .rotateX(Math.PI / 2).translate(pc.x, pc.y + pc.h / 2, pc.z),
            grilleGeos, 0.55, "#aab2ba", shade);
            filePiece(new THREE.CylinderGeometry(r * 0.26, r * 0.26, pc.h * 0.8, 10, 1).toNonIndexed()
              .translate(pc.x, pc.y + pc.h * 0.1, pc.z),
            grilleGeos, 0.55, "#7d858d", shade);
          } else if (pc.kind === "hatch") {
            // ROUND TWO. A lipped upstand kerb with the LID TILTED OPEN on it —
            // the open lid is the whole read: a closed flat plate is a floor
            // tile, a plate standing at 55° is a way into the building.
            filePiece(new RoundedBoxGeometry(pc.w, pc.h, pc.d, 1, 0.03).toNonIndexed()
              .translate(pc.x, pc.y, pc.z),
            plantGeos, 0.9, "#8b9299", shade);
            const lid = new RoundedBoxGeometry(pc.w * 0.98, 0.06, pc.d * 0.98, 1, 0.025).toNonIndexed();
            lid.translate(0, 0.03, pc.d * 0.49);           // hinge to the -Z lip
            lid.rotateX(-0.96);                            // ~55° open
            lid.translate(pc.x, pc.y + pc.h / 2, pc.z - pc.d * 0.49);
            filePiece(lid, plantGeos, 0.9, "#9aa2a9", shade * 1.05);
          } else if (pc.kind === "post" || pc.kind === "rail") {
            // ROUND TWO. Deliberately NOT rounded: at the 7-9 cm scale a
            // railing lives at, a RoundedBoxGeometry's 3 extra loops per axis
            // are 5x the triangles for a chamfer nothing can resolve, and there
            // are up to 28 of these per roof.
            filePiece(new THREE.BoxGeometry(pc.w, pc.h, pc.d).toNonIndexed()
              .translate(pc.x, pc.y, pc.z),
            plantGeos, 0.55, pc.kind === "post" ? "#7f868d" : "#9aa1a8",
            shade * (pc.kind === "post" ? 0.94 : 1.06));
          } else {
            const round = Math.min(0.06, Math.min(pc.w, pc.h, pc.d) * 0.22);
            // CONDENSER bodies go to the `louvre` mesh; the legs stay galv.
            filePiece(new RoundedBoxGeometry(pc.w, pc.h, pc.d, 1, round).toNonIndexed()
              .translate(pc.x, pc.y, pc.z),
            pc.kind === "plant" ? grilleGeos : plantGeos,
            // 1.05 m/repeat puts TWO louvre panels across a condenser's long
            // face and ONE up its height — which is how a real unit is built,
            // and it keeps the tile's frame border reading as the panel edge
            // instead of as a random seam across the middle of the flank.
            pc.kind === "plant" ? 1.05 : 1.1,
            pc.kind === "leg" ? "#6f767d" : "#a4abb2", shade);
          }
        }
      }
    }

    // MINOR MASONRY (2026-10-23, `graphics`): a coping on every undressed low
    // wall / ledge and a base + capital on every free-standing pillar — the
    // cover a player fights around was the last bare cuboid on most maps. Runs
    // LAST so it only spends trim budget the buildings, kerbs, rubble and
    // shopfront masonry left (every earlier pass is byte-identical), biggest
    // first, all-or-nothing per box. Merged into the per-cell trim mesh: no new
    // draw call or material, and `detailBudget` is 0 on low-spec, so weak
    // machines see no change.
    if (detailBudget > 0 && minorQueue.length) {
      minorQueue.sort((a, b) => dressPriority(a.box, b.box));
      for (const { box, kind } of minorQueue) {
        if (detailCount + MINOR_MAX_BOXES > detailBudget) break;
        dressOut.length = 0;
        const t = { p: box.p, s: box.s };
        dressMinor(t, kind, floorUnder(t, floorQueue) !== null, dressOut);
        const col = box.color ?? "#20263a";
        for (const m of dressOut) addDetailBox(m.w, m.h, m.d, m.x, m.y, m.z, col);
      }
    }

    // ONE material per family, shared across all its spatial cells — cells
    // add draw calls (cullable) but never shader programs.
    // Weathering shader gate: same machines that get the full trim/contact
    // pass (never lowSpec / TV), minus integrated GPUs — it is ~20 hashes per
    // map fragment, which a discrete GPU never notices and an iGPU might.
    mmark("dressing");
    const weatherOn = !lowSpec() && !isTvApp() && !weakGpu();
    const familyMat = (familyKey: string): THREE.MeshStandardMaterial => {
      const [family, groundSub] = familyKey.split("~");
      const building = family === "building";
      const ashlar = family === "ashlar";
      const paved = family === "paved";
      const roofTop = family === "roof";
      const flatGround = family === "ground" || paved || roofTop;
      const minecraft = family.startsWith("mc_");
      const mcStone = family === "mc_stone";
      const mcBrick = family === "mc_brick";
      const mcWood = family === "mc_wood";
      const mcFoliage = family === "mc_foliage";
      const mcEarth = family === "mc_earth";
      const mcGlass = family === "mc_glass";
      const mcMetal = family === "mc_metal";
      // GENERIC WALLS (`big`/`small`) used to fall through to `surface` — brushed
      // metal with domed rivets — which is the one metal in the palette and was
      // the wrong material on every non-industrial map: a sunlit VILLAGE house
      // read as a riveted steel warehouse. They now wear the painted `plaster`
      // render by default; only the explicitly INDUSTRIAL maps keep the metal
      // (a compound bay / reactor hall genuinely IS sheet-steel-panelled).
      const genericWall = !building && !flatGround && !minecraft;
      const metalWall = genericWall && (map.id === "compound" || map.id === "reactor");
      // TIMBER MAPS — a ship's hull and a frontier town are built from BOARDS, not
      // painted stucco. Their generic walls (and their dressing, below) wear the
      // sawn-plank `wood` family instead of `plaster`. Everything else about the
      // routing is unchanged, so the material is the only difference these maps see.
      const woodWall = genericWall && !metalWall && woodMap;
      const plasterWall = genericWall && !metalWall && !woodMap;
      const surf: SurfaceFamily = mcStone ? "mcStone"
        : mcBrick ? "mcBrick"
          : mcWood ? "mcWood"
            : mcFoliage ? "mcFoliage"
              : mcEarth ? "mcEarth"
                : mcGlass ? "mcGlass"
                  : mcMetal ? "surface"
                    : minecraft ? "mcStone"
                      : roofTop ? "roofing"
                        : paved ? "paving"
                        : flatGround ? (theme === "day" ? "terrain" : "concrete")
                          : building ? "facade"
                            : woodWall ? "wood"
                              : plasterWall ? "plaster"
                                : "surface";
      const params: THREE.MeshStandardMaterialParameters = {
        color: "#ffffff", // per-box tint lives in vertex colors
        vertexColors: true,
        // `roughness`/`metalness` are now the CEILING the per-family roughness
        // map modulates (three multiplies map × value), so they sit high; the
        // texture supplies the variation that used to be missing entirely.
        roughness: mcGlass ? 0.14 : mcMetal ? 0.5 : minecraft ? 0.95 : flatGround ? 1 : building ? 0.9 : woodWall ? 0.92 : plasterWall ? 1 : 0.95,
        metalness: mcGlass ? 0.03 : mcMetal ? 0.55 : minecraft ? 0.04 : flatGround ? 0.03 : building ? 0.12 : woodWall ? 0.0 : plasterWall ? 0.02 : theme === "day" ? 0.1 : 0.28,
        transparent: mcGlass,
        opacity: mcGlass ? 0.62 : 1,
        depthWrite: !mcGlass,
        emissive: building ? "#ffffff" : "#000000",
        emissiveIntensity: building ? (theme === "day" ? 0.12 : 0.85) : 0,
        // REFLECTIONS (2026-10-21 `graphics`): the metallic/glazed world families
        // lean harder on the scene env probe so glass blocks and industrial sheet
        // steel read as real reflective surfaces, not flat tints — the same free,
        // probe-already-uploaded boost the vehicles/props got (vehicleMaps). Every
        // other family stays at the default 1.0 (byte-identical to before), and
        // matte families (plaster/wood/ground) are high-roughness so the probe
        // barely shows regardless. No-op on low-spec (env map skipped there).
        envMapIntensity: mcGlass ? 2.0 : mcMetal ? 1.6 : metalWall ? 1.5 : 1.0,
      };
      // per-family normal strength: masonry and panelling want the relief,
      // ground and foliage want a whisper of it (they're seen at grazing
      // angles over a huge area, where a strong normal map turns to noise)
      // paving keeps a stronger normal than natural ground: its detail is the
      // JOINT GRID, which is a straight line the eye tracks into the distance
      // rather than the high-frequency speckle that turns to noise at range.
      surfaceMaps(surf, params, minecraft ? 0.7 : paved ? 0.85 : flatGround ? 0.55 : building ? 0.9 : woodWall ? 0.85 : plasterWall ? 0.7 : 1.0);
      if (building) params.emissiveMap = tex.facadeGlow;
      const fmat = new THREE.MeshStandardMaterial(params);
      // Minecraft imports keep their crisp voxel canvases — never photo-swapped
      if (minecraft) fmat.userData.npNoPbr = true;
      // PHOTO routing (pbrTextures.ts): natural ground by tint class, and generic
      // walls by theme — stone on the castle, sandstone block on day maps, cast
      // concrete panels on neon maps. Unset = the procedural family's own scan.
      else if (groundSub) fmat.userData.npPbrFamily = groundSub;
      else if (paved && theme !== "day") fmat.userData.npPbrFamily = "pavingNeon";
      // castle ASHLAR reuses the coursed sandstone-block scan (the day-wall
      // `plaster` set — already loaded, no new texture memory) at 2x scale via
      // its 6.4 m UV tile; the rubble `castle` scan stays on the rest.
      else if (ashlar) fmat.userData.npPbrFamily = "plaster";
      else if (plasterWall) fmat.userData.npPbrFamily = map.id === "medieval_castle" ? "castle" : theme === "day" ? "plaster" : "plasterNeon";
      // WORLD-SPACE WEATHERING (2026-10-23, mapWeather.ts): macro tonal
      // variation against tiling, rain streaks on walls, damp patches on made
      // ground, per-window lit variation on facades. Crafted families only;
      // Minecraft imports keep their voxel look.
      const wk = weatherOn && !minecraft ? (ashlar ? "masonry" : weatherKindFor(surf)) : null;
      if (wk) applyMapWeather(fmat, WEATHER_PRESETS[wk]);
      return fmat;
    };
    const familyMats = new Map<string, THREE.MeshStandardMaterial>();
    this.dayBuildingMat = null;
    this.signGlowMat = null; // the previous map's material is about to be disposed
    this.shopGlowMat = null;
    for (const [key, geos] of meshGroups) {
      const merged = mergeGeometries(geos);
      if (!merged) { console.error(`[map] merge failed for group ${key} — skipped`); continue; }
      const family = key.split("|")[0];
      const mat = familyMats.get(family) ?? familyMats.set(family, familyMat(family)).get(family)!;
      if (family === "building" && theme === "day") this.dayBuildingMat = mat; // windows glow at night
      const mesh = new THREE.Mesh(merged, mat);
      const swapAt = key.indexOf("|swap:");
      if (swapAt >= 0) mesh.userData.propSwap = key.slice(swapAt + 6);
      mesh.castShadow = family === "big" || family === "building" || family === "ashlar";
      mesh.receiveShadow = family !== "small";
      group.add(mesh);
      for (const g of geos) g.dispose(); // merged copy owns the data now
    }
    const detailMat = detailGroups.size > 0
      // `trim` (2026-08-01): every cornice, plinth, pilaster, belt course,
      // mullion fin, parapet and door surround in the game used to wear
      // `surface` — brushed metal with domed rivets — which is the wrong
      // material on a stone castle or a concrete plaza, and on a
      // building-dense map is a large slice of the screen. Cast stone with
      // weather drips reads correctly everywhere and gives the dressing a
      // material CONTRAST against the smooth `facade` panel it sits on;
      // with both wearing `surface` the dressing dissolved into the wall.
      // On the TIMBER maps the dressing is framing timber, not cast stone — its
      // cornices, corner posts and girt rails are the same sawn boards as the
      // walls, so they wear `wood` (a hair darker, tinted by the box colour) and
      // read as beams framing the boards instead of stone glued onto a ship.
      ? new THREE.MeshStandardMaterial(surfaceMaps(woodMap ? "wood" : "trim", {
        color: woodMap ? "#d8d2c8" : "#ffffff",
        vertexColors: true,
        roughness: 0.95,
        metalness: woodMap ? 0.0 : theme === "day" ? 0.04 : 0.12,
      }, woodMap ? 0.5 : 1.1))
      : null;
    if (detailMat && weatherOn) applyMapWeather(detailMat, WEATHER_PRESETS[woodMap ? "timberTrim" : "trim"]);
    for (const [key, geos] of detailGroups) {
      const merged = mergeGeometries(geos);
      if (!merged) { console.error(`[map] detail merge failed for group ${key} — skipped`); continue; }
      const mesh = new THREE.Mesh(merged, detailMat!);
      mesh.castShadow = true;
      mesh.receiveShadow = true;
      group.add(mesh);
      for (const g of geos) g.dispose();
    }
    // THE FLOOR CONTACT — one mesh, MULTIPLY blended (dst *= src), so a skirt
    // darkens grass, asphalt and roof felt alike and tracks the day/night cycle
    // for free. Same blend the decal field is built on; see decalTextures.ts.
    // FOG IS LEFT ON here, unlike the decals: under multiply, fogging the RGB
    // toward the (bright) fog colour drives the multiplier toward white, which
    // is exactly "the contact shadow fades out with distance". Turning it off
    // would leave hard dark smudges hanging in the haze.
    if (contactGeos.length) {
      const merged = mergeGeometries(contactGeos);
      if (merged) {
        const mesh = new THREE.Mesh(merged, new THREE.MeshBasicMaterial({
          map: groundGrimeTexture(),
          blending: THREE.MultiplyBlending,
          transparent: true,
          depthWrite: false,          // it is 3 cm of dirt; it occludes nothing
          // tone mapping would transform the multiplier before the blend unit
          // and a "white = invisible" texel would stop being white
          toneMapped: false,
          side: THREE.FrontSide,
        }));
        mesh.castShadow = false;
        mesh.receiveShadow = false;   // it IS the shadow
        mesh.renderOrder = 1;         // after opaque, before the additive FX
        group.add(mesh);
      }
      // logged, like `[maptex]`, so "did the contact pass actually run on this
      // map" is never a guess from a screenshot again
      console.log(`[map] floor contact: ${contactGeos.length} quads`);
      for (const g of contactGeos) g.dispose();
    }
    // ROAD PAINT — one mesh, ALPHA-TESTED (no blending, no sort, writes depth).
    // `depthWrite` stays on and the quad sits 2 cm above the floor, so nothing
    // here can z-fight or fall out of the opaque pass.
    if (markGeos.length) {
      const merged = mergeGeometries(markGeos);
      if (merged) {
        const paint = roadPaintTexture();
        const mat = new THREE.MeshStandardMaterial({
          color: "#ffffff",
          vertexColors: true,          // white lines vs the warm centre line
          map: paint.map,
          normalMap: paint.normal ?? undefined,
          roughnessMap: paint.roughness ?? undefined,
          roughness: 0.85,
          metalness: 0.02,
          alphaTest: 0.45,
          transparent: false,
          side: THREE.FrontSide,
        });
        const mesh = new THREE.Mesh(merged, mat);
        mesh.castShadow = false;      // 2 cm of paint casts nothing
        mesh.receiveShadow = true;    // ...but a building's shadow crosses it
        group.add(mesh);
      }
      for (const g of markGeos) g.dispose();
    }
    // STREET IRONWORK — manhole frames/covers and gutter grates, one merged
    // mesh wearing the industrial `surface` family (the one metal in the map
    // texture set), which is the material contrast the paving never had.
    if (ironGeos.length) {
      for (const g of ironGeos) worldProjectUV(g, 0.9);
      const merged = mergeGeometries(ironGeos);
      if (merged) {
        const mesh = new THREE.Mesh(merged, new THREE.MeshStandardMaterial(surfaceMaps("surface", {
          color: "#6a6e75", roughness: 0.66, metalness: 0.62,
        }, 1.0)));
        mesh.castShadow = false;
        mesh.receiveShadow = true;
        group.add(mesh);
      }
      for (const g of ironGeos) g.dispose();
    }
    // THE AWNINGS — striped fabric on the `cloth` family, vertex-coloured two
    // tones from one material. It CASTS: the shadow an awning throws across a
    // doorway is most of what makes the street read as lit from above, and it
    // is the only shadow in this game cast by something coloured.
    if (awningGeos.length) {
      const merged = mergeGeometries(awningGeos);
      if (merged) {
        const mesh = new THREE.Mesh(merged, new THREE.MeshStandardMaterial(surfaceMaps("cloth", {
          color: "#ffffff", vertexColors: true, roughness: 0.94, metalness: 0.0,
        }, 1.2)));
        mesh.castShadow = true;
        mesh.receiveShadow = true;
        group.add(mesh);
      }
      for (const g of awningGeos) g.dispose();
    }
    // THE LIT SIGN FACES — the second emissive material in the game (the office
    // -window `facade` glow is the other), and the only one that is a texture
    // ATLAS: four sign designs in one 512² sheet, one 0.5 x 0.5 cell per quad.
    // Deliberately built on the SAME program variant as the building material
    // (map + normal + roughness + emissive, front-sided, opaque, no vertex
    // colour) so a street full of signs never costs a cold shader compile.
    if (signFaceGeos.length) {
      const merged = mergeGeometries(signFaceGeos);
      if (merged) {
        const mat = new THREE.MeshStandardMaterial(surfaceMaps("signage", {
          color: "#ffffff", roughness: 0.55, metalness: 0.12,
          emissive: "#ffffff", emissiveMap: mapTextures().signageGlow,
          emissiveIntensity: 1.0,
        }, 1.0));
        const mesh = new THREE.Mesh(merged, mat);
        mesh.castShadow = false;   // a plane 12 mm proud of its own case
        mesh.receiveShadow = false; // ...and it makes its own light
        group.add(mesh);
        this.signGlowMat = mat;
        this.applySignGlow();
      }
      for (const g of signFaceGeos) g.dispose();
    }
    // THE SHOP GLAZING — the third emissive material in the game, and the only
    // surface anywhere in it that shows a room BEHIND itself. Built on the same
    // program variant as the sign faces (map + normal + roughness + emissive,
    // front-sided, opaque, no vertex colour) so a street of shopfronts never
    // costs a cold shader compile. `metalness` is high and `roughness` low
    // because this is plate glass: it has to catch the sky the stone next to it
    // does not, and that contrast is what makes it read as a different material
    // rather than as a dark painted panel.
    if (shopGlassGeos.length) {
      const merged = mergeGeometries(shopGlassGeos);
      if (merged) {
        const mat = new THREE.MeshStandardMaterial(surfaceMaps("shopwin", {
          // metalness is DELIBERATELY modest. The first capture ran it at 0.55
          // and the environment reflection turned every pane into a sheet of
          // flat blue steel — a mirror is not a window, and the room behind it
          // is the whole point of the family.
          color: "#ffffff", roughness: 0.34, metalness: 0.18,
          emissive: "#ffffff", emissiveMap: mapTextures().shopwinGlow,
          emissiveIntensity: 1.0,
        }, 1.0));
        const mesh = new THREE.Mesh(merged, mat);
        mesh.castShadow = false;    // a 2 cm pane inside its own frame
        mesh.receiveShadow = true;  // ...but the awning's shadow crosses it
        group.add(mesh);
        this.shopGlowMat = mat;
        this.applySignGlow();
      }
      for (const g of shopGlassGeos) g.dispose();
    }
    // HOUSE WINDOW JOINERY — frames and shutters, vertex-coloured, on the
    // `wood` family (photo planks when the scans are loaded). Casts: the
    // shutters' and cills' small shadows are what make the window read as set
    // into the wall.
    if (joineryGeos.length) {
      const merged = mergeGeometries(joineryGeos);
      if (merged) {
        const mesh = new THREE.Mesh(merged, new THREE.MeshStandardMaterial(surfaceMaps("wood", {
          color: "#ffffff", vertexColors: true, roughness: 0.8, metalness: 0.0,
        }, 1.0)));
        mesh.castShadow = true;
        mesh.receiveShadow = true;
        group.add(mesh);
      }
      for (const g of joineryGeos) g.dispose();
    }
    // HOUSE WINDOW GLASS — dark, smooth, reflects the sky/HDRI. Modest
    // metalness for the same reason as the shop glazing: a mirror is not a
    // window. No texture: one tiny program, shared by every pane on the map.
    if (winGlassGeos.length) {
      const merged = mergeGeometries(winGlassGeos);
      if (merged) {
        const mesh = new THREE.Mesh(merged, new THREE.MeshStandardMaterial({
          color: "#141b20", roughness: 0.12, metalness: 0.0, envMapIntensity: 0.7,
        }));
        mesh.castShadow = false;
        mesh.receiveShadow = true;
        group.add(mesh);
      }
      for (const g of winGlassGeos) g.dispose();
    }
    // ROOF DECK — the felt membrane. One merged mesh for the whole map. It
    // RECEIVES shadows (the plant standing on it, and the tower next door) but
    // casts none: it is 7 cm of ballast lying on a roof that already casts.
    if (deckGeos.length) {
      const merged = mergeGeometries(deckGeos);
      if (merged) {
        const mesh = new THREE.Mesh(merged, new THREE.MeshStandardMaterial(surfaceMaps("roofing", {
          color: "#ffffff", vertexColors: true, roughness: 0.98, metalness: 0.02,
        }, 1.0)));
        mesh.castShadow = false;
        mesh.receiveShadow = true;
        group.add(mesh);
      }
      for (const g of deckGeos) g.dispose();
    }
    // ROOF PLANT — corrugated galvanised steel, the one genuinely metallic
    // material on the map mesh. `metalness` sits high on purpose: this family's
    // roughness map has the lowest floor in the texture set, so the rib crests
    // are the one place in a map where the sky actually reflects.
    if (plantGeos.length) {
      const merged = mergeGeometries(plantGeos);
      if (merged) {
        const mesh = new THREE.Mesh(merged, new THREE.MeshStandardMaterial(surfaceMaps("galv", {
          color: "#ffffff", vertexColors: true, roughness: 0.72, metalness: 0.68,
        }, 1.15)));
        mesh.castShadow = true;   // the plant's shadows ACROSS the deck are most
        mesh.receiveShadow = true; // of what makes a roof read as occupied
        group.add(mesh);
      }
      for (const g of plantGeos) g.dispose();
    }
    // ROOF GRILLE (2026-08-05, ROUND TWO) — the condenser flanks and their fan
    // cowls, on the `louvre` family. `metalness` sits BELOW the plant's on
    // purpose: powder-coated grille next to bare zinc is the material contrast
    // that stops the whole roof reading as one lump of grey metal, and the
    // louvre's dark gaps do the depth work that a specular highlight would
    // otherwise have to fake. Every parameter here is chosen to land on an
    // EXISTING shader program variant (front-sided, vertex-coloured, opaque
    // standard) so the roof grille never costs a cold compile.
    if (grilleGeos.length) {
      const merged = mergeGeometries(grilleGeos);
      if (merged) {
        const mesh = new THREE.Mesh(merged, new THREE.MeshStandardMaterial(surfaceMaps("louvre", {
          color: "#ffffff", vertexColors: true, roughness: 0.8, metalness: 0.42,
        }, 1.35)));
        mesh.castShadow = true;
        mesh.receiveShadow = true;
        group.add(mesh);
      }
      for (const g of grilleGeos) g.dispose();
    }
    for (const [ekey, geos] of edgeGroups) {
      const [color, opacity] = ekey.split("|");
      const merged = mergeGeometries(geos);
      if (!merged) { console.error(`[map] edge merge failed for ${ekey} — skipped`); continue; }
      const lines = new THREE.LineSegments(
        merged,
        new THREE.LineBasicMaterial({ color, transparent: true, opacity: Number(opacity) }),
      );
      group.add(lines);
      for (const g of geos) g.dispose();
    }

    if (theme === "neon") {
      const grid = new THREE.GridHelper(48, 24, "#00f0ff", "#1a2a45");
      grid.position.y = 0.02;
      (grid.material as THREE.Material).transparent = true;
      (grid.material as THREE.Material).opacity = 0.18;
      group.add(grid);
    }

    mmark("merge");
    // GALLEONS (shipDressing.ts): static, so it is added before the freeze.
    if (shipBoxes.length) group.add(buildShipDressing(shipBoxes, this.renderer.shadowMap.enabled));
    // PER-MAP EXTRAS (mapExtras/<mapId>.ts): static, so also before the freeze
    for (const g of buildMapExtras({ map, theme, shadows: this.renderer.shadowMap.enabled, lowSpec: lowSpec() })) group.add(g);
    mmark("ship+extras");

    // PERF — FREEZE THE STATIC MAP MATRICES. Everything added to `group` above
    // this line is baked static geometry (merged per-cell family meshes, trim,
    // edge lines, the neon grid): local transforms never change again. But three
    // recomputes each one EVERY frame anyway — WebGLRenderer.render calls
    // scene.updateMatrixWorld(), the Scene has matrixAutoUpdate on so it dirties
    // itself, and that cascades force=true through the whole graph
    // (Object3D.updateMatrixWorld, three.core.js:7841 — the block sets force=true
    // unconditionally once entered), so every descendant pays a matrix.compose +
    // matrixWorld.multiplyMatrices per frame regardless of the auto-update flags
    // on the leaves. This is the same cascade the weapon-cache freeze addresses
    // (bench-matrix-static.mjs); here it hits the biggest static subtree in the
    // scene. updateMatrixWorld(true) bakes the current local+world matrices ONCE
    // (grid's y=0.02, every mesh's identity), then dropping both flags leaves
    // matrixWorld frozen-correct and skips the compose+multiply forever after.
    // Done BEFORE the water/lava/rain/decor/sprite children are added below —
    // those genuinely animate their transforms (tide, camera-follow, billboard,
    // spinners, EQ bars) and keep the default auto-update. Measured by
    // agent/bench-map-static.mjs.
    group.updateMatrixWorld(true);
    for (const child of group.children) {
      child.matrixAutoUpdate = false;
      child.matrixWorldAutoUpdate = false;
    }
    console.log(`[map] froze ${group.children.length} static meshes (no per-frame matrix update)`);

    // animated WATER surface: one mesh, rippling shader (built from the water
    // boxes pulled out above)
    if (waterGeos.length) {
      const merged = mergeGeometries(waterGeos);
      if (merged) {
        // uSkyLo/uSkyHi/uSunDir/uNight (2026-08-01, mission `graphics`): the sea
        // used to reflect a HARD-CODED pale blue and glint off a hard-coded
        // light vector, so a moat under a magenta neon night still had a
        // daylight sky in it and the specular sat where no light was. These are
        // rewritten by setSky on every theme change and day/night blend step.
        const uniforms: WaterUniforms = {
          uTime: { value: 0 },
          uRipple: { value: rippleNormalTexture() },
          uSkyLo: { value: new THREE.Color("#7fb0e8") },
          uSkyHi: { value: new THREE.Color("#b9dcf2") },
          uSunDir: { value: this.sunDir.clone() },
          uNight: { value: 0 },
        };
        this.waterUniforms = uniforms;
        const waterMat = new THREE.ShaderMaterial({
          uniforms,
          transparent: true,
          side: THREE.DoubleSide, // see the surface from below while swimming
          vertexColors: true,
          vertexShader: `
            // NB: 'color' is injected by three when vertexColors:true — do NOT
            // redeclare it (that's a GLSL redefinition error that silently
            // killed the whole water/lava mesh).
            uniform float uTime;
            varying vec3 vWorld;
            varying vec3 vBase;
            varying float vCrest;
            varying vec3 vNormalW;
            // two crossing wave trains → a rolling swell + its surface normal
            void main() {
              vBase = color;
              vec4 wp = modelMatrix * vec4(position, 1.0);
              float a = wp.x * 0.5 + uTime * 1.2;
              float b = wp.z * 0.42 - uTime * 0.9;
              float h = sin(a) * 0.10 + sin(b) * 0.09 + sin((wp.x + wp.z) * 0.3 + uTime * 0.7) * 0.05;
              wp.y += h;
              vCrest = h;
              // analytic normal from the wave gradient (for lighting/fresnel)
              float dhx = cos(a) * 0.5 * 0.10 + cos((wp.x + wp.z) * 0.3 + uTime * 0.7) * 0.3 * 0.05;
              float dhz = cos(b) * 0.42 * 0.09 + cos((wp.x + wp.z) * 0.3 + uTime * 0.7) * 0.3 * 0.05;
              vNormalW = normalize(vec3(-dhx, 1.0, -dhz));
              vWorld = wp.xyz;
              gl_Position = projectionMatrix * viewMatrix * wp;
            }`,
          fragmentShader: `
            uniform float uTime;
            uniform sampler2D uRipple;
            uniform vec3 uSkyLo, uSkyHi, uSunDir;
            uniform float uNight;
            varying vec3 vWorld;
            varying vec3 vBase;
            varying float vCrest;
            varying vec3 vNormalW;
            void main() {
              // CAPILLARY RIPPLE (2026-08-01, mission graphics): the analytic
              // swell above is a ~13 m wave, and below that the surface was a
              // perfectly smooth plastic sheet — nothing between "big wave" and
              // "mirror", which is why close-up water read as a flat teal slab.
              // Two scrolling samples of the tiling ripple normal (different
              // scale AND different drift direction, so they never beat into a
              // visible grid) perturb the normal that the fresnel and the sun
              // glint below both key off — high-frequency slope is exactly what
              // makes water sparkle instead of smear.
              vec2 uvA = vWorld.xz * 0.22 + vec2(uTime * 0.035, uTime * 0.021);
              vec2 uvB = vWorld.xz * 0.071 - vec2(uTime * 0.019, uTime * 0.028);
              vec3 rn = (texture2D(uRipple, uvA).xyz - 0.5) * 2.0
                      + (texture2D(uRipple, uvB).xyz - 0.5) * 2.0 * 0.7;
              vec3 N = normalize(vNormalW + vec3(rn.x, 0.0, rn.y) * 0.34);
              vec3 V = normalize(cameraPosition - vWorld);
              // depth-graded body: deep navy troughs → turquoise crests
              // (2026-11 realism pass) was vBase*0.5 → mix(.., (0.5,0.9,0.95), 0.5):
              // the crest colour was a milky turquoise, which from a low camera
              // turned the whole open sea pale. Deep water is dark; the crest
              // only gains a little green from light scattered through it.
              vec3 deep = vBase * 0.42;
              vec3 shallow = mix(vBase, vec3(0.16, 0.5, 0.5), 0.3);
              float cg = clamp(vCrest * 4.0 + 0.5, 0.0, 1.0);
              vec3 col = mix(deep, shallow, cg);
              // CAUSTIC SHIMMER: two crossing moving grids brighten the surface
              float ca = sin(vWorld.x * 0.6 + uTime * 1.5) * sin(vWorld.z * 0.55 - uTime * 1.2);
              float cb = sin((vWorld.x + vWorld.z) * 0.4 + uTime * 2.0);
              col += vec3(0.05, 0.11, 0.12) * clamp(ca * 0.5 + cb * 0.3, 0.0, 1.0);
              // FOAM streaks riding the wave crests
              // Only the very tops of the swell break (the old 0.10 threshold
              // whitened ~30 % of the sea — "snow" from a low angle), and the
              // whitecap is torn into streaks by the ripple texture.
              float tear = texture2D(uRipple, uvB * 2.3).x;
              float foam = smoothstep(0.165, 0.215, vCrest) * smoothstep(0.42, 0.7, tear + 0.15 * ca);
              col = mix(col, vec3(0.9, 0.95, 0.97), clamp(foam, 0.0, 0.6));
              // SKY REFLECTION at grazing angles (fresnel) — the big "it's
              // water" cue. The reflected ray is what decides WHICH bit of sky
              // you see: a steep look reflects the zenith, a grazing one the
              // horizon, so the same sea reads bright near the shore and deep
              // underfoot, and it tracks the day/night cycle instead of being
              // frozen at noon.
              vec3 R = reflect(-V, N);
              vec3 skyRefl = mix(uSkyLo, uSkyHi, smoothstep(0.0, 0.55, R.y));
              float fres = pow(1.0 - clamp(dot(N, V), 0.0, 1.0), 3.0);
              col = mix(col, skyRefl, fres * (0.62 - 0.16 * uNight));
              // a moving sun glint off the wave slopes (sharp, not noisy),
              // aimed down the scene's real sun vector
              vec3 H = normalize(uSunDir + V);
              float spec = pow(max(dot(N, H), 0.0), 90.0);
              col += spec * mix(vec3(1.0, 0.98, 0.9), vec3(0.72, 0.78, 1.0), uNight) * (0.9 - 0.35 * uNight);
              // and a broad sheen along the same vector so the whole sun side
              // of the water is lifted, not just the specular pinpoint
              col += vec3(1.0, 0.96, 0.86) * pow(max(dot(N, H), 0.0), 8.0) * 0.10 * (1.0 - uNight);
              // more opaque at grazing angles (fresnel) — with the darker body
              // above, a flat 0.9 let seabed ruins glow through as pale blocks
              gl_FragColor = vec4(col, mix(0.94, 1.0, fres));
            }`,
        });
        const wmesh = new THREE.Mesh(merged, waterMat);
        wmesh.receiveShadow = true;
        group.add(wmesh);
        this.waterMesh = wmesh;
        this.syncWaterSky();
      }
      for (const g of waterGeos) g.dispose();
    }

    // animated LAVA surface (medieval moat): a dark crust with glowing molten
    // cracks that flow and pulse — self-lit, no shadow.
    if (lavaGeos.length) {
      const merged = mergeGeometries(lavaGeos);
      if (merged) {
        const uniforms = { uTime: { value: 0 } };
        this.lavaUniforms = uniforms;
        const lavaMat = new THREE.ShaderMaterial({
          uniforms,
          vertexColors: true,
          vertexShader: `
            uniform float uTime;
            varying vec3 vWorld;
            varying vec3 vBase;
            void main() {
              vBase = color;
              vec4 wp = modelMatrix * vec4(position, 1.0);
              wp.y += sin(wp.x * 0.5 + uTime * 0.7) * 0.05 + sin(wp.z * 0.4 - uTime * 0.5) * 0.05;
              vWorld = wp.xyz;
              gl_Position = projectionMatrix * viewMatrix * wp;
            }`,
          fragmentShader: `
            uniform float uTime;
            varying vec3 vWorld;
            varying vec3 vBase;
            // cheap value-noise for the crust/flow pattern
            float hash(vec2 p){ return fract(sin(dot(p, vec2(41.3, 289.1))) * 43758.5); }
            float noise(vec2 p){
              vec2 i = floor(p), f = fract(p);
              vec2 u = f * f * (3.0 - 2.0 * f);
              return mix(mix(hash(i), hash(i + vec2(1,0)), u.x),
                         mix(hash(i + vec2(0,1)), hash(i + vec2(1,1)), u.x), u.y);
            }
            void main() {
              // flow the pattern slowly so the lava creeps along the moat
              vec2 p = vWorld.xz * 0.16 + vec2(0.0, uTime * 0.12);
              float n = noise(p) * 0.6 + noise(p * 2.3 + 7.0) * 0.4;
              float pulse = 0.5 + 0.5 * sin(uTime * 1.3 + vWorld.x * 0.2);
              // crust = dark base; cracks (low n) glow molten; hot spots brightest
              vec3 crust = vBase * 0.5;
              vec3 molten = mix(vec3(0.85, 0.22, 0.03), vec3(1.0, 0.75, 0.15), pulse);
              float crack = smoothstep(0.55, 0.15, n);           // where the crust splits
              vec3 col = mix(crust, molten, crack);
              col += vec3(1.0, 0.55, 0.1) * smoothstep(0.12, 0.0, n) * (0.6 + 0.4 * pulse); // white-hot cores
              gl_FragColor = vec4(col, 1.0);
            }`,
        });
        const lmesh = new THREE.Mesh(merged, lavaMat);
        group.add(lmesh);
      }
      for (const g of lavaGeos) g.dispose();
    }

    // decorative dressing: distant scenery ring + instanced ground props
    mmark("water");
    const decor = buildDecor(map);
    group.add(decor);
    mmark("decor");
    this.decorSpinners = [];
    this.decorEq = [];
    this.decorBeat = [];
    this.decorHistViz = [];
    decor.traverse((o) => {
      if (o.userData.spin) this.decorSpinners.push(o);
      if (o.userData.eq !== undefined) this.decorEq.push(o);
      if (o.userData.beatRing) this.decorBeat.push(o as THREE.InstancedMesh);
      if (o.userData.histViz) this.decorHistViz.push(o.userData.histViz);
    });
    this.decorEq.sort((a, b) => (a.userData.eq as number) - (b.userData.eq as number));
    // where the scattered props meet the ground — consumed once by the contact
    // shadow field (contactShadows.ts), which owns the blobs under them
    this.decorGroundSpots = (decor.userData.groundSpots as DecorGroundSpot[]) ?? [];

    // WEATHER: cosmetic falling-particle rain (e.g. mega_chibre). One THREE.Points
    // draw call, added to the map group so disposeSubtree frees it on the next
    // load. Spread ±25 in X/Z, span rainSpanY in Y (from 0 up); render() recentres
    // it on the camera.
    //
    // The FALL is done ON THE GPU: the base positions are uploaded ONCE and each
    // droplet's Y is wrapped in the vertex shader as `mod(baseY - fall*t, span)`.
    // This was a per-frame CPU loop over up to 4000 droplets that also flagged
    // the whole position buffer `needsUpdate` — a ~48 KB re-upload EVERY frame
    // (≈2.9 MB/s at 60 fps and 4000 drops). Now the card owns the animation and
    // render() only writes one `uRainTime` float. Same downward fall + top
    // re-entry as the old loop (`y -= fall*dt; if (y<0) y += span`), just
    // relocated to GLSL; cosmetic, client-only, no sim/determinism impact.
    if (map.weather?.kind === "rain") {
      const count = Math.max(1, Math.min(4000, map.weather.count ?? 800));
      this.rainSpeed = map.weather.fallSpeed ?? 12;
      const SPREAD = 50;
      const pos = new Float32Array(count * 3);
      for (let i = 0; i < count; i++) {
        // deterministic-free scatter is fine here (cosmetic, client-only)
        pos[i * 3] = (Math.random() - 0.5) * SPREAD;
        pos[i * 3 + 1] = Math.random() * this.rainSpanY;
        pos[i * 3 + 2] = (Math.random() - 0.5) * SPREAD;
      }
      const geo = new THREE.BufferGeometry();
      geo.setAttribute("position", new THREE.BufferAttribute(pos, 3));
      const span = this.rainSpanY, fall = this.rainSpeed;
      const mat = new THREE.PointsMaterial({
        color: new THREE.Color(map.weather.color),
        size: map.weather.size ?? 0.5,
        transparent: true,
        opacity: 0.85,
        depthWrite: false,
        fog: false,
      });
      // Inject the GPU fall into PointsMaterial's own program (keeps its size
      // attenuation / color / alpha). `begin_vertex` is where three defines
      // `transformed = vec3(position)`, before the model matrix (the camera
      // re-centre) is applied — so the wrap happens in local Y, exactly like the
      // old loop did on the untranslated buffer.
      mat.onBeforeCompile = (shader) => {
        shader.uniforms.uRainTime = { value: 0 };
        shader.uniforms.uRainFall = { value: fall };
        shader.uniforms.uRainSpan = { value: span };
        shader.vertexShader =
          "uniform float uRainTime;\nuniform float uRainFall;\nuniform float uRainSpan;\n" +
          shader.vertexShader.replace(
            "#include <begin_vertex>",
            "#include <begin_vertex>\n\ttransformed.y = mod(transformed.y - uRainFall * uRainTime, uRainSpan);",
          );
        this.rainUniforms = shader.uniforms as { uRainTime: { value: number } };
      };
      const points = new THREE.Points(geo, mat);
      points.frustumCulled = false; // it's recentred on the camera every frame
      group.add(points);
      this.rainPoints = points;
    }

    if (map.id === "mega_chibre") this.buildChibreDecor(group);

    this.mapGroup = group;
    // photo set already loaded (any map load after the first, or a live map
    // swap): wear it from the first frame. Otherwise precompile() upgrades.
    upgradeMapMaterials(group, FACADE_LAYOUT);
    this.scene.add(group);
    // (post-processing already set by applyAtmosphere)
    mmark("rest");
    console.log(`[load] loadMap ${map.id} ${(performance.now() - mt0).toFixed(0)} ms (${mt.join(" ")})`);
  }

  /** mega_chibre flavour (photobooth/pong project): pixelised floating sprites
   *  that bob + billboard the camera, plus a projector-style wallpaper plane on
   *  the side. NearestFilter keeps the pixel art crisp. Added to `group` so
   *  disposeSubtree frees them; sprites tracked for the per-frame bob. */
  private buildChibreDecor(group: THREE.Group): void {
    // DOWNSAMPLE each image to a tiny canvas then upscale with NearestFilter so
    // everything reads as chunky PIXELS (not just crisp native art). `repeat`
    // tiles the texture (for the projector background).
    const pixTex = (url: string, px = 56, repeat = 1): THREE.Texture => {
      const canvas = document.createElement("canvas");
      canvas.width = canvas.height = px;
      const tex = new THREE.CanvasTexture(canvas);
      tex.magFilter = THREE.NearestFilter;
      tex.minFilter = THREE.NearestFilter;
      tex.colorSpace = THREE.SRGBColorSpace;
      if (repeat > 1) { tex.wrapS = tex.wrapT = THREE.RepeatWrapping; tex.repeat.set(repeat, repeat); }
      const img = new Image();
      img.onload = () => {
        const ar = img.width / img.height || 1;
        let w = px, h = px;
        if (ar > 1) h = Math.max(1, Math.round(px / ar)); else w = Math.max(1, Math.round(px * ar));
        canvas.width = w; canvas.height = h;
        const ctx = canvas.getContext("2d");
        if (!ctx) return;
        ctx.imageSmoothingEnabled = false;
        ctx.clearRect(0, 0, w, h);
        ctx.drawImage(img, 0, 0, w, h);
        tex.needsUpdate = true;
      };
      img.src = url;
      return tex;
    };
    // floating pixel sprites (pong penis art) scattered around the sculpture
    const spots: [number, number, number, number][] = [
      [-15, 6, -10, 3.4], [14, 7, 4, 3.8], [0, 10, -24, 3.2], [-10, 5, 20, 3.0], [12, 6, 40, 3.4],
      [5, 9, -40, 2.8], [-16, 8, 14, 3.0], [16, 9, -16, 3.2], [-6, 7, 44, 2.8], [8, 8, -6, 3.0],
    ];
    for (let i = 0; i < spots.length; i++) {
      const [x, y, z, s] = spots[i];
      const mat = new THREE.MeshBasicMaterial({ map: pixTex(`/chibre/cock${(i % 5) + 1}.png`, 10), transparent: true, side: THREE.DoubleSide, depthWrite: false });
      const spr = new THREE.Mesh(new THREE.PlaneGeometry(s, s), mat);
      spr.position.set(x, y, z);
      spr.userData.baseY = y;
      spr.userData.phase = i * 0.9;
      group.add(spr);
      this.chibreSprites.push(spr);
    }
    // PROJECTOR background: a big pixelised plane on the side, TILED with the pong
    // penis art (not a photo). Dark backing so the transparent sprite reads, and a
    // faint additive glow so it looks like a projection.
    const backing = new THREE.Mesh(new THREE.PlaneGeometry(42, 26),
      new THREE.MeshBasicMaterial({ color: "#141018", side: THREE.DoubleSide }));
    backing.position.set(40, 13, 0); backing.rotation.y = -Math.PI / 2;
    const screen = new THREE.Mesh(new THREE.PlaneGeometry(42, 26),
      new THREE.MeshBasicMaterial({ map: pixTex("/chibre/cock2.png", 8, 5), transparent: true, opacity: 0.95, side: THREE.DoubleSide, depthWrite: false }));
    screen.position.set(39.7, 13, 0); screen.rotation.y = -Math.PI / 2;
    const glow = new THREE.Mesh(new THREE.PlaneGeometry(42, 26),
      new THREE.MeshBasicMaterial({ color: "#ffe6f0", transparent: true, opacity: 0.05, blending: THREE.AdditiveBlending, depthWrite: false }));
    glow.position.set(39.5, 13, 0); glow.rotation.y = -Math.PI / 2;
    group.add(backing, screen, glow);
  }

  // Wall-clock scheduling keeps GPU work independent of the monitor refresh
  // rate. Frame-count gates accidentally ran 2-4x faster on 144/240 Hz panels.
  private nextShadowAt = 0;
  private nextVizAt = 0;

  // ---- adaptive resolution: the universal fluidity lever. When frame times
  // climb past ~60 fps budget the internal render resolution steps down
  // (0.25 increments, floor 1.0 — barely visible); with headroom it climbs
  // back toward the display's native ratio. Checked every ~2 s so buffer
  // reallocation never becomes its own stutter.
  /** Smoothed frame time (ms) — exposed for the HUD fps readout. */
  frameEma = 16;
  /** Smoothed per-frame WORK time (ms) — frameEma minus the vsync wait. On a
   *  60 Hz display frameEma is pinned at ~16.7 no matter how idle the GPU is,
   *  which made every "recover quality" threshold (< 13 ms) unreachable:
   *  scale/ladder ratcheted DOWN only ("desktop lags now" = permanent 0.55x
   *  blur at L2). busyEma is refresh-rate independent. */
  private busyEma = 8;
  private lastDownAt = 0;
  private lastFrameAt = 0;
  private lastEqNow = 0; // frame-dt clock for the per-frame music-EQ bounce (rate-independent ease)
  private lastScaleCheck = 0;
  private postKilled = false; // L2+: post chain off, render direct
  // dynamic cost ladder: proven-weak machines start one level in
  // The quality preset can raise the floor (Low pins L2: no shadows, no post).
  private gfxLvl = Math.max(initialGfxLvl(), this.caps.minLevel);
  private lvlBadSince = 0;
  private lvlGoodSince = 0;
  // weak machines start at a lower internal resolution — the ladder raises
  // it automatically (frameEma < 13ms) if the hardware turns out to cope
  private pixelScale = gfxPinned() ? Math.min(window.devicePixelRatio, this.caps.pixelCap)
    : weakGpu() ? Math.min(window.devicePixelRatio, 0.65, this.caps.pixelCap)
    : weakHardware() ? Math.min(window.devicePixelRatio, 0.75, this.caps.pixelCap)
    : Math.min(window.devicePixelRatio, this.caps.pixelCap);

  private jankCount = 0;
  private jankWindowAt = 0;

  /** Refresh-rate-independent load signals for other client visual throttles. */
  hasFrameHeadroom(): boolean {
    return frameHasHeadroom(this.frameEma, this.busyEma, this.jankCount);
  }

  isFrameOverBudget(thresholdMs: number): boolean {
    return frameIsOverBudget(this.frameEma, this.busyEma, thresholdMs);
  }

  private adaptResolution(now: number): void {
    // A hidden or occluded tab renders at 1–4 Hz by BROWSER throttling — those
    // frame intervals say nothing about this machine. Feeding them into the
    // EMA/jank counters escalated the cost ladder to L3+ during a long
    // alt-tab and could then PERMANENTLY flag a healthy desktop as low-spec
    // (sticky localStorage). Don't sample, don't escalate, don't flag;
    // re-baseline on the first visible frame (lastFrameAt=0 skips its dt).
    if (document.visibilityState !== "visible") {
      this.lastFrameAt = 0;
      this.jankCount = 0;
      this.lvlBadSince = 0;
      this.lowSpecCandidateAt = 0;
      return;
    }
    // GRACE (settleFrameStats): the first live frames after a load screen pay
    // one-off costs — neither sample them nor decide anything on them
    if (now < this.statsGraceUntil && this.ladderApplied) { this.lastFrameAt = now; return; }
    if (this.lastFrameAt > 0) {
      const dt = Math.min(100, now - this.lastFrameAt);
      this.frameEma += (dt - this.frameEma) * 0.05;
      if (dt > 50) this.jankCount++; // spike: EMA barely notices these
    }
    this.lastFrameAt = now;
    if (now - this.lastScaleCheck < 1000) return; // react in 1 s, not 2
    this.lastScaleCheck = now;
    const native = Math.min(window.devicePixelRatio, this.caps.pixelCap);
    // resolution adapts continuously; the deepest ladder level lowers the
    // floor further. A soft image beats a stuttering one every time.
    const floor = this.gfxLvl >= 3 ? 0.45 : 0.55;
    let next = this.pixelScale;
    // real headroom, refresh-rate independent: cadence healthy (no missed
    // vsyncs), the frame's actual WORK small, and no recent jank spikes
    const headroom = frameHasHeadroom(this.frameEma, this.busyEma, this.jankCount);
    if (frameIsOverBudget(this.frameEma, this.busyEma, 25) && this.pixelScale > floor) { next = Math.max(floor, this.pixelScale - 0.5); this.lastDownAt = now; }
    else if (frameIsOverBudget(this.frameEma, this.busyEma, 18) && this.pixelScale > floor) { next = Math.max(floor, this.pixelScale - 0.25); this.lastDownAt = now; }
    // 10 s lockout after any downscale so a genuinely GPU-bound machine
    // (low CPU busy, saturated GPU) can't ping-pong render-target reallocs
    else if (headroom && this.pixelScale < native && now - this.lastDownAt > 10_000) next = Math.min(native, this.pixelScale + 0.25);
    // A sustained heavy hint pins rendering at 0.8. Do not let the adaptive
    // ladder raise it every second only for render() to lower it next frame;
    // that ping-pong reallocates every render target and causes a regular hitch.
    if (this.wasHeavy && next > 0.8) next = 0.8;
    if (gfxPinned()) next = native; // screenshot pin: hold full resolution
    // ---- DYNAMIC COST LADDER: one step deeper every 2.5s over budget, one
    // step back after 8s of solid headroom. Each level cuts the next-biggest
    // cost; the look stays the same, only the price drops:
    //   L1: shadows off + dynamic FX lights off + fewer particles
    //   L2: post chain off (bloom is THE iGPU killer) + slower cosmetic ticks
    //   L3: resolution floor drops to 0.45
    //   L4: 30fps draw cap — LAST resort: halving the draw rate makes every
    //       moving thing (bots/grenades/vehicles) visibly non-fluid
    const UP_AT = [20, 22, 26, 32]; // frame-ms that escalates from L0..L3
    if (gfxPinned()) { /* screenshot pin: never degrade, hold L0 + native res */ }
    else if (this.gfxLvl < 4 && frameIsOverBudget(this.frameEma, this.busyEma, UP_AT[this.gfxLvl])) {
      if (!this.lvlBadSince) this.lvlBadSince = now;
      else if (now - this.lvlBadSince > 2500) {
        this.gfxLvl++;
        this.lvlBadSince = 0;
        console.log(`[render] cost ladder → L${this.gfxLvl} (frame avg ${this.frameEma.toFixed(1)} ms)`);
      }
    } else this.lvlBadSince = 0;
    // the 30fps cap (L4) releases FAST after 3s of measured headroom — choppy
    // motion is the worst tier; lower levels require the same signal for 8s
    const goodFor = this.gfxLvl >= 4 ? 3000 : 8000;
    // like the resolution: busyEma-based recovery, or 60 Hz screens could
    // never leave L1/L2 once entered (frameEma floor = refresh interval)
    const ladderHealthy = frameHasHeadroom(this.frameEma, this.busyEma, this.jankCount);
    if (this.gfxLvl > this.caps.minLevel && ladderHealthy) {
      if (!this.lvlGoodSince) this.lvlGoodSince = now;
      else if (now - this.lvlGoodSince > goodFor) {
        this.gfxLvl--;
        this.lvlGoodSince = 0;
        console.log(`[render] cost ladder ← L${this.gfxLvl} (headroom returned)`);
      }
    } else this.lvlGoodSince = 0;
    // UN-flag sticky low-spec: the 60 Hz recovery bug mislabeled healthy
    // machines forever (one bad stretch escalated to L3, never came back,
    // 8 s later the flag stuck). A session that proves itself — ladder at
    // L0 with real headroom for 30 s — clears the flag so the NEXT session
    // starts at full quality again.
    // Recovery accepts L1 too (2026-07-22): demanding 30s of flawless headroom
    // at L0 specifically made the flag near-permanent in practice — the bar to
    // SET it (2×8s bad) was far lower than the bar to clear it. A machine
    // holding L0/L1 with real headroom for 30s is not a low-spec machine.
    if (this.gfxLvl <= 1 && ladderHealthy && lowSpec()) {
      if (!this.unflagSince) this.unflagSince = now;
      else if (now - this.unflagSince > 30_000) {
        setLowSpec(false);
        this.unflagSince = 0;
        console.log("[render] low-spec flag CLEARED — machine proved healthy");
      }
    } else this.unflagSince = 0;
    // SPIKE-driven escalation: repeated jank (4+ frames over 50ms within 5s)
    // means visible stutter even when the smoothed average looks healthy —
    // step down immediately. A single alt-tab/GC hiccup never trips this.
    // spikes only ever cut QUALITY (max L2) — a GC hiccup must never halve
    // the draw rate; L3/L4 need sustained over-budget frames
    if (now - this.jankWindowAt > 5000) {
      if (!gfxPinned() && this.jankCount >= 4 && this.gfxLvl < 2) {
        this.gfxLvl++;
        console.log(`[render] cost ladder → L${this.gfxLvl} (${this.jankCount} jank spikes in 5s)`);
      }
      this.jankCount = 0;
      this.jankWindowAt = now;
    }
    currentGfxLevel = this.gfxLvl;
    // apply. Shadows used to be one-way per session, on the premise that
    // re-enabling forces a shader recompile. Traced through three r172 and that
    // is not true: the program cache key's `shadowMapEnabled` is only ever
    // re-read when a material's own version bumps or the LIGHTS hash changes
    // (three.cjs:53557), and the shadow counts in that hash come from
    // `light.castShadow` alone (three.cjs:44991) — `renderer.shadowMap.enabled`
    // is not an input. Toggling it re-derives no programs at all; it just skips
    // the shadow pass. So shadows are now DERIVED from the level like
    // postKilled/cap30 below, and a machine that earns its way back to L0 gets
    // them back instead of staying degraded until reload.
    // (Left alone during a heavy burst — render() owns the flag then, and its
    // own restore path at the end of the burst already respects the ladder.)
    if (now >= this.heavySceneUntil) {
      const wantShadows = this.gfxLvl === 0 && !lowSpec();
      if (wantShadows !== this.renderer.shadowMap.enabled) {
        this.renderer.shadowMap.enabled = wantShadows;
        // a re-enabled map still holds the frozen contents from when it was
        // switched off — force one fresh pass or the stale shadows persist
        if (wantShadows) this.renderer.shadowMap.needsUpdate = true;
        console.log(`[render] shadows ${wantShadows ? "restored" : "disabled"} (cost ladder L${this.gfxLvl})`);
      }
    }
    this.postKilled = this.gfxLvl >= 2;
    this.cap30 = this.gfxLvl >= 4;
    this.ladderApplied = true;
    this.syncAoPass();
    // sticky LOW-SPEC: hitting the deepest level twice ≥8s apart marks the
    // machine weak (persisted) so every future session starts conservative —
    // a one-off GC/alt-tab hiccup on a good machine must not degrade it forever.
    // busyEma gate (2026-07-22): a healthy desktop was flagged from long frame
    // INTERVALS alone (vsync/compositor/throttling can all stretch cadence
    // while the GPU idles). Weakness means the measured per-frame WORK is
    // high — cadence-only badness must never persist a downgrade. And the
    // player is TOLD (toast) — a silent permanent visual downgrade cost a
    // whole debugging session to even discover.
    // Only on AUTO: a player who picked Medium/High chose that ceiling — the
    // live ladder still lowers cost this session, but nothing is persisted.
    if (this.gfxLvl >= 3 && this.busyEma > 12 && !lowSpec() && gfxQuality() === "auto") {
      if (this.lowSpecCandidateAt && now - this.lowSpecCandidateAt > 8000) {
        setLowSpec(true);
        console.log("[render] auto LOW-SPEC mode enabled (persisted)");
        announcePerfMode();
      } else if (!this.lowSpecCandidateAt) this.lowSpecCandidateAt = now;
    }
    if (next !== this.pixelScale) {
      this.pixelScale = next;
      this.renderer.setPixelRatio(next);
      this.renderer.setSize(window.innerWidth, window.innerHeight);
      // the composer keeps ITS OWN pixel ratio — without this the whole neon
      // post chain (bloom mips included) stayed at the initial 1.5x forever
      // and adaptive resolution never helped neon maps at all
      this.composer?.setPixelRatio(next);
      this.composer?.setSize(window.innerWidth, window.innerHeight);
      console.log(`[render] adaptive resolution → ${next}x (frame avg ${this.frameEma.toFixed(1)} ms)`);
    }
  }

  private statTick = 0;
  private cap30 = false;
  private drawFlip = false;
  private heavySceneUntil = 0;
  private preHeavyPixelScale: number | null = null;

  /** Compile every material in the scene NOW (during the connect/drop-in
   *  screen) — on weak iGPUs the first-render shader compile storm otherwise
   *  freezes the page for seconds right as the match starts. With the
   *  parallel-compile extension present this runs off the main thread.
   *
   *  `models`: realistic-model instances staged by the model modules
   *  (gltfAssets WarmStage — zombie/horse/camel/dragon). Their GLB waits run in
   *  parallel with the photo/prop waits below, they join the ONE scene-wide
   *  compile and the ONE warm frame at the end, and are armed + disposed
   *  before this returns. Used to be one sequential wait + full frame per
   *  model AFTER this method.
   *  `onArmed`: runs right after the models are armed, BEFORE the warm frame —
   *  the caller mounts them on its dormant spares there and returns the hidden
   *  roots to show for that one frame (so their bone textures, skinned shadow
   *  variants and buffers are uploaded here, not on first sight). */
  async precompile(models?: Promise<(WarmStage | null)[]>, onArmed?: () => THREE.Object3D[]): Promise<void> {
    const lt0 = performance.now();
    const lt: string[] = [];
    let ltAt = lt0;
    const lmark = (name: string): void => { const n = performance.now(); lt.push(`${name}=${(n - ltAt).toFixed(0)}`); ltAt = n; };
    // glTF CREATURES (loadGate.ts): realistic sharks / kraken still loading get
    // a bounded wait (in parallel with the photo-surface wait below) so a swap
    // that lands in time is IN the scene for the compile at the end of this
    // method; the gate closes after it, so a late one never pops in mid-match.
    const gates = awaitLoadGates(isTvApp() ? 0 : MODEL_LOAD_WAIT_MS); // real models (gltfAssets)
    // AO module (High preset): bounded wait, then slot the pass into the live
    // composer BEFORE the post-chain warm render at the end of this method
    const aoReady = this.caps.ao && !lowSpec()
      ? Promise.race([loadN8AO(), new Promise<null>((r) => window.setTimeout(() => r(null), 3000))])
      : Promise.resolve(null);
    // PHOTO SURFACES (pbrTextures.ts): give the in-flight load a bounded wait,
    // swap the map's procedural family textures for the photo set BEFORE the
    // compile below (a newly-present normal/rough slot is a program change),
    // and upload them here so the first frame does not. If the load is still
    // running at the deadline this round keeps the canvas look — never a
    // mid-match swap; the next map load picks the photos up from the cache.
    try {
      const ready = this.mapGroup ? await pbrReady(lowSpec() ? 0 : 2500) : false;
      if (!ready && this.mapGroup && !lowSpec()) console.log("[pbr] not ready at the load-screen deadline — procedural surfaces this round");
      if (this.mapGroup && ready) {
        const n = upgradeMapMaterials(this.mapGroup, FACADE_LAYOUT);
        if (n) console.log(`[pbr] ${n} map materials on photo surfaces`);
        // Upload only the photo textures THIS map's materials now reference.
        // Warming the whole library (16 families x 3 maps, ~5 MB of VRAM each
        // with mips) uploaded ~250 MB on every map, most of it never sampled,
        // and paid every upload + mip build on the load screen. pbr textures
        // are only ever bound by upgradeMapMaterials (map group, load time), so
        // an unreferenced one can never be needed mid-match on this map.
        const lib = new Set(pbrTextures());
        const used = new Set<THREE.Texture>();
        this.mapGroup.traverse((o) => {
          const m = (o as THREE.Mesh).material;
          if (!m) return;
          for (const mat of Array.isArray(m) ? m : [m]) {
            const sm = mat as THREE.MeshStandardMaterial;
            for (const t of [sm.map, sm.normalMap, sm.roughnessMap]) if (t && lib.has(t)) used.add(t);
          }
        });
        const pt = [...used];
        for (let i = 0; i < pt.length; i++) {
          warmTexture(pt[i]);
          // yield between families: each 1k upload + mip build is a few ms,
          // keep the load-screen spinner painting instead of one long block
          if (i % 3 === 2) await new Promise<void>((r) => window.setTimeout(r, 0));
        }
      }
    } catch { /* best-effort: procedural textures stay */ }
    lmark("photos");
    // scanned rocks etc.: bounded wait, swap BEFORE the compile below. Same gate
    // as startMapPropLoad (models: every machine but the TV app) — this used to
    // be !lowSpec(), so performance mode downloaded the scans and never showed
    // them (2026-11-05 "real 3D models everywhere")
    if (this.mapGroup && !isTvApp()) {
      try {
        if (await mapPropsReady(MODEL_LOAD_WAIT_MS)) {
          const n = upgradeMapProps(this.mapGroup, { shadows: this.renderer.shadowMap.enabled });
          if (n) console.log(`[props] ${n} prop sets on photoscanned models`);
        } else console.log("[props] scans not ready at the load-screen deadline — procedural props this round");
      } catch { /* best-effort: procedural props stay */ }
    }
    // real prop models (water tower, wagons, crates …): same bounded wait + swap
    if (this.mapGroup && !isTvApp()) {
      try {
        await propSwapsReady(MODEL_LOAD_WAIT_MS);
        const n = await upgradePropSwaps(this.mapGroup, this.renderer.shadowMap.enabled);
        if (n) console.log(`[props] ${n} prop kinds on real models`);
      } catch (e) { console.warn("[props] prop model swap failed — box props stay", e); }
    }
    lmark("props");
    await gates; // never rejects
    // staged realistic models (each stage has its own bounded GLB wait)
    let staged: WarmStage[] = [];
    try { staged = models ? (await models).filter((s): s is WarmStage => s !== null) : []; } catch { staged = []; }
    lmark("models");
    const aoMod = await aoReady;
    if (hdriLoad) await Promise.race([hdriLoad, new Promise<void>((r) => window.setTimeout(r, 2500))]);
    lmark("ao+hdri");
    this.refreshEnv();
    this.envLocked = true;
    if (aoMod && this.composer && !this.aoPass) this.addAoPass(this.composer, aoMod);
    // Everything built LAZILY mid-match (avatars, viewmodel weapons, monsters,
    // vehicles, pickups) is absent from the scene right now, so precompile has
    // never covered it — each one's first appearance was a cold compile in the
    // middle of a fight. Put one throwaway mesh per *shader program variant*
    // into the scene so those programs are built here instead. See
    // ensureWarmGroup for why variants, not entity instances.
    try { this.ensureWarmGroup(); } catch { /* best-effort, like the rest */ }
    // Same argument one level down, for CPU work rather than shaders: the five
    // cosmetic-gear texture families (felt/leather/bone/nylon/plate) are
    // generated on first use, and first use is a player picking up a jetpack or
    // dropping in under a parachute — mid-match. Generate them here, under the
    // load screen, where a few ms of canvas work costs nothing. Idempotent.
    try { warmAvatarTextures(); } catch { /* best-effort, like the rest */ }
    // PHOTO DETAIL on the avatar families (avatarTextures.ts): bounded wait for
    // the scans, fold them into the shared textures, upload here — never mid-match.
    try {
      const ap = avatarPhotoLoadPromise();
      if (ap && await Promise.race([ap.then(() => true), new Promise<boolean>((r) => window.setTimeout(() => r(false), 1500))])) {
        const n = upgradeAvatarTextures();
        if (n) console.log(`[avatex] ${n} avatar families on photo detail`);
      }
      for (const t of avatarTextures()) warmTexture(t);
    } catch { /* best-effort: procedural avatar surfaces stay */ }
    // Same argument for the six VEHICLE surface families (paint/plate/metal/
    // tyre/glass/track, 2026-08-01): first use is a ride spawning or a bot
    // mounting one, which is mid-match. ~40 ms of canvas work, once, here.
    try { prewarmVehicleTextures(); } catch { /* best-effort, like the rest */ }
    // ...and the eight MONSTER surface families (scale/fur/chitin/ceph/hide/
    // flesh/horn/burlap, 2026-08-01). A kaiju spawns in the MIDDLE of a round —
    // the single worst moment in the game to build canvases — so they are built
    // here too, under the load screen.
    try { prewarmMonsterTextures(); } catch { /* best-effort, like the rest */ }
    // ...and the seven PROP surface families (crate/oak/steel/tread/weave/
    // enamel/hex, 2026-08-01). A pickup respawns on a 20 s timer and a purge
    // door is bought mid-round, so these are mid-match builds too. ~70 ms once.
    try { prewarmPropTextures(); } catch { /* best-effort, like the rest */ }
    // ...and the SKY: cloud field + star field + moon (2026-08-01). The dome is
    // already in the scene by now (setSky runs at map load), so its program is
    // covered by the compile below — but the star field is 1024x512 and is
    // first SAMPLED when night falls mid-match, which is exactly the wrong
    // moment to be generating it.
    try { prewarmSkyTextures(); } catch { /* best-effort, like the rest */ }
    // ...and the six COMBAT-FX textures (fire / smoke / ember / muzzle flash /
    // shock ring / debris, 2026-08-01). These are by definition first used
    // MID-FIGHT — the first trigger pull, the first kill, the first rocket —
    // which is the worst moment in the game to run ~60 ms of canvas work.
    try { prewarmCombatTextures(); } catch { /* best-effort, like the rest */ }
    // ...and the four WORLD-DECAL textures (crater / scorch / blood / wet,
    // 2026-08-01). Same argument, one step further: a decal is generated the
    // first time a bullet meets a wall.
    try { prewarmDecalTextures(); } catch { /* best-effort, like the rest */ }
    // GPU UPLOAD, not just the canvas (2026-10-21 `fluid`). The prewarm* calls
    // above only do the CPU raster; the glTexImage2D upload was still deferred to
    // each texture's FIRST DRAW — i.e. mid-fight (first muzzle flash / impact
    // decal / nightfall star field), a 1–2 ms-per-texture hitch on a weak iGPU,
    // several coincident on the first blast. Upload them HERE, under the load
    // screen, exactly as the fur/hide/hose warms already do (match.ts). Idempotent
    // (the accessors return the cached canvas); warmTexture is a safe no-op if the
    // context is lost. This closes the texture half of the hitch; the shader half
    // is already covered by ensureWarmGroup + compileAsync below.
    try {
      for (const t of [
        firePuffTexture(), smokePuffTexture(), emberTexture(),
        muzzleFlashTexture(), shockRingTexture(), debrisTexture(),
        bulletCraterTexture(), scorchTexture(), bloodTexture(),
        wetSplatTexture(), bootPrintTexture(), skidTexture(),
        starTexture(), cloudTexture(), moonTexture(),
      ]) warmTexture(t);
      // Entity detail maps too — a kart/kaiju/pickup/door spawning mid-round is
      // first-drawn mid-match, so upload its map/normal/rough here. Lower
      // frequency than the FX above but the same free load-screen move.
      warmVehicleTextures(warmTexture);
      warmMonsterTextures(warmTexture);
      warmPropTextures(warmTexture);
    } catch { /* best-effort, like the rest */ }
    if (this.warmGroup) this.scene.add(this.warmGroup);
    // staged realistic models join the same compile + warm frames
    for (const s of staged) if (s.root) this.scene.add(s.root);
    // glTF creatures on HIDDEN dormant spares (loadGate warm roots): show them
    // for this compile, or their skinned programs compile on first appearance
    const restoreWarm = revealWarmRoots();
    // compileAsync (three r152+) can keep driver work in flight after this call
    // returns. Await it here so the arrival/loading phase actually absorbs that
    // work instead of letting the first controllable frames pay for it.
    const rAsync = (this.renderer as unknown as { compileAsync?: (s: THREE.Scene, c: THREE.Camera) => Promise<unknown> }).compileAsync;
    try {
      if (rAsync) {
        // compileAsync resolves only once EVERY warm material reports ready, so
        // on a parallel-compile driver it can outlast the load screen — we race
        // it against a timeout and move on. But three keeps its 10 ms ready-poll
        // running AFTER the race resolves, and that poll does
        // `properties.get(material).currentProgram.isReady()` with no guard
        // (three.module.js compileAsync): a warm material whose program isn't in
        // the cache yet leaves `currentProgram` undefined and the poll throws —
        // ASYNCHRONOUSLY, after we've already left this try/catch. That surfaced
        // as an uncaught "Cannot read properties of undefined (reading
        // 'isReady')" on every map load (caught in agent/shot.mjs's page log).
        // Hold the promise and swallow a late rejection so a post-race throw
        // can't escape as an unhandled error / leak past the load screen.
        //
        // RENDER-TARGET VARIANTS (2026-11-04): with post on, the live frame
        // draws the scene through the composer INTO A RENDER TARGET, and three
        // keys outputColorSpace + toneMapping on whether a target is bound
        // (canvas: sRGB + ACES; target: linear + none — OutputPass grades
        // afterwards). compile() with no target bound built only the CANVAS
        // variants, so every material outside the spawn view still compiled
        // its real (target) program on first sight — loadperf.ts caught them
        // as late srgb->srgb-linear programs. Compile both: the target set is
        // what is drawn; the canvas set covers the ladder's post-off levels
        // and heavy-scene bursts (render() drops the composer for those).
        const postRT = this.composer && !this.postKilled ? this.composer.readBuffer : null;
        const warms: Promise<unknown>[] = [];
        for (const rt of postRT ? [postRT, null] : [null]) {
          this.renderer.setRenderTarget(rt);
          try {
            const warm = rAsync.call(this.renderer, this.scene, this.camera) as Promise<unknown>;
            void warm.catch(() => { /* late ready-poll throw after the race */ });
            warms.push(warm);
          } finally { this.renderer.setRenderTarget(null); }
        }
        await Promise.race([
          Promise.all(warms),
          new Promise<void>((resolve) => window.setTimeout(resolve, lowSpec() ? 2200 : 1400)),
        ]);
      }
      else this.renderer.compile(this.scene, this.camera);
    } catch { /* best-effort */ }
    lmark("compile");
    // Out of the scene graph so it costs nothing per frame — but NEVER
    // disposed: three releases a program when its last material is disposed
    // (onMaterialDispose), so disposing here would throw away the exact work
    // this method just did. Holding the materials alive is what keeps the
    // programs in the cache for the real entities to hit.
    if (this.warmGroup) this.scene.remove(this.warmGroup);
    // FULL-MAP WARM FRAME (2026-11-04, "lag when I start a game"). compile()
    // builds the LIT programs only and uploads nothing: a texture or vertex
    // buffer is uploaded on its FIRST DRAW, and a caster's shadow-depth
    // program (incl. the skinned variants of the staged models / revealed
    // spares) is built by the shadow pass itself. The precompile camera sees
    // one spawn's view, so everything else on the map — scanned props, prop
    // models, castle walls — uploaded during the 2.6 s intro crane, which
    // frames the WHOLE map: that was a burst of first-draw uploads right as
    // the match appeared (agent/screenshot-rig/loadperf.ts counts them). One
    // plain render with frustum culling OFF draws every visible object once,
    // into the camera AND the shadow map, under the load screen instead.
    // ARM the staged models now and let the caller mount them on its dormant
    // spares, so those instances (hidden until claimed) are drawn by the warm
    // frame below too — revealed for it, like the loadGate warm roots
    for (const s of staged) { try { s.arm(); } catch { /* best-effort */ } }
    const shown: THREE.Object3D[] = [];
    if (onArmed) {
      try { for (const o of onArmed()) if (!o.visible) { o.visible = true; shown.push(o); } } catch { /* best-effort */ }
    }
    const restoreCull = this.uncullScene();
    const ac = this.renderer.autoClear;
    try {
      this.renderer.autoClear = true;
      if (this.renderer.shadowMap.enabled) this.renderer.shadowMap.needsUpdate = true;
      // into the composer's target when post is live: the program variants the
      // live frame will actually ask for (see the compile above)
      this.renderer.setRenderTarget(this.composer && !this.postKilled ? this.composer.readBuffer : null);
      this.renderer.render(this.scene, this.camera);
    } catch { /* best-effort */ }
    this.renderer.setRenderTarget(null);
    this.renderer.autoClear = ac;
    restoreCull();
    restoreWarm();
    for (const o of shown) o.visible = false;
    lmark("warmframe");
    // WARM THE NEON POST CHAIN: bloom passes live in the EffectComposer, NOT
    // the scene — renderer.compile never touches them, so their shaders
    // compiled on the FIRST composer.render() of a neon map = a match-start
    // freeze on slow GPUs. Render one composer frame now to compile them.
    if (this.composer && !this.postKilled) {
      // compile the AO pass too even if the ladder has it off right now, so a
      // later climb back to L0 doesn't compile N8AO's shaders mid-fight
      if (this.aoPass) { this.aoPass.enabled = true; if (this.renderPass) this.renderPass.enabled = false; }
      try { this.composer.render(); } catch { /* best-effort */ }
      this.syncAoPass();
    }
    // the staged models are compiled, drawn (lit + shadow + post) and uploaded:
    // free the throwaway instances (their modules were armed above)
    for (const s of staged) { try { s.dispose(); } catch { /* best-effort */ } }
    // first live frame redraws the shadow map with the real camera-following setup
    this.renderer.shadowMap.needsUpdate = true;
    // load-screen frames are not gameplay frames: never let them feed the
    // adaptive-resolution / cost-ladder statistics (see settleFrameStats)
    this.settleFrameStats();
    lmark("post");
    console.log(`[load] precompile ${(performance.now() - lt0).toFixed(0)} ms (${lt.join(" ")})${staged.length ? ` incl. ${staged.length} realistic model(s)` : ""}`);
  }

  /** Disable frustum culling on every object in the scene; returns the undo. */
  private uncullScene(): () => void {
    const culled: THREE.Object3D[] = [];
    this.scene.traverse((o) => { if (o.frustumCulled) { o.frustumCulled = false; culled.push(o); } });
    return () => { for (const o of culled) o.frustumCulled = true; };
  }

  /** Forget frame timing gathered so far and give the cost ladder a short
   *  grace period. Called at the end of every precompile and again when the
   *  match goes live (match.ts). Load-screen frames (the warm renders, and the
   *  multi-second gap between the last of them and the first live frame) used
   *  to land in frameEma / busyEma / jankCount: each counted as a >50 ms spike
   *  and pushed both EMAs up, so a healthy machine could start the match with
   *  the ladder already stepping down — adaptive resolution halved and
   *  shadows/bloom cut within the first second, then climbing back over the
   *  next 10 s (render-target reallocs on every step): a visible "laggy, blurry
   *  start". The first live frames also pay one-off costs (GC of the load
   *  garbage, bone-texture uploads) that say nothing about steady state. */
  settleFrameStats(graceMs = 1500): void {
    const now = performance.now();
    this.lastFrameAt = 0;
    this.frameEma = 16;
    this.busyEma = 8;
    this.jankCount = 0;
    this.jankWindowAt = now;
    this.lvlBadSince = 0;
    this.lvlGoodSince = 0;
    this.lowSpecCandidateAt = 0;
    this.statsGraceUntil = now + graceMs;
  }
  private statsGraceUntil = 0;
  /** adaptResolution has applied the ladder at least once (postKilled/cap30/
   *  shadows derived) — the grace window may only skip passes after that. */
  private ladderApplied = false;

  private warmGroup: THREE.Group | null = null;

  /** One throwaway object per shader-program VARIANT used by the lazily-built
   *  entities, built once per session and kept alive forever.
   *
   *  Variants rather than real `new Avatar()` / `new MonsterMesh()` instances,
   *  because three keys its program cache on material parameters, not on which
   *  class made the material (`getProgramCacheKey`, three.cjs:44172) — so a
   *  bare MeshToonMaterial with the same gradientMap/side/blending produces the
   *  same program the real avatar will later ask for and hit warm. Building the
   *  real classes would drag in their side effects for no extra coverage:
   *  Avatar adds itself to the scene and starts a nameplate timer, VehicleMesh
   *  pops the module-level FX light pool, and a skinned avatar fires a network
   *  fetch.
   *
   *  Axes that matter to the cache key and are therefore covered below:
   *  material type, map presence (`mapUv`), `side`, and `opaque`
   *  (transparent/blending). Axes that do NOT: emissive colour, depthWrite,
   *  depthTest — those are uniforms or GL state, so no variant is needed. */
  private ensureWarmGroup(): void {
    if (this.warmGroup) return;
    const g = new THREE.Group();
    g.visible = false; // compile() traverses ALL objects, render() skips these
    // 1x1 stand-in for every mapped variant: only the PRESENCE of `map`
    // reaches the cache key, never its contents or size.
    const px = new THREE.DataTexture(new Uint8Array([255, 255, 255, 255]), 1, 1);
    px.needsUpdate = true;
    const grad = toonGradient();
    const geo = new THREE.BoxGeometry(0.01, 0.01, 0.01); // shared by every mesh
    const add = (m: THREE.Material): void => { g.add(new THREE.Mesh(geo, m)); };
    const glow = { transparent: true, blending: THREE.AdditiveBlending, depthWrite: false } as const;
    // avatar bodies / cowboy gear (toon + gradient), custom skins (+map),
    // parachute canopy (+DoubleSide)
    add(new THREE.MeshToonMaterial({ gradientMap: grad }));
    add(new THREE.MeshToonMaterial({ gradientMap: grad, map: px }));
    add(new THREE.MeshToonMaterial({ gradientMap: grad, side: THREE.DoubleSide }));
    // the european dragon's wing membrane (toon + gradient + textured scales +
    // DoubleSide). `map` presence AND `side` are BOTH cache-key layers in three
    // r172 (WebGLPrograms.getParameters: doubleSided layer 11, HAS_MAP), so this
    // three-way combo is a distinct program the two entries above don't cover —
    // and the dragon is BUILT MID-MATCH (match.ts euroRide spawn), so a map with
    // no ambient bats used to cold-compile it on mount.
    add(new THREE.MeshToonMaterial({ gradientMap: grad, map: px, side: THREE.DoubleSide }));
    // viewmodel sleeve/glove/cuff, and the skinned sleeve
    add(new THREE.MeshLambertMaterial({}));
    add(new THREE.MeshLambertMaterial({ map: px }));
    // AVATAR BODIES + first-person arms (2026-08-01, `graphics`): standard,
    // mapped, AND carrying a normalMap + roughnessMap. `normalMapUv` and
    // `USE_ROUGHNESSMAP` are program-cache-key parameters in three r172
    // (getParameters → normalMapUv / roughnessMap), so this is a DISTINCT
    // program from the plain `{ map }` standard entry below — and every avatar
    // is built MID-MATCH (a player joins, a bot spawns, the cosmetics preview
    // rebuilds on every click), which is exactly the cold-compile window this
    // group exists to close. agent/bench-warmcov.mjs models both layers.
    // …and, since the 2026-08-01 WEAPON pass, EVERY weapon part too (first
    // person, the third-person held gun, and the weapon-chest prop) — they wear
    // procedural gunmetal/polymer/wood families with normal + roughness now.
    // Weapons are prewarmed under the load screen, but a remote player's held
    // gun is built the moment they join, i.e. mid-match.
    add(new THREE.MeshStandardMaterial({ map: px, normalMap: px, roughnessMap: px }));
    // …and, since the 2026-08-01 COSMETIC GEAR pass, the drop-in PARACHUTE
    // canopy: the same surfaced-standard program but DoubleSide (cache-key
    // layer 11), so the front-side entry above misses it. It is built the
    // instant a player drops in, which is frame 1 of the match.
    add(new THREE.MeshStandardMaterial({ map: px, normalMap: px, roughnessMap: px, side: THREE.DoubleSide }));
    // …and, since the 2026-08-01 VEHICLE pass, every windshield / canopy / UFO
    // dome: a surfaced standard that is ALSO transparent. `opaque` is cache-key
    // layer 17, so neither surfaced entry above covers it. Vehicles are built
    // mid-match (a ride spawns, a bot mounts, the DeLorean cinematic fires).
    add(new THREE.MeshStandardMaterial({ map: px, normalMap: px, roughnessMap: px, transparent: true }));
    // …and, since the 2026-08-01 PROP pass, the PURGE CONTAINMENT DOOR: a
    // surfaced standard that is transparent, DoubleSide AND carries an
    // emissiveMap (the hex lattice glows at its cell walls only). `emissiveMapUv`
    // is its own cache-key parameter in r172, so none of the three surfaced
    // entries above covers it — and a door dissolves the instant a sector is
    // bought, i.e. mid-round.
    add(new THREE.MeshStandardMaterial({
      map: px, normalMap: px, roughnessMap: px, emissiveMap: px,
      transparent: true, side: THREE.DoubleSide,
    }));
    // monsters, vehicles; horse/camel hide; delorean glass and the mini-UFO dome
    add(new THREE.MeshStandardMaterial({}));
    add(new THREE.MeshStandardMaterial({ map: px }));
    // monster EYES (2026-10-23): the species eye picture is both `map` and, on
    // the glowing kinds (godzilla/goblin/kraken), `emissiveMap` — a distinct
    // program, and the goblin and the kraken spawn mid-round.
    add(new THREE.MeshStandardMaterial({ map: px, emissiveMap: px }));
    add(new THREE.MeshStandardMaterial({ transparent: true }));
    // DoubleSide lit surfaces built mid-match: the glider/plane sail (transparent
    // DoubleSide) and plunder's flag cloth (opaque DoubleSide). `doubleSided` is
    // cache-key layer 11, so the three front-side standards above miss both.
    add(new THREE.MeshStandardMaterial({ transparent: true, side: THREE.DoubleSide }));
    add(new THREE.MeshStandardMaterial({ side: THREE.DoubleSide }));
    // misc unlit parts, the avatar's BackSide outline hull, and every additive
    // glow (rocket trails, pickup auras, bombkart neon)
    add(new THREE.MeshBasicMaterial({}));
    add(new THREE.MeshBasicMaterial({ side: THREE.BackSide }));
    add(new THREE.MeshBasicMaterial({ ...glow }));
    // DoubleSide transparent glow — kill rings, treasure/beacon beams, pickup
    // auras, teleport rings, monster auras. All spawn mid-match and all render
    // both faces; the front-side glow above is a different program (doubleSided
    // layer 11). This is the highest-frequency missed variant.
    add(new THREE.MeshBasicMaterial({ ...glow, side: THREE.DoubleSide }));
    // …and, since the 2026-08-01 COMBAT-FX pass, the same additive DoubleSide
    // glow WITH A MAP: the muzzle flash + its crossed twin (viewmodel), the
    // mega shockwave and arena kill-aura rings, and the rocket exhaust plume all
    // wear a procedural `combatTextures` billboard now. `mapUv` (HAS_MAP) is a
    // cache-key PARAMETER, so the map-less glow entry above does not cover them
    // — and all four are built mid-match (first trigger pull, first rocket,
    // first bomb). agent/bench-warmcov.mjs models this row.
    add(new THREE.MeshBasicMaterial({ ...glow, map: px, side: THREE.DoubleSide }));
    // the Armageddon storm shroud: an inward BackSide sphere with fog DISABLED.
    // `useFog` (material.fog) is cache-key layer 1 and BackSide→flipSided layer
    // 12, so it shares no program with any warm entry; it cold-compiled on the
    // first storm (a maximally dramatic, maximally bad moment to hitch).
    add(new THREE.MeshBasicMaterial({ transparent: true, side: THREE.BackSide, fog: false, depthWrite: false }));
    // avatar edge outlines and viewmodel trim need a real Line object — three
    // derives the program from the object type as well as the material
    g.add(new THREE.LineSegments(new THREE.EdgesGeometry(geo), new THREE.LineBasicMaterial({ transparent: true })));
    // nameplates, vehicle drivers, pickup labels, the auto-aim marker
    g.add(new THREE.Sprite(new THREE.SpriteMaterial({ map: px, transparent: true })));
    // the particle BURST (worldEntities spawnBurst) renders its sparks as ONE
    // InstancedMesh — since iteration 359, to collapse n draw calls into one.
    // `instancing` is program-cache-key layer 1 in three r172
    // (getProgramCacheKeyBooleans → _programLayers.enable(1), three.cjs:44254),
    // so an InstancedMesh compiles a DISTINCT program from every regular-Mesh
    // entry above even at byte-identical material params — and NONE of the
    // warm entries, nor the merged map boxes, are instanced. The two decor
    // InstancedMeshes (decor.ts) are map-specific and MeshStandard, not this
    // additive MeshBasic. A burst fires on the highest-frequency combat events
    // (every kill / hit / pickup, several per frame in portal/dragon fx), yet
    // none is in the scene at map-load precompile, so the FIRST one used to
    // cold-compile the instanced program mid-fight. Warm it with a throwaway
    // 1-instance mesh; blending/transparent/depthWrite are GL state not
    // cache-key, but instanced + MeshBasic + fog IS the missed program.
    const warmInst = new THREE.InstancedMesh(geo, new THREE.MeshBasicMaterial({ ...glow }), 1);
    warmInst.setMatrixAt(0, new THREE.Matrix4());
    warmInst.frustumCulled = false;
    g.add(warmInst);
    // the sandworm acid gicle (worldEntities spawnWormVomitGicle) renders its
    // up-to-62 blobs as ONE InstancedMesh whose per-blob colour rides
    // `instanceColor` — `instancingColor` is program-cache-key layer 2 in three
    // r172 (getProgramCacheKeyBooleans → _programLayers.enable(2), three.cjs:
    // 44257), a DISTINCT program from the layer-1-only burst above. It spawns
    // mid-match on a worm attack, so warm it too. Calling setColorAt makes
    // instanceColor non-null, which is exactly what flips the layer-2 bit.
    const warmInstColor = new THREE.InstancedMesh(geo, new THREE.MeshBasicMaterial({ transparent: true, depthWrite: false }), 1);
    warmInstColor.setMatrixAt(0, new THREE.Matrix4());
    warmInstColor.setColorAt(0, new THREE.Color(0xffffff));
    warmInstColor.frustumCulled = false;
    g.add(warmInstColor);
    // the MEGA-explosion DEBRIS chunks (worldEntities spawnExplosion, `mega`
    // truck/bomb detonation) render their 14 chunks as ONE InstancedMesh whose
    // material is OPAQUE MeshBasic (`{ color }` — no transparent, NormalBlending,
    // no additive) — unlike the two additive/transparent instanced entries above.
    // `opaque` is program-cache-key layer 17 in three r172
    // (getProgramCacheKeyBooleans → `if (parameters.opaque) _programLayers.enable(17)`,
    // and `opaque = material.transparent===false && blending===NormalBlending &&
    // !alphaToCoverage`), so an OPAQUE instanced MeshBasic is a DISTINCT program
    // from the transparent instanced burst — and nothing in the scene supplies it
    // (decor's opaque instanced meshes are MeshStandard, a different shaderID).
    // A mega blast is a maximally dramatic mid-fight moment to cold-compile, so
    // warm it. No setColorAt → instanceColor stays null (layer 2 off), matching
    // the debris which never calls setColorAt.
    const warmInstOpaque = new THREE.InstancedMesh(geo, new THREE.MeshBasicMaterial({}), 1);
    warmInstOpaque.setMatrixAt(0, new THREE.Matrix4());
    warmInstOpaque.frustumCulled = false;
    g.add(warmInstOpaque);
    // …and since the 2026-08-01 COMBAT-FX pass those debris chunks are TEXTURED
    // (charred rubble with hot seams instead of flat `#ff6a2a`), so the opaque
    // instanced program they want now also carries HAS_MAP — a distinct key
    // from the map-less entry directly above.
    const warmInstOpaqueMap = new THREE.InstancedMesh(geo, new THREE.MeshBasicMaterial({ map: px }), 1);
    warmInstOpaqueMap.setMatrixAt(0, new THREE.Matrix4());
    warmInstOpaqueMap.frustumCulled = false;
    g.add(warmInstOpaqueMap);
    // the HOSE WEAPONS' liquid stream (match.ts renderJet): the tube and the
    // muzzle/impact billboards are instanced MeshBasic WITH a map and DoubleSide.
    // `mapUv` (HAS_MAP) is a cache-key PARAMETER and `doubleSided` is layer 11,
    // so this shares no program with any of the three instanced entries above —
    // all of which are map-less FrontSide. The drop layer has no map and stays
    // on `warmInst`. First trigger-pull is mid-fight, so warm it here.
    // agent/bench-warm-jet.mjs locks the pairing.
    const warmInstMap = new THREE.InstancedMesh(
      geo, new THREE.MeshBasicMaterial({ map: px, transparent: true, depthWrite: false, side: THREE.DoubleSide }), 1);
    warmInstMap.setMatrixAt(0, new THREE.Matrix4());
    warmInstMap.frustumCulled = false;
    g.add(warmInstMap);
    // WORLD DECALS (decals.ts, 2026-08-01): four InstancedMeshes whose material
    // is a MAPPED MeshBasic with instanceColor (the fade channel) and fog OFF.
    // Three cache-key axes at once that no entry above combines — `instancing`
    // (layer 1) + `instancingColor` (layer 2) + `mapUv` HAS_MAP + `useFog`
    // (layer 1 of the boolean set, disabled here). The nearest neighbours miss
    // it: warmInstColor has no map, warmInstMap has no instanceColor and keeps
    // fog. A decal is placed on the FIRST bullet that hits a wall, i.e. within a
    // second of the first trigger pull, so it must be warm before then.
    // Blending (MultiplyBlending) is GL state, not part of the key.
    const warmInstDecal = new THREE.InstancedMesh(
      geo, new THREE.MeshBasicMaterial({ map: px, transparent: true, depthWrite: false, fog: false, toneMapped: false }), 1);
    warmInstDecal.setMatrixAt(0, new THREE.Matrix4());
    warmInstDecal.setColorAt(0, new THREE.Color(0xffffff));
    warmInstDecal.frustumCulled = false;
    g.add(warmInstDecal);
    this.warmGroup = g;
  }

  /** The one and only scene fog — see the note in the constructor. Mutated,
   *  never reassigned. */
  private readonly fog = new THREE.Fog("#cfe4f5", 150, 460);
  /** What the ATMOSPHERE wants the fog to be (theme / dimension blend / night
   *  cycle). Held separately from `fog` because the underwater murk overrides
   *  it temporarily and must be able to hand back the *current* atmosphere on
   *  surfacing — not a snapshot taken at dive time. */
  private readonly fogBase = { color: new THREE.Color("#cfe4f5"), near: 150, far: 460 };

  /** Atmosphere fog. Cheap enough to call every frame. */
  private setBaseFog(color: THREE.ColorRepresentation, near: number, far: number): void {
    this.fogBase.color.set(color);
    this.fogBase.near = near;
    this.fogBase.far = far;
    this.applyFog();
  }

  /** Write whichever of {atmosphere, underwater murk} is in force onto the
   *  single fog object. */
  private applyFog(): void {
    if (this.underwater) {
      this.fog.color.set(UNDERWATER_FOG);
      this.fog.near = UNDERWATER_FOG_NEAR;
      this.fog.far = UNDERWATER_FOG_FAR;
    } else {
      this.fog.color.copy(this.fogBase.color);
      // GFX.fogDensity (gfxConfig.ts): >1 pulls the linear fog in, <1 pushes it out
      const k = 1 / Math.max(0.1, GFX.fogDensity);
      this.fog.near = this.fogBase.near * k;
      this.fog.far = this.fogBase.far * k;
    }
    // the sky's bottom band is the ATMOSPHERE fog colour (never the underwater
    // murk — you cannot see the dome from under the sea), so distant scenery
    // fades into a sky of the same colour instead of hitting a horizon seam
    if (this.skyU) this.skyU.uFog.value.copy(this.fogBase.color);
  }

  private underwater = false;
  /** Submerged: dense teal murk so you can't see far (realistic underwater, and
   *  it hides the water-volume box walls). Restores on surface.
   *
   *  Deliberately a LINEAR fog, not the FogExp2 this used to build. `fogExp2`
   *  is a shader-program cache-key entry (three.cjs:44082, :44226), so the old
   *  code's first dive on any water map compiled a second complete program set
   *  for the whole scene mid-swim — the worst possible moment. Linear keeps the
   *  key identical, so a dive now costs nothing at all. UNDERWATER_FOG_NEAR/FAR
   *  are fitted to the old exp2 curve — see the note on those constants. */
  setUnderwater(on: boolean): void {
    if (on === this.underwater) return;
    this.underwater = on;
    this.applyFog();
  }

  /** Live GPU program count — a steadily growing number mid-match means a
   *  shader recompile storm (the classic iGPU freeze source). */
  programCount(): number {
    return this.renderer.info.programs?.length ?? 0;
  }

  /** One reused object for the perf HUD (2 Hz flush — never per frame).
   *  calls/tris are last frame's values: renderer.info auto-resets each draw. */
  private readonly perfSnap: RendererPerfSnap = {
    calls: 0, tris: 0, programs: 0, geometries: 0, textures: 0,
    frameEma: 0, busyEma: 0, pixelScale: 0, gfxLvl: 0,
  };

  perfSnapshot(): Readonly<RendererPerfSnap> {
    const i = this.renderer.info;
    const s = this.perfSnap;
    s.calls = i.render.calls;
    s.tris = i.render.triangles;
    s.programs = i.programs?.length ?? 0;
    s.geometries = i.memory.geometries;
    s.textures = i.memory.textures;
    s.frameEma = this.frameEma;
    s.busyEma = this.busyEma;
    s.pixelScale = this.pixelScale;
    s.gfxLvl = this.gfxLvl;
    return s;
  }

  /** A known-heavy EVENT is starting (antigravity, armageddon): weak machines
   *  jump straight to L2 instead of stuttering for 2.5s while the ladder
   *  measures it. No-op on capable machines — the desktop stays untouched. */
  eventSurge(): void {
    if (gfxPinned()) return; // screenshot pin: never degrade
    if (!weakHardware() && !lowSpec()) return;
    if (this.gfxLvl < 2) {
      this.gfxLvl = 2;
      currentGfxLevel = 2;
      console.log("[render] cost ladder → L2 (heavy event pre-arm)");
    }
    this.lvlGoodSince = 0; // and don't relax mid-event
  }

  setLoadHint(level: "normal" | "heavy"): void {
    if (level !== "heavy") return;
    this.heavySceneUntil = performance.now() + 2500;
  }

  private lowSpecCandidateAt = 0;
  private unflagSince = 0;
  private wasHeavy = false;
  /** Redraw a facade histogram-skin canvas from the live music spectrum. */
  private drawFacadeHistogram(hv: { canvas: HTMLCanvasElement; texture: THREE.CanvasTexture }, levels: number[]): void {
    const ctx = hv.canvas.getContext("2d");
    if (!ctx) return;
    const W = hv.canvas.width, H = hv.canvas.height;
    ctx.clearRect(0, 0, W, H);
    ctx.fillStyle = "rgba(6,8,16,0.9)"; // dark screen the bars glow on
    ctx.fillRect(0, 0, W, H);
    // ONE full-height gradient reused for every bar (was 24 gradient objects/frame)
    const grad = ctx.createLinearGradient(0, H, 0, 0);
    grad.addColorStop(0, "#00f0ff");
    grad.addColorStop(0.55, "#7b4bff");
    grad.addColorStop(1, "#ff2ec4");
    ctx.fillStyle = grad;
    const n = levels.length, bw = W / n;
    for (let i = 0; i < n; i++) {
      const h = Math.max(2, levels[i] * H * 0.94);
      ctx.fillRect(i * bw + bw * 0.12, H - h, bw * 0.76, h);
    }
    hv.texture.needsUpdate = true;
  }

  render(): void {
    if (this.restorePending) {
      // First frame after a GPU context restore (see the ctor). Every program
      // the lost context dropped is warmed here in one pass — precompile()
      // compiles the WHOLE live scene (all current entities are still in the
      // graph; only their GPU programs are gone) plus the warm-variant group and
      // the post chain, exactly as at map load. Skip the normal body this one
      // frame: precompile() already presents a composer frame when post is live,
      // and one recovery frame after a black-screen loss is unnoticeable.
      this.restorePending = false;
      void this.precompile(); // best-effort, same as the map-load call
      return;
    }
    const now = performance.now();
    // WIND: one uniform write moves every banner, canopy, bush and kelp frond
    // in the world (wind.ts). O(1) per frame, no allocation, no upload.
    tickWind(now);
    const heavy = now < this.heavySceneUntil;
    if (heavy) {
      if (!this.wasHeavy) this.preHeavyPixelScale = this.pixelScale;
      this.teardownPost();
      if (this.renderer.shadowMap.enabled) this.renderer.shadowMap.enabled = false;
      if (this.pixelScale > 0.8) {
        this.pixelScale = 0.8;
        this.renderer.setPixelRatio(this.pixelScale);
        this.renderer.setSize(window.innerWidth, window.innerHeight);
      }
      this.wasHeavy = true;
    } else if (this.wasHeavy) {
      // heavy burst over → RESTORE the pretty pipeline (this used to be
      // permanent: one vehicle pile-up killed bloom + shadows until reload)
      this.wasHeavy = false;
      // Respect the cost ladder: restoring shadows at L1+ immediately undid
      // its decision and forced another shader compile after every surge.
      if (!lowSpec()) {
        if (this.gfxLvl === 0) {
          this.renderer.shadowMap.enabled = true;
          this.renderer.shadowMap.needsUpdate = true;
        }
        // L1 disables shadows, not bloom. Rebuild the post chain unless the
        // ladder is actually at L2+, where direct rendering is intentional.
        if (this.gfxLvl < 2 && !this.composer) this.setupPost(this.loadedTheme);
      }
      if (this.preHeavyPixelScale !== null && this.pixelScale < this.preHeavyPixelScale) {
        this.pixelScale = this.preHeavyPixelScale;
        this.renderer.setPixelRatio(this.pixelScale);
        this.renderer.setSize(window.innerWidth, window.innerHeight);
        this.composer?.setPixelRatio(this.pixelScale);
        this.composer?.setSize(window.innerWidth, window.innerHeight);
      }
      this.preHeavyPixelScale = null;
    }
    this.adaptResolution(now);
    this.followShadow();
    if (this.cap30) {
      this.drawFlip = !this.drawFlip;
      if (this.drawFlip) return; // skip this draw — effective 30fps presentation
    }
    // dev: expose the renderer + log per-frame draw stats every ~2s so we can
    // see whether lag is draw-call / triangle bound
    (window as unknown as { __np_renderer?: THREE.WebGLRenderer }).__np_renderer = this.renderer;
    // dev: the scene too, so an offline probe can traverse it (e.g. count/toggle
    // a tagged subtree to attribute draw calls to it). Sibling of __np_renderer.
    (window as unknown as { __np_scene?: THREE.Scene }).__np_scene = this.scene;
    if ((this.statTick = (this.statTick + 1) % 120) === 0) {
      const i = this.renderer.info.render;
      const line = `calls=${i.calls} tris=${i.triangles} frame=${this.frameEma.toFixed(1)}ms busy=${this.busyEma.toFixed(1)}ms scale=${this.pixelScale} L${this.gfxLvl}`;
      console.log(`[stats] ${line}`);
      perfBeacon(gpuTag(), "game", line); // opt-in remote telemetry (localStorage neonpurge.perf=1)
    }
    // the sky dome is camera-centred so its horizon never drifts as you roam
    if (this.skyDome) this.skyDome.position.copy(this.camera.position);
    // the cloud decks are a ground-plane projection, so they need the camera's
    // world XZ to parallax against — three floats, no allocation
    if (this.skyU) {
      this.skyU.uTime.value = now / 1000;
      this.skyU.uCamXZ.value.set(this.camera.position.x, this.camera.position.z);
    }
    if (this.waterUniforms) this.waterUniforms.uTime.value = now / 1000;
    if (this.lavaUniforms) this.lavaUniforms.uTime.value = now / 1000;
    // WEATHER rain: keep the cloud on the camera (X/Z follow; Y stays world-fixed
    // so vertical parallax reads naturally). The fall itself is a GPU vertex
    // displacement — advance its clock with ONE float write (no per-droplet loop,
    // no buffer re-upload). uRainTime is reduced into the wrap period
    // (span/fall seconds) so it never grows large enough to lose float precision.
    if (this.rainPoints) {
      if (this.rainUniforms) {
        const period = this.rainSpanY / Math.max(0.001, this.rainSpeed);
        this.rainUniforms.uRainTime.value = (now / 1000) % period;
      }
      this.rainPoints.position.set(this.camera.position.x, 0, this.camera.position.z);
    }
    // mega_chibre floating sprites: bob + billboard toward the camera
    if (this.chibreSprites.length) {
      const t = now / 1000;
      for (const spr of this.chibreSprites) {
        spr.quaternion.copy(this.camera.quaternion);
        spr.position.y = (spr.userData.baseY as number) + Math.sin(t * 1.5 + (spr.userData.phase as number)) * 0.6;
      }
    }
    if (this.decorSpinners.length) {
      const t = now / 1000;
      for (const o of this.decorSpinners) {
        const sp = o.userData.spin as { axis: "x" | "y" | "z"; speed: number };
        o.rotation[sp.axis] = t * sp.speed;
      }
    }
    if (this.decorEq.length || this.decorBeat.length || this.decorHistViz.length) {
      // every music visualiser (boombox EQ, tower facade histogram, dancing
      // mountains) reads the SAME live spectrum; each element picks its band.
      // The GPU-uploading ones (canvas texture, instance buffer) only refresh
      // at ~30 Hz — 60 Hz re-uploads were a real steady frame cost.
      const levels = audio.getMusicLevels(EQ_BANDS);
      const heavyViz = now >= this.nextVizAt;
      if (heavyViz) this.nextVizAt = now + 1000 / 30;
      // The EQ bars ease EVERY frame (deliberately, so they stay smooth on a
      // high-refresh display — unlike the 30 Hz-gated beat rings below). A fixed
      // 0.4/frame fraction converged FASTER on a 144 Hz monitor than a 60 Hz one
      // (the last refresh-rate-dependent ease in the render path). Convert to the
      // rate-independent exp form: k = 1-exp(-dt*30.6) equals 0.4 at 60 fps and
      // holds the same wall-clock spring at every refresh rate.
      const eqDt = this.lastEqNow > 0 ? Math.min(0.1, (now - this.lastEqNow) / 1000) : 1 / 60;
      this.lastEqNow = now;
      const eqK = 1 - Math.exp(-eqDt * 30.6);
      for (const o of this.decorEq) { // cheap: just a local scale, no upload
        const d = o.userData.eq as { band: number; base: number; max: number };
        const target = d.base + (levels[d.band] ?? 0) * d.max;
        o.scale.y += (target - o.scale.y) * eqK; // smooth spring for a nice bounce
      }
      if (heavyViz) {
        for (const hv of this.decorHistViz) this.drawFacadeHistogram(hv, levels);
        for (const im of this.decorBeat) {
          const bases = im.userData.beatRing as { x: number; z: number; rad: number; h: number; band: number; sy: number }[];
          for (let i = 0; i < bases.length; i++) {
            const b = bases[i];
            const target = b.h * (0.78 + (levels[b.band] ?? 0) * 0.6); // squash 0.78× … stretch 1.38×
            b.sy += (target - b.sy) * 0.4; // 30 Hz so a touch snappier per step
            this.beatM4.compose(this.beatPos.set(b.x, -2, b.z), this.beatQ, this.beatScl.set(b.rad, b.sy, b.rad));
            im.setMatrixAt(i, this.beatM4);
          }
          im.instanceMatrix.needsUpdate = true;
        }
      }
    }
    // Cap dynamic shadow refresh by time, not display frames. 60 Hz preserves
    // fluid motion while preventing 144/240 Hz monitors from rendering 72/120
    // complete shadow maps per second. (A 30 Hz cap was tried in the 2026-11
    // render-cost pass and NOT shipped: avatar / vehicle shadows would step at
    // half the draw rate on a 60 Hz panel — a motion look change that a still
    // screenshot cannot clear. See BACKLOG 2026-11-05.)
    if (now >= this.nextShadowAt) {
      this.nextShadowAt = now + 1000 / 60;
      this.renderer.shadowMap.needsUpdate = true;
    }
    if (this.localViews && this.localViews.length >= 1) {
      // LOCAL SPLIT-SCREEN (1-4 players on one machine): scissor viewport per
      // seat, one shared scene/context (invariant: never a second renderer).
      // A SINGLE view is real too — pads-only with one controller renders the
      // seat camera fullscreen (the match's own camera belongs to the
      // retreated owner session and must not win the frame).
      // Composer bypassed for the frame, same trade as the dead-spectate split.
      const w = window.innerWidth, h = window.innerHeight;
      this.renderer.autoClear = true;
      // full-frame clear FIRST: with 3 seats one quadrant is never scissor-
      // rendered and would otherwise keep smearing the previous frame
      this.renderer.setScissorTest(false);
      this.renderer.setViewport(0, 0, w, h);
      this.renderer.clear();
      this.renderer.setScissorTest(true);
      for (const v of this.localViews) {
        const pw = Math.max(1, Math.round(v.rect.w * w));
        const ph = Math.max(1, Math.round(v.rect.h * h));
        const px = Math.round(v.rect.x * w);
        // rect.y is measured from the TOP (CSS-style, matches the HUD overlay
        // divs); GL viewports measure from the bottom — flip here, once.
        const py = Math.round((1 - v.rect.y - v.rect.h) * h);
        v.camera.aspect = pw / ph;
        v.camera.updateProjectionMatrix();
        // each seat's horizon: the sky dome is camera-centred by design
        if (this.skyDome) this.skyDome.position.copy(v.camera.position);
        v.onBefore?.();
        this.renderer.setViewport(px, py, pw, ph);
        this.renderer.setScissor(px, py, pw, ph);
        this.renderer.render(this.scene, v.camera);
        v.onAfter?.();
      }
      this.renderer.setScissorTest(false);
      this.renderer.setViewport(0, 0, w, h);
      this.camera.aspect = w / h;
      this.camera.updateProjectionMatrix();
    } else if (this.splitCam) {
      // DEAD-SPECTATE SPLIT SCREEN: my view left, a teammate's view right.
      // Direct scissor renders (composer bypassed for the frame — bloom is a
      // fair trade while dead); autoClear+scissor clears each half separately.
      const w = window.innerWidth, h = window.innerHeight;
      const halfAspect = (w / 2) / h;
      this.renderer.autoClear = true;
      this.renderer.setScissorTest(true);
      this.camera.aspect = halfAspect;
      this.camera.updateProjectionMatrix();
      this.splitCam.aspect = halfAspect;
      this.splitCam.fov = this.camera.fov;
      this.splitCam.far = this.camera.far;
      this.splitCam.updateProjectionMatrix();
      this.renderer.setViewport(0, 0, w / 2, h);
      this.renderer.setScissor(0, 0, w / 2, h);
      this.renderer.render(this.scene, this.camera);
      // recentre the sky dome on the right eye so its horizon holds there too
      if (this.skyDome) this.skyDome.position.copy(this.splitCam.position);
      this.renderer.setViewport(w / 2, 0, w / 2, h);
      this.renderer.setScissor(w / 2, 0, w / 2, h);
      this.renderer.render(this.scene, this.splitCam);
      this.renderer.setScissorTest(false);
      this.renderer.setViewport(0, 0, w, h);
      this.camera.aspect = w / h;
      this.camera.updateProjectionMatrix();
    } else if (this.composer && !this.postKilled) this.composer.render();
    else {
      // EffectComposer leaves autoClear=false; a direct (day-map) render on the
      // SHARED renderer must restore it or the frame won't clear (round-2 smear)
      this.renderer.autoClear = true;
      this.renderer.render(this.scene, this.camera);
    }
    // BUSY time = actual work this frame (excludes the vsync wait that pins
    // frameEma to the refresh interval) — this is what lets quality RECOVER
    // on 60 Hz displays, where frameEma can never dip under ~16.7 ms.
    if (now >= this.statsGraceUntil) this.busyEma += (Math.min(100, performance.now() - now) - this.busyEma) * 0.05;
  }

  /** Dead-spectate: set a second camera to render the right half of a split
   *  screen this frame (null = normal fullscreen render). */
  splitCam: THREE.PerspectiveCamera | null = null;

  /** LOCAL SPLIT-SCREEN: when set (1+ entries), the frame is rendered as one
   *  scissor viewport per entry instead of the fullscreen/composer path.
   *  Entry 0 is normally the match's own camera; rect is normalized with y
   *  from the TOP of the window. onBefore/onAfter bracket that seat's render
   *  (used to hide the seat's own third-person avatar). Takes precedence over
   *  splitCam. The match must null this on close — the renderer is shared. */
  localViews: {
    camera: THREE.PerspectiveCamera;
    rect: { x: number; y: number; w: number; h: number };
    onBefore?: () => void;
    onAfter?: () => void;
  }[] | null = null;

  dispose(): void {
    this.localViews = null; // never leak seat cameras into the next screen
    window.removeEventListener("resize", this.onResize);
    // actually free GPU memory: scene.clear() only detaches children, and a
    // new renderer is built every round — without disposal VRAM climbs by a
    // full map + bloom chain + env map per round until the context is lost
    this.disposeSubtree(this.scene);
    this.scene.clear();
    this.envRT?.dispose();
    this.envRT = null;
    this.pmrem?.dispose();
    this.pmrem = null;
    if (activeRenderer === this) { activeRenderer = null; setGfxTarget(null); }
    this.teardownPost();
    this.skyDome = null;
    this.skyU = null; // the dome's material went with disposeSubtree above
    // NOTE: do NOT dispose the shared WebGLRenderer here — its context is
    // reused by the next round/menu. We only freed this scene's resources.
    this.renderer.renderLists.dispose();
  }
}
